/**
 * Collaboration Pricing Calculator.
 *
 * A port of the Apps Script tool "Costing Tool for SOP 2" (project 10DMZlju…, deployed as a Google
 * web app). Same two principles, same guideline rules, same numbers — the arithmetic lives in
 * src/pricingModel.js so it can be checked against the Costing Guideline without reading React.
 *
 * What the port changes on purpose:
 *   - saved projects go to the portal database instead of a Google Sheet, so they sit behind the same
 *     login and the same activity log as everything else here;
 *   - the frame price list is a TABLE (00_PORTAL_cp_frame), not a constant in the bundle, so a price
 *     change does not need a deploy.
 *
 * What is deliberately NOT ported yet: the original's 252-row reference table of standard SKUs, used
 * to compare a collab cost against a comparable in-line product. It is a frozen 2026 snapshot, and the
 * portal now holds the live material master and price lines — sourcing the comparison from those is
 * better than carrying the snapshot across. Flagged to Max rather than half-built.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FormulaHint from './FormulaHint';
import { Calculator, Settings, Plus, Copy, Trash2, Save, Upload, FolderOpen, AlertTriangle, Pencil, ChevronUp, ChevronDown, ChevronRight, ExternalLink, CheckCircle2, Eye } from 'lucide-react';
import Header from './Header';
import ModuleTabs from './ModuleTabs';
import ModuleHelp from './ModuleHelp';
import useAuth from './useAuth';
// The same dialog Project Approval and Pricing Approval use — an approver deciding from inside the
// quote answers the same two questions, and a third copy of that markup is how the three drift.
import { DecisionDialog } from './DecisionDialog';
/* THE APPROVER'S PANEL, opened from here as well — Max, 2026-10-05: "nó hiện nguyên cái bự luôn
   chứ. ko phải hiện mỗi cái popup summary thui". The same component Pricing Approval draws, so a
   quote is read the same way wherever the approver happens to be standing. */
import PricingApprovalDetail from './36_PricingApprovalDetail';
import { BACKEND_URL } from './backendConfig';
import { SortTh, sortRows, rowPassesColumnFilters, distinctColumnValues } from './15_SampleTracker';
import {
  computeSku, computeEntity, landedCost, marginTone, num, fmtUSD, fmtPct, fmtLocal,
  FRAME_PROFIT_RATE,
  ruleSet,
  MOQ_PARTS,
  MOQ_PARTS_DEFAULT,
  isFurnishedMaterial,
  isCbd,
  slRate as slRateOf,
} from './pricingModel';
import {
  cpFigures, CpColHead, CpTotalCost, CpPriceMargin, CP_BENCH_LINES, OPT_CONDITIONS, withOption,
  cpCards, CpCardRow, money, CbdPriceApproval, CbdPriceApprovalExport,
  optDepartures as cpOptDepartures, optDepartureText as cpOptDepartureText,
} from './cpSummary';
import './PricingCalculator.css';
import CpAttachments from './CpAttachments';
import CpTrackerLink from './CpTrackerLink';

const API = `${BACKEND_URL}/api/pricing`;
/* The same build flag the nav entry and the route use. Without it the approval endpoints answer 404,
   so the button must not offer what cannot happen. */
const PRICING_APPROVAL_ON = process.env.REACT_APP_PRICING_APPROVAL === '1';

/* Takes the rules so a NEW quote starts at whatever an Admin last set (Max, 2026-09-29). Only the
   SEEDS come from here: once a quote is saved it carries its own `t0Oh` / `t1Oh`, which is what it
   is then priced on, so a later change to the rule cannot move it. */
/* `method` — which way the quote is priced (see PRICING_METHODS in pricingModel.js). A NEW quote
   starts on HEVN Costing's CBD workbook: Linda, relayed by Max on 2026-10-26, "cách tính CBD áp cho
   mọi collab nhé". The guideline stays one pick away on the quote (and `?method=guideline` opens a
   blank one on it), because every quote saved before carries no method and is still priced the way
   it was approved. A SKU added to a quote takes the quote's method, since one quote is priced one way. */
const newQuoteMethod = () => {
  try {
    return new URLSearchParams(window.location.search).get('method') === 'guideline' ? 'guideline' : 'cbd';
  } catch (e) { return 'cbd'; }
};
/* THE CALCULATION STEP (Max, 04/Nov/2026) — a CBD quote now goes Costing -> Calculation (Amy: HPAC Overhead % and T2
   Markup % per item) -> sale manager -> CEO, and it is priced the way Linda's "Calculation" sheet prices: the selling-
   material fees passed through (pricingModel.js computeCbd, `cbdCalc`). Build-time, like the gate itself: the backend
   half is PRICING_CALC_STEP=1. A quote already waiting or approved keeps the arithmetic it was sent with. */
const CALC_ON = process.env.REACT_APP_PRICING_CALC === '1';
const calcFlag = (method) => (CALC_ON && method === 'cbd' ? { cbdCalc: 1 } : {});
const BLANK_SKU = (rules, method = newQuoteMethod()) => ({
  name: '',
  auto: true,
  overrides: {},
  values: {
    pricingMethod: method,
    ...calcFlag(method),
    qty: 500, poDate: '', xfDate: '', supply: 'sell',
    skin: '', cmt: '', ohp: '', frame: '', frameProfitRate: ruleSet(rules).frame_profit_pct,
    fprofit: '', handling: '', fmfee: '', transport: '', moq: '', addMat: '', shortLead: '',
    moqParts: [...MOQ_PARTS_DEFAULT], moqRatePct: '',
    matSupply: 'vendor', ffcmt: '',
    // 10% unless somebody changes it (Max, 2026-09-11) — the usual collab uplift on the in-line
    // shelf price, and a default beats an empty box everyone fills in with the same number.
    /* The overhead is the fixed 15% and the PROFIT is what a person sets, so the default totals
       are unchanged: T2 is still 45% (15 + 30) and T1 is still blank, because a blank T1 is what
       makes Case B ask for one. A ratio that lived only on the label would be one nobody can clear
       or correct — the trap the badly-late threshold fell into in September. */
    t1Ratio: ruleSet(rules).tier_oh_pct + 30,
    t1Oh: ruleSet(rules).tier_oh_pct, t1Profit: 30,
    t0Oh: ruleSet(rules).tier_oh_pct, t0Profit: '',
    inlineRrp: '', premium: 10,
    /* Jay's guideline by default. 'buying' is the Quote Sheet's convention — see marginBasis in
       pricingModel.js; the two differ by $0.90 a unit on CP-000010. */
    marginBasis: 'base', t0Ratio: '',
    /* Which collaboration model — Max, 2026-09-14. It decides which tier ratio has to be filled in,
       so it is deliberately BLANK rather than defaulting to one of the three: a default here would
       assert the shape of a deal nobody has looked at yet, and the whole point of the field is that
       somebody chooses. Kept on the SKU because the ratios it governs are on the SKU. */
    dealCase: '',
  },
});

/* A QUOTE SAVED BEFORE THE SPLIT carries a tier ratio and no parts, and its boxes would come up
   empty over a label saying 45%: a figure that prices the quote and that nobody can reach. Seeded
   onto the profit half on the way in, which is what the arithmetic does with it anyway — so the
   screen and the price agree, and the first person to open an old quote can correct it. */
/* MONEY IS TWO DECIMALS, EVERYWHERE — Max, 2026-09-23: "thống nhất vụ chữ số thập phân... tôi
   nghĩ là lấy 2 chữ số thập phân", answering Jay's "decimal places are inconsistent (12.4837 next
   to two-decimal figures)".

   Applied WHERE A VALUE ENTERS THE FORM — on import and when a saved quote is opened — rather than
   in the input box. Rounding for display alone is the drift trap: the box would read 12.48 while
   the payload still held 12.4837, and the two would disagree until somebody saved. Doing it here
   means what is shown IS what is stored.

   A quote that is opened and not saved is not rewritten: this changes the form, and the row on
   disk only follows if somebody presses Save. CP-000087 moves 12.4837 -> 12.48, which is $0.0037 a
   unit and $0.37 across its 100 EA order.

   PERCENTAGES AND QUANTITIES ARE NOT TOUCHED. A ratio of 12.5% is not money, and an order of 3,000
   EA has no cents; rounding them would be this rule applied where it was never the complaint. */
const MONEY_KEYS = [
  'skin', 'cmt', 'ohp', 'frame', 'fprofit', 'handling', 'transport', 'moq', 'addMat',
  'ffcmt', 'shortLead', 'inlineRrp', 'inlineT1',
  'bench_skin', 'bench_cmt', 'bench_ohp', 'bench_frame',
];
const toCents = (x) => {
  const str = String(x ?? '').trim();
  /* An empty money field is '', never `undefined` — a React input handed `undefined` becomes
     uncontrolled and React warns about it on the first keystroke. The importers used to spell this
     `?? ''` at each call site; it belongs here, once. */
  if (str === '') return '';
  const n = Number(str);
  return Number.isFinite(n) ? String(Math.round(n * 100) / 100) : x;
};
const centsInValues = (values) => {
  const out = { ...values };
  MONEY_KEYS.forEach((k) => { if (k in out) out[k] = toCents(out[k]); });
  // The additional-material lines carry their own figures and are money too.
  if (Array.isArray(out.addMatLines)) {
    out.addMatLines = out.addMatLines.map((r) => ({ ...r, amt: toCents(r.amt) }));
  }
  if (Array.isArray(out.benchWhyMore)) {
    out.benchWhyMore = out.benchWhyMore.map((r) => ({ ...r, amt: toCents(r.amt) }));
  }
  return out;
};

/* Takes the quote's OWN rules, resolved at its creation: the overhead a quote is split against must
   be the one in force when it was raised, or reopening an old quote would fit it to a figure set
   after it was signed. */
const seedTierParts = (values, rules) => {
  const OH = ruleSet(rules).tier_oh_pct;
  const out = { ...values };
  [['t0Ratio', 't0Oh', 't0Profit'], ['t1Ratio', 't1Oh', 't1Profit']].forEach(([total, oh, profit]) => {
    if (String(out[profit] ?? '').trim() !== '') return;
    const stored = String(out[total] ?? '').trim();
    if (stored === '') { out[oh] = OH; out[profit] = ''; return; }
    /* THE STORED TOTAL IS WHAT THAT QUOTE WAS APPROVED AT, so it is what comes back — the split is
       fitted to it, never the other way round. A ratio at or above the fixed overhead splits into
       overhead + the rest; one below it (CP-000011 is quoted at 8%) keeps its whole figure as the
       overhead rather than being repriced upwards to satisfy a rule made after it was signed. */
    const n = Number(stored) || 0;
    out[oh] = n >= OH ? OH : n;
    out[profit] = String(Math.round((n - (Number(out[oh]) || 0)) * 100) / 100);
  });
  return out;
};

/* WHAT AN OPTION MAY CHANGE — Max, 2026-09-18: "sẽ có 1 phần condition để user có thể customize
   bằng cách tích chọn. Sau khi tick chọn xong và bấm done thì sẽ ra option 1... Nếu đã có option mà
   thay đổi số ở trên thì giá trị trong option sẽ phải đổi theo."

   THAT LAST CLAUSE IS THE WHOLE DESIGN. An option stores ONLY the conditions it was ticked for —
   never a copy of the quote — so everything it does not override follows the inputs above it and a
   correction to the vendor's price moves all three options with it. An option holding its own copy
   of the costing would be three quotes drifting apart behind one screen, which is the thing a
   comparison exists to prevent.

   Six conditions, and all six are things this calculator already prices differently: the order
   size, which of the two costing conventions the margin is taken on, either tier's profit, the
   collab premium and the short-lead surcharge. Nothing here is a new rule — an option is the quote
   asked again with one or two answers changed. */
/* The extra-cost lines an option may drop, in the order block 4 shows them. */
const EXTRA_ITEM_KEYS = ['fprofit', 'handling', 'transport', 'moq', 'addMat', 'ffcmt', 'shortLead'];

const OPT_MAX = 3;
/* The levers an option cannot pull on the CBD method: there is no basis convention, no T1 profit
   and no T2 ratio to change — the 30% and the 1.3 are rules. */
const CBD_NO_LEVER = ['marginBasis', 't0Profit', 't1Ratio'];
/* The quote with one option's answers put in its place. `values` alone is replaced — the overrides
   and the auto flag are the SKU's and an option is not a different SKU. */

/* Which ratio each case needs — CORRECTED by Max, 2026-09-14: "Case A thì T2 bắt buộc còn case B
   là T1 bắt buộc". I had them the other way round.

   Read the keys through their labels or this looks wrong twice over: **'t0Ratio' is the box
   labelled T1 ratio** and **'t1Ratio' is the box labelled T2 ratio** (the ladder moved up a nudge
   the same day; the stored keys did not — see the note in i18n.js). So:
     Case A (HPAC Direct)   needs T2  ->  't1Ratio'
     Case B (Subsidiary-led) needs T1  ->  't0Ratio' */
const CASE_NEEDS = { a: ['t1Ratio'], b: ['t0Ratio'], both: ['t0Ratio', 't1Ratio'] };

// The entities the licensed model is quoted for. Editable — this is a starting list, not a rule.
const BLANK_ENTITY = (name, fx) => ({ name, fx, srp: '' });
const DEFAULT_ENTITIES = () => [
  BLANK_ENTITY('HUSA (USD)', 1),
  BLANK_ENTITY('HEMA (EUR)', 0.92),
  BLANK_ENTITY('HJPN (JPY)', 155),
];

// The seven items that are recovered as an HPAC marketing debit rather than marked up. `auto` marks
// the ones the guideline computes for you; Additional material is always typed.
// The seven excluded items, as translation keys — `t` is applied at render so switching language
// relabels the form without rebuilding these tables. `auto` marks the ones the guideline computes.
/* The FM fee left this list on 2026-09-04 (Jay): it is part of the base cost now, so it is asked for
   up with the frame instead of down here among the costs that are debited rather than marked up. */
const EXCLUDED_FIELDS = [
  { key: 'fprofit', tk: 'fprofit', auto: true },
  { key: 'handling', tk: 'handling', auto: true },
  { key: 'transport', tk: 'transport', auto: true },
  { key: 'moq', tk: 'moqCharge', auto: true },
  /* Typed as a LIST since 2026-09-18, under the boxes rather than among them — but still one of
     the excluded items, because the cost ledger in Results is built from this table. */
  { key: 'addMat', tk: 'addMat', auto: false, asList: true },
  /* Charged only when Helinox furnishes the material — see MATERIAL_SUPPLY in pricingModel.js. The
     box stays in the list rather than hiding, because a reader scanning EXCLUDED ITEMS for where a
     cost went should find it named and told why it is nil. */
  { key: 'ffcmt', tk: 'ffcmt', auto: false, needsFf: true },
  { key: 'shortLead', tk: 'shortLead', auto: true },
];

/* The FM fee sits here, after Vendor OH & Profit (Max, 2026-09-04) — it is part of the base cost
   now, so it is asked for with the rest of the base rather than down in EXCLUDED ITEMS. It keeps the
   auto value and the per-field override it had there, which is why it is rendered by its own
   function rather than by `money`. */
/* A figure inside a worked line, at the precision that makes the line TRUE. fmtUSD rounds to two
   decimals, and "$13.50 × 100 = $1,350.20" is a line that does not multiply out — the per-unit
   excluded total is 13.502. Two decimals when two decimals are exact, up to four when they are not,
   so the extra digits appear only where they are load-bearing. */
const fmtWork = (v) => {
  const n = Number(v) || 0;
  const two = Math.round(n * 100) / 100;
  if (Math.abs(n - two) < 1e-9) return fmtUSD(n);
  return `$${n.toFixed(4).replace(/0+$/, '').replace(/\.$/, '')}`;
};

/* "Max", not "Max Bui" (Max, 2026-09-11). Rendered only — submitted_by keeps the whole name. */
const givenName = (name) => String(name || '').trim().split(/\s+/)[0] || '';

/* The vendor's own sewing cost, which is step 2. The frame and the FM fee left this list on
   2026-09-17 for a step of their own — they are bought elsewhere and priced off another list. The
   MARGIN is still taken on all five together (`markupBase` in src/pricingModel.js is unchanged);
   this is how the form reads, not what the arithmetic is. */
/* The three factory lines come off the shared list, which the approver's panel also walks — a
   second copy here is how the two screens come to compare a quote on different lines. The frame is
   the list's fourth member and is priced in its own block, so it is dropped from THIS one. */
const FACTORY_FIELDS = CP_BENCH_LINES.filter((l) => l.key !== 'frame');

/* THE LADDERS, printed as the hint of the box each one produces. They are the vendor's own price
   table (tabled 2026-09-17) and they mirror `moqRate` and `slRate` in src/pricingModel.js — the
   label a band carries here has to match the band that function picks, or the screen would
   underline one step while the number came from another. */
const MOQ_LADDER = [
  { upto: 100, label: '≤100 EA', rate: '100%' },
  { upto: 299, label: '101–299 EA', rate: '60%' },
  { upto: 499, label: '300–499 EA', rate: '30%' },
  { upto: Infinity, label: '500+ EA', rate: 'none' },
];
/* The factory CBD's own ladder ('Chair One'!K8:M11) — the one the CBD method charges. */
const CBD_MOQ_LADDER = [
  { upto: 99, label: '1–99 EA', rate: '100%' },
  { upto: 299, label: '100–299 EA', rate: '60%' },
  { upto: 499, label: '300–499 EA', rate: '30%' },
  { upto: Infinity, label: '500+ EA', rate: 'none' },
];
const SL_LADDER = [
  { from: 75, label: '≥75 days', n: '≥75', rate: 'none' },
  { from: 65, label: '65–74 days', n: '65–74', rate: '30%' },
  /* EVERYTHING UNDER 65, not 60–64 — Max, 2026-09-17. `slRate` returns 50% for any lead time
     below 65 days, so the old label quietly implied that under 60 nothing was charged. */
  { from: -Infinity, label: '<65 days', n: '<65', rate: '50%' },
];
/* THE ROWS OF THE TWO-CONVENTION TABLE, in one list so the two columns cannot describe different
   things. `both` means the figure does not depend on the convention — MSRP is built from the inline
   RRP and the premium, and the partner price from MSRP, so neither moves when the basis changes;
   printing them twice would invite a reader to look for a difference that cannot exist.
   A tier with no ratio is null and prints a dash: a zero would read as "this tier earns nothing",
   which is a different statement from "this quote has no such tier". */
/* THE SAME ROWS, ONE COLUMN PER OPTION — Max, 2026-09-22: "chỗ này thay bằng các option tính thì
   mới đúng". The table used to print the quote on the two COSTING CONVENTIONS side by side, which
   answered a question the radio above it already settles; the comparison somebody actually makes
   here is between the options they built, so those are the columns.

   Each row reads a priced quote rather than holding two numbers, so a column is added by pricing
   one more quote and nothing in this table has to know how many there are. */
/* THE ROWS OF THE COMPARISON, one column per option — Max, 2026-09-22.

   NO EXPLANATIONS UNDER THE LABELS: "ko cần diễn giải nữa bạn cho chữ i bên các cột option tính
   toán nhá". A sentence under a row name describes the row for every column at once, and the
   columns no longer agree — an option can be on another convention or another ratio. The working
   moved INTO the cells, where each figure carries its own, which is also where somebody checking
   one number is already looking. */
const CASE_ROWS = (t, base, v, multi) => {
  const pct = (x) => `${(Number(x) || 0).toFixed(1)}%`;
  const split = (s2) => (s2 && s2.oh > 0 ? ` · ${t('pricing.ohPart')} ${pct(s2.oh * 100)} + ${t('pricing.profitPart')} ${pct(s2.profit * 100)}` : '');
  const sideOfCalc = (c) => c.byBasis[c.marginBasis === 'buying' ? 'buying' : 'base'];
  const qtyOf = (c) => c.qty || 0;
  const times = (f) => (c) => { const x = f(c); return x === null || x === undefined ? null : x * qtyOf(c); };
  return [
    /* THE ORDER MAX ASKED FOR, 2026-09-18: "Gía trị T xong tới revenue tới cost rồi tới Profit".
       It reads as one sentence down the column — this is the price per unit, this is what the order
       is worth at it, this is what the order costs, this is what is left. */
    { key: 'on', label: t('pricing.marginTakenOn'), get: (c) => sideOfCalc(c).on },
    { key: 't1', cls: 'big', label: t('pricing.t0'), get: (c) => sideOfCalc(c).t1 },
    { key: 't2', cls: 'big', label: t('pricing.t1'), get: (c) => sideOfCalc(c).t2 },
    { key: 'r1', label: t('pricing.revenueAtT1'), get: times((c) => sideOfCalc(c).t1) },
    { key: 'r2', label: t('pricing.revenueAtT2'), get: times((c) => sideOfCalc(c).t2) },
    { key: 'debit', label: t('pricing.hpacTotal'), get: (c) => sideOfCalc(c).debit },
    { key: 'tp1', cls: 'cut', label: t('pricing.totalProfitAtT1'), get: times((c) => sideOfCalc(c).p1) },
    { key: 'tp2', label: t('pricing.totalProfitAtT2'), get: times((c) => sideOfCalc(c).p2) },
    /* PROFIT AT T1 / T2 AND THEIR TWO PARTS CAME OFF — Max, 2026-09-24: "chỗ này thấy sai và
       không cần lắm á". Per unit they were the margin over the base, which since this morning
       marks up the whole extra cost — so the row read as a profit while carrying a recovery, and
       the two rows under it split a figure that was already the wrong one to read. What the
       decision is made on is the ORDER, and Total profit at T1 / T2 above says that. */
    { key: 'msrp', cls: 'cut', label: t('pricing.msrp'), get: (c) => c.msrp },
    { key: 'partner', label: t('pricing.partnerPrice'), get: (c) => c.partnerPrice },
  ];
};

const slBandLabel = (days) => {
  if (days === null || days === undefined) return null;
  return (SL_LADDER.find((b) => days >= b.from) || SL_LADDER[SL_LADDER.length - 1]).label;
};

/* THE GUIDE (Max 01/11: every module has one). It walks the General mode, which is where the screen opens; a step whose
   control is not drawn for this person (the inputs on Saved projects, say) is skipped by the tour itself. */
const TOUR = [
  { selector: '.cp-modes', title: 'tourPricingCalc.modesT', body: 'tourPricingCalc.modesB' },
  { selector: '.cp-toolbar .cp-name', title: 'tourPricingCalc.nameT', body: 'tourPricingCalc.nameB' },
  { selector: '.cp-toolbar', title: 'tourPricingCalc.actionsT', body: 'tourPricingCalc.actionsB' },
  { selector: '.cp-skubar', title: 'tourPricingCalc.skuT', body: 'tourPricingCalc.skuB' },
  { selector: '.cp-inputs', title: 'tourPricingCalc.inputsT', body: 'tourPricingCalc.inputsB' },
  { selector: '.cp-results', title: 'tourPricingCalc.resultT', body: 'tourPricingCalc.resultB' },
];

export default function PricingCalculator() {
  const { t } = useTranslation();
  const [mode, setMode] = useState('general');
  const [projectName, setProjectName] = useState('');
  const [projectId, setProjectId] = useState('');
  // What this quote is filed under. Free text, offered from the values already in use.
  const [folder, setFolder] = useState('');
  // Folders the reader has collapsed. A set of names, so a folder that appears later starts open.
  const [shutFolders, setShutFolders] = useState([]);
  // The registry — folders that exist even with nothing in them.
  const [folders, setFolders] = useState([]);
  const [newFolder, setNewFolder] = useState('');
  const [renaming, setRenaming] = useState(null);      // { from, to }
  const [dropOn, setDropOn] = useState(null);          // the heading under the cursor
  const [confirmFolderDel, setConfirmFolderDel] = useState('');
  const dragging = useRef(null);                       // the project id in flight
  const [skus, setSkus] = useState([BLANK_SKU()]);
  const [cur, setCur] = useState(0);
  /* The original tool's defaults, not invented ones: logistics 15%, wholesale 50% of SRP, wholesale
     royalty 11%, DTC royalty 5.5%. At a 50% wholesale ratio those two royalties are the same real
     burden, which is why they are quoted as a pair. */
  const BLANK_GLOBALS = {
    globalMsrp: '', t1Fob: '', sku: '', logistics: 15, wholesale: 50, wsRoyalty: 11, dtcRoyalty: 5.5,
  };
  const [globals, setGlobals] = useState(BLANK_GLOBALS);
  const [entities, setEntities] = useState(DEFAULT_ENTITIES());
  const [frames, setFrames] = useState([]);
  const [projects, setProjects] = useState([]);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [ruleDraft, setRuleDraft] = useState({});
  const [ruleMsg, setRuleMsg] = useState('');
  /* OPA / SCT was briefly a fourth tab here. It moved to its own page under Costing on the same
     day (Max, 2026-08-29) — see src/31_OpaSctPage.jsx. Nothing of it is left on this screen: the
     same list reachable two ways would have gone stale in one of them. */
  // The saved list sorts and filters in the browser, the way Style Master and Recon do: it is capped
  // at 200 rows, so the column menus can offer the values that are really there.
  const [savedSort, setSavedSort] = useState({ key: '', dir: 'asc' });
  const [savedFilters, setSavedFilters] = useState({});
  const [savedOpenFilter, setSavedOpenFilter] = useState(null);
  // The project id awaiting confirmation. window.confirm was doing this; a browser dialog blocks the
  // page, cannot be styled, and reads as the browser talking rather than the portal (Max, 2026-08-28).
  const [confirmDel, setConfirmDel] = useState('');
  // The two-level gate: which quote is mid-action, and the rejection being written.
  const [busyId, setBusyId] = useState('');
  const [approvalMsg, setApprovalMsg] = useState(null);
  // True while the discard question is up. A costing half-typed is worth more than the click that
  // would throw it away, so the button asks before it clears anything (Max, 2026-08-28).
  const [confirmCancel, setConfirmCancel] = useState(false);

  const sku = skus[cur] || BLANK_SKU();
  const v = sku.values;
  /* THE RULES THIS SCREEN IS PRICING UNDER — Max, 2026-09-29. `currentRules` is what a NEW quote
     uses; opening a saved one replaces them with the rules in force when that quote was CREATED, so
     an admin changing a constant today cannot move a quote raised last month.
     Undefined until the fetch lands, and `ruleSet` falls back to the module constants meanwhile —
     the calculator computes on every keystroke and must not blank out waiting for the network. */
  const [rules, setRules] = useState(null);
  const [currentRules, setCurrentRules] = useState(null);
  const [ruleHistory, setRuleHistory] = useState([]);
  const [mayEditRules, setMayEditRules] = useState(false);

  const calc = useMemo(() => computeSku(sku, rules), [sku, rules]);
  /* THE SAME DERIVATION THE APPROVER'S SCREEN USES, for the summary at the foot of this one —
     Max, 2026-09-29: the person building the quote should see what the approver will see before
     sending it. Shared rather than copied: the whole value of showing it here is that it IS the
     same figures, and two copies of an arithmetic that has to agree is how they stop agreeing. */
  const summaryFig = useMemo(() => cpFigures(sku, rules), [sku, rules]);
  /* Block 7's cards, from the same call the approver's screen makes. It reads EVERY SKU, not the
     one on screen: the decision it draws is one per quote, so a card computed on the SKU whose tab
     happens to be open would show a fraction of what is being decided. */
  const summaryCards = useMemo(() => cpCards(skus, rules, t), [skus, rules, t]);
  // Whether the tier ratio on screen has to be filled in for the case this SKU is under.
  const needs = (key) => (CASE_NEEDS[v.dealCase] || []).includes(key);
  // Which costing convention this quote is on; four places on the results card read it.
  const onBuying = calc.marginBasis === 'buying';
  // The FM fee keeps the auto/override behaviour it had in EXCLUDED ITEMS, in its new home.
  const fmAuto = sku.auto !== false && !sku.overrides.fmfee;
  const landed = useMemo(() => landedCost(globals), [globals]);
  const entCalc = useMemo(
    () => entities.map((e) => ({ ent: e, r: computeEntity(e, globals, landed) })),
    [entities, globals, landed],
  );

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/frames`, { credentials: 'include' });
        const d = await r.json();
        if (r.ok) setFrames(d.frames || []);
      } catch (err) { /* the picker just stays empty; every field is still typeable */ }
    })();
  }, []);

  const loadRules = useCallback(async () => {
    try {
      const r = await fetch(`${API}/rules`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) return;
      setCurrentRules(d.current || null);
      setRuleHistory(Array.isArray(d.history) ? d.history : []);
      setMayEditRules(!!d.mayEdit);
      setRules((prev) => prev || d.current || null);
    } catch (err) { /* the constants still price; a missing rule book is not a broken screen */ }
  }, []);
  useEffect(() => { loadRules(); }, [loadRules]);

  const loadProjects = useCallback(async () => {
    try {
      const r = await fetch(`${API}/projects`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setProjects(d.rows || []);
    } catch (err) { /* leave the previous list on screen */ }
  }, []);
  const loadFolders = useCallback(async () => {
    try {
      const r = await fetch(`${API}/folders`, { credentials: 'include' });
      const d = await r.json();
      if (r.ok) setFolders(d.folders || []);
    } catch (err) { /* the list still groups by whatever is on the quotes */ }
  }, []);
  useEffect(() => { if (showSaved) { loadProjects(); loadFolders(); } }, [showSaved, loadProjects, loadFolders]);

  /* One quote, one column. Its own endpoint rather than a save: filing is not costing, and it has to
     work on an approved quote — locked against changing its numbers is not locked against being
     tidied away. */
  const fileInto = async (id, target) => {
    setError('');
    try {
      const r = await fetch(`${API}/projects/${id}/folder`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ folder: target }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xPricing.moveFailed', { status: r.status }));
      await loadProjects();
      await loadFolders();
      if (id === projectId) setFolder(target);
    } catch (err) { setError(err.message); }
  };

  const makeFolder = async () => {
    const name = newFolder.trim();
    if (!name) return;
    setError('');
    try {
      const r = await fetch(`${API}/folders`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xPricing.createFailed', { status: r.status }));
      setNewFolder('');
      await loadFolders();
    } catch (err) { setError(err.message); }
  };

  const renameFolder = async () => {
    const { from, to } = renaming || {};
    if (!from || !to || !to.trim() || to.trim() === from) { setRenaming(null); return; }
    setError('');
    try {
      const r = await fetch(`${API}/folders/${encodeURIComponent(from)}`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: to.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xPricing.renameFailed', { status: r.status }));
      setRenaming(null);
      await loadProjects();
      await loadFolders();
    } catch (err) { setError(err.message); setRenaming(null); }
  };

  const dropFolder = async (name) => {
    const id = dragging.current;
    dragging.current = null;
    setDropOn(null);
    if (id) await fileInto(id, name);
  };

  /* The saved list, sorted and filtered in the browser — the same Excel-style header Style Master and
     Recon use, so the column menus offer the values that are really in the list rather than a guess.
     The list is capped at 200 rows server-side, which is what makes doing it here honest. */
  const SAVED_COLUMNS = [
    { key: 'project_id', tk: 'colProject' },
    { key: 'name', tk: 'colName' },
    { key: 'modeLabel', tk: 'colMode' },
    { key: 'count', tk: 'colSkus', num: true },
    /* Short labels in the table: "Total buying cost" and "T1 (Helinox entity)" explain themselves on
       the results card, where there is room; as column headers they only push the numbers apart. */
    /* Where the quote is in the two-level gate. Before the buying cost, because it qualifies every
       number to its right: an approved $4.83 and a draft $4.83 are not the same claim. */
    { key: 'statusLabel', tk: 'colStatus' },
    { key: 'buying_cost', tk: 'colBuying', num: true },
    { key: 't1', tk: 'colT1', num: true },
    { key: 'msrpAny', tk: 'msrp', num: true },
    { key: 'savedBy', tk: 'colSavedBy' },
    { key: 'savedOn', tk: 'colSaved' },
  ];
  const SAVED_GETTERS = Object.fromEntries(SAVED_COLUMNS.map((c) => [
    c.key,
    c.num ? (row) => Number(row[c.key] ?? 0) : (row) => row[c.key] || '',
  ]));

  // The derived columns are computed ONCE, here, so sorting and filtering see the same text the cell
  // shows — a filter offering "General" while the row sorts on "general" is how those two drift.
  const savedRows = useMemo(() => {
    const all = projects.map((pr) => ({
      ...pr,
      modeLabel: pr.mode === 'general' ? t('pricing.general') : t('pricing.licensed'),
      count: pr.sku_count ?? pr.entity_count ?? 0,
      msrpAny: pr.msrp ?? pr.global_msrp ?? null,
      savedBy: pr.updated_by || pr.created_by || '',
      savedOn: String(pr.updated_at || '').slice(0, 10),
      statusLabel: t(`pricing.st${(pr.approval_status || 'Draft').replace(/\s/g, '')}`),
    }));
    const kept = Object.keys(savedFilters).length
      ? all.filter((r) => rowPassesColumnFilters(r, savedFilters, SAVED_GETTERS))
      : all;
    return savedSort.key ? sortRows(kept, savedSort, SAVED_GETTERS) : kept;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects, savedFilters, savedSort, t]);

  /* The values already in use, for the datalist — from the saved list rather than a second endpoint,
     because the list is already loaded and capped at 200. */
  const folderOptions = useMemo(
    () => [...new Set(projects.map((pr) => (pr.folder || '').trim()).filter(Boolean))].sort(),
    [projects],
  );

  /* Rows grouped into folders, AFTER sorting and filtering — a group is a heading over the rows that
     survived, not a second way of choosing them. Unfiled last: it is where new work lands, and a
     reader scanning for a season should not have to pass it first. */
  const savedGroups = useMemo(() => {
    const byFolder = new Map();
    savedRows.forEach((r) => {
      const key = (r.folder || '').trim();
      if (!byFolder.has(key)) byFolder.set(key, []);
      byFolder.get(key).push(r);
    });
    /* Every folder in the registry gets a heading, empty or not — an empty folder you cannot see is
       an empty folder you cannot drop into, which is the whole reason it exists. Names found on
       quotes but missing from the registry are shown too, so nothing filed before the registry
       existed disappears. */
    const named = [...new Set([...folders.map((f) => f.name), ...[...byFolder.keys()].filter(Boolean)])]
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const out = named.map((k) => ({ key: k, label: k, rows: byFolder.get(k) || [] }));
    if (byFolder.has('')) out.push({ key: '', label: t('pricing.folderNone'), rows: byFolder.get('') });
    return out;
  }, [savedRows, folders, t]);

  const savedTh = (column) => ({
    label: t(`pricing.${column.tk}`),
    sortKey: column.key,
    sort: savedSort,
    className: column.num ? 'num' : '',
    onSort: (key, dir) => setSavedSort((prev) => ({
      key, dir: dir || (prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc'),
    })),
    filterValues: savedOpenFilter?.key === column.key
      ? distinctColumnValues(savedRows.concat(projects.length ? [] : []), column.key, savedFilters, SAVED_GETTERS)
      : undefined,
    filterSelected: savedFilters[column.key],
    filterOpen: savedOpenFilter?.key === column.key,
    filterAnchor: savedOpenFilter?.key === column.key ? savedOpenFilter.anchor : null,
    onOpenFilter: (key, anchor) => setSavedOpenFilter({ key, anchor }),
    onCloseFilter: () => setSavedOpenFilter(null),
    onApplyFilter: (key, picked, all) => {
      setSavedFilters((prev) => {
        const next = { ...prev };
        if (!picked.length || picked.length === all.length) delete next[key];
        else next[key] = picked;
        return next;
      });
      setSavedOpenFilter(null);
    },
  });

  /* ---- editing --------------------------------------------------------------------------- */
  const setVal = (key, value) => setSkus((prev) => prev.map((s, i) => (i === cur
    ? { ...s, values: { ...s.values, [key]: value } }
    : s)));
  /* THE PARTS ARE TYPED, THE TOTAL IS DERIVED — 2026-09-18, the whiteboard's "Helinox O.H 15%
     (Fixed)" and "Helinox profit 10% (adjustable)" inside one tier. `t0Ratio` / `t1Ratio` sit in
     the payload JSON of every quote ever saved and are what the price, the required-field rule,
     the mail body and the Excel template all read, so the split writes the total in the SAME
     update instead of becoming a second source of truth beside it. Both parts empty leaves the
     total empty, which is still "this tier has no ratio" and not a zero. */
  /* T2 IS ONE RATIO, NOT TWO HALVES — Max, 2026-09-24: "T2 ko có tách ra Helinox O.H và Helinox
     profit". So its box writes the total, and it must CLEAR the halves with it: `tierParts` reads
     the two halves in preference to the total, so leaving 15 and 30 behind would quietly keep
     pricing at 45% however the box was changed. */
  const setT2Ratio = (value) => setSkus((prev) => prev.map((sk, i) => (i !== cur ? sk : {
    ...sk, values: { ...sk.values, t1Ratio: value, t1Oh: '', t1Profit: '' },
  })));

  const setPart = (totalKey, partKey, otherKey, value) => setSkus((prev) => prev.map((s, i) => {
    if (i !== cur) return s;
    const values = { ...s.values, [partKey]: value };
    /* The total follows the PROFIT half. The overhead stands at 15% on every quote whether or not
       it has such a tier, so adding it to an unset tier would put a 15% T1 on a quote nobody has
       chosen one for — and Case B would stop asking for the ratio it requires. */
    const profit = String(values[totalKey === 't0Ratio' ? 't0Profit' : 't1Profit'] ?? '').trim();
    const oh = String(values[totalKey === 't0Ratio' ? 't0Oh' : 't1Oh'] ?? '').trim();
    values[totalKey] = profit === ''
      ? ''
      : String(Math.round(((Number(oh) || 0) + (Number(profit) || 0)) * 100) / 100);
    return { ...s, values };
  }));
  /* The additional-material lines. `addMatLines` is the list and `addMat` follows it, because
     `addMat` is what the arithmetic, the Excel template and every saved quote already read — the
     list is a better way of ARRIVING at that number, not a second number beside it. */
  const setAddMat = (i, patch) => setSkus((prev) => prev.map((sk, ix) => {
    if (ix !== cur) return sk;
    const base = sk.values.addMatLines
      || (String(sk.values.addMat ?? '').trim() === '' ? [] : [{ name: '', amt: sk.values.addMat }]);
    const lines = base.slice();
    lines[i] = { ...(lines[i] || { name: '', amt: '' }), ...patch };
    const total = lines.reduce((a, l) => a + (Number(l.amt) || 0), 0);
    return { ...sk, values: { ...sk.values, addMatLines: lines, addMat: lines.length ? String(total) : '' } };
  }));
  const dropAddMat = (i) => setSkus((prev) => prev.map((sk, ix) => {
    if (ix !== cur) return sk;
    const lines = (sk.values.addMatLines || []).filter((_, j) => j !== i);
    const total = lines.reduce((a, l) => a + (Number(l.amt) || 0), 0);
    return { ...sk, values: { ...sk.values, addMatLines: lines, addMat: lines.length ? String(total) : '' } };
  }));

  /* The option being edited, by index, or null when the panel is shut. It is page state and not
     quote state: which card somebody has open is not part of the costing. */
  const [optEdit, setOptEdit] = useState(null);
  /* WHAT CANCEL GOES BACK TO — Max, 2026-09-24: "thiếu nút cancel". The editor writes on every
     keystroke, which is what makes the card beside it move as you type, so there is no draft to
     throw away: leaving without a record of how the option started would leave the typing in
     place and the button a lie. The option is copied when the editor OPENS and put back if
     Cancel is pressed. */
  const [optBefore, setOptBefore] = useState(null);
  const openOptEdit = (i) => {
    setOptBefore(options[i] ? JSON.parse(JSON.stringify(options[i])) : null);
    setOptEdit(i);
  };
  const closeOptEdit = (revert) => {
    if (revert && optBefore && optEdit !== null) {
      const back = optBefore;
      const at = optEdit;
      setOptions((prev) => prev.map((o, i) => (i === at ? back : o)));
    }
    setOptBefore(null);
    setOptEdit(null);
  };
  const options = sku.options || [];
  const setOptions = (fn) => setSkus((prev) => prev.map((s, i) => (i === cur
    ? { ...s, options: fn(s.options || []) }
    : s)));
  /* A new option starts EMPTY — nothing ticked. It is the quote as it stands, and every tick is a
     deliberate departure from it; pre-ticking anything would put a number in front of somebody
     that they never chose. */
  const addOption = () => {
    // The always-on conditions start at what the quote says, so the option opens on the run it
    // departs from rather than on an empty box that silently means "the quote's quantity".
    /* Only the always-on conditions are seeded — the order quantity, which every option is priced
       at. The levers start unpicked, because an option that has not been told what to change is
       the quote and should say so rather than arrive pre-filled with the quote's own answers. */
    const seed = {};
    /* THE CONVENTION COMES PICKED — Max, 2026-09-22: "hoặc là bạn chọn nó sẵn từ đầu luôn", said
       right after the radio above the table came off. Set to the OTHER one, so a new option opens
       as the column that radio used to give: the same quote on the other costing convention,
       there to be read rather than switched to. Anything else about it is still a lever to pick. */
    setOptions((prev) => (prev.length >= OPT_MAX ? prev : [...prev, {
      on: { marginBasis: true },
      values: { ...seed, marginBasis: onBuying ? 'base' : 'buying' },
    }]));
    const at = Math.min(options.length, OPT_MAX - 1);
    /* A new option's "before" is the option as it was created, so Cancel on one that was just
       added undoes the typing rather than the option — Remove is what undoes the option. */
    setOptBefore(null);
    setOptEdit(at);
  };
  const setOptCond = (idx, key, patch) => setOptions((prev) => prev.map((o, i) => (i === idx
    ? { ...o, on: { ...o.on, ...(patch.on || {}) }, values: { ...o.values, ...(patch.values || {}) } }
    : o)));
  /* Picking a lever opens an EMPTY box — see OPT_CONDITIONS. The one exception is the basis: a
     select cannot be empty, and the only reason to pick it is to price this option on the OTHER
     convention, so it opens there and the figures move the moment it is chosen. */
  /* TICKING FILLS THE BOX WITH WHAT THE QUOTE SAYS — Max, 2026-09-24: "cứ để sẵn số phần trăm khi
     nào cho thì active và có thể sửa á". It opened empty, with the quote's figure as a grey
     placeholder, because of his own earlier objection: an option that repeats the quote IS the
     quote — "chọn như ko chọn vậy chọn chi ta".

     That objection is answered by something else now. The card beside the editor already says
     "Same as the quote" in words when nothing differs, and `optDepartureText` compares each value
     against the quote's before naming it — so a tick that changes nothing is visible either way,
     and a placeholder is worse for the thing people actually do, which is nudge a number rather
     than retype it.

     AN AUTO FIELD HAS NO TYPED VALUE. The short-lead surcharge is computed unless somebody has
     overridden it, so `v.shortLead` is empty on most quotes while the screen shows $2.98. Seeding
     the empty string there would hand back the empty box this change exists to remove, so what is
     seeded is what the quote is actually pricing with.

     The BASIS keeps seeding the other convention: it is a two-way choice, and offering the one
     already in force is the no-op Max objected to in the first place. */
  const optSeed = (c) => {
    if (c.kind === 'items') return [];      // ticked, with every line still applied
    if (c.kind === 'basis') return onBuying ? 'base' : 'buying';
    const typed = String(v[c.key] ?? '').trim();
    if (typed !== '') return typed;
    const auto = calc.items?.[c.key];
    return auto ? String(Math.round(auto * 100) / 100) : '';
  };
  const pickOptCond = (idx, c) => setOptCond(idx, c.key, {
    on: { [c.key]: true },
    values: { [c.key]: optSeed(c) },
  });
  const dropOptCond = (idx, c) => setOptCond(idx, c.key, { on: { [c.key]: false }, values: { [c.key]: '' } });
  const dropOption = (idx) => {
    setOptions((prev) => prev.filter((_, i) => i !== idx));
    setOptEdit(null);
  };
  // Typing into an auto field is what marks it overridden — the same gesture the original used, and
  // the reason a negotiated number survives the next recalculation.
  const setExcluded = (key, value) => setSkus((prev) => prev.map((s, i) => (i === cur
    ? { ...s, values: { ...s.values, [key]: value }, overrides: { ...s.overrides, [key]: true } }
    : s)));
  const clearOverride = (key) => setSkus((prev) => prev.map((s, i) => {
    if (i !== cur) return s;
    const o = { ...s.overrides };
    delete o[key];
    return { ...s, overrides: o };
  }));
  const setSkuField = (patch) => setSkus((prev) => prev.map((s, i) => (i === cur ? { ...s, ...patch } : s)));

  /* One rule at a time, each with its own reason. A form that saved three at once would take one
     sentence for three different decisions, and the reason is the whole point of the table. */
  const saveRule = async (key) => {
    const d = ruleDraft[key] || {};
    setBusy(true); setRuleMsg('');
    try {
      const r = await fetch(`${API}/rules`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value: Number(d.value), reason: String(d.reason || '') }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || t('xPricing.saveFailed', { status: r.status }));
      await loadRules();
      /* The screen must move to the new rule as well, or the admin saves a change and goes on
         quoting under the old one. Only `currentRules` follows it — a quote already open keeps what
         it was loaded with, which is the rule the whole table exists to protect. */
      if (!projectId) setRules(j.current || null);
      setRuleDraft((prev) => ({ ...prev, [key]: { value: '', reason: '' } }));
      setRuleMsg(t('pricing.ruleSaved'));
    } catch (err) {
      setRuleMsg(err.message);
    } finally {
      setBusy(false);
    }
  };

  const methodNow = (skus[0] && skus[0].values && skus[0].values.pricingMethod) || 'guideline';
  const QUOTE_WIDE = ['partnerName', 'offerSeason', 'priceTerm', 'validFrom', 'validTo', 'factoryName'];
  const addSku = () => {
    const first = (skus[0] && skus[0].values) || {};
    const blank = BLANK_SKU(rules, methodNow);
    QUOTE_WIDE.forEach((k) => { if (first[k] !== undefined) blank.values[k] = first[k]; });
    setSkus((prev) => [...prev, blank]); setCur(skus.length);
  };
  /* ONE METHOD FOR THE WHOLE QUOTE: it decides what every tier price means, and a quote whose SKUs
     were priced two ways would add two different kinds of number into one order total. */
  const setMethod = (m) => setSkus((prev) => prev.map((sk) => ({ ...sk, values: { ...sk.values, pricingMethod: m, ...calcFlag(m) } })));
  // A fact about the whole quote (the partner, the season, the price term, the validity), kept on every SKU.
  const setQuoteVal = (key, value) => setSkus((prev) => prev.map((sk) => ({ ...sk, values: { ...sk.values, [key]: value } })));
  const dupSku = () => {
    setSkus((prev) => [...prev, JSON.parse(JSON.stringify(prev[cur]))]);
    setCur(skus.length);
  };
  const delSku = () => {
    if (skus.length <= 1) return;
    setSkus((prev) => prev.filter((_, i) => i !== cur));
    setCur((c) => Math.max(0, c - 1));
  };

  /* ---- save / load / export -------------------------------------------------------------- */
  const payload = () => {
    if (mode === 'general') {
      const all = skus.map((s) => computeSku(s, rules));
      return {
        mode,
        /* Each SKU carries its RESOLVED numbers alongside its inputs. The rules that turn one into
           the other live in pricingModel.js and run here; the export reads what is stored rather
           than re-deriving it in a second language, because two implementations of the Costing
           Guideline is exactly how an export starts disagreeing with the screen somebody signed
           off. The inputs are stored too, so a project reloads and recalculates. */
        skus: skus.map((s, i) => ({ ...s, resolved: all[i] })),
        totals: {
          orderQty: all.reduce((a, x) => a + x.qty, 0),
          markupBase: all[0]?.markupBase ?? null,
          buyingCost: all[0]?.buyingCost ?? null,
          hpacDebit: all.reduce((a, x) => a + x.hpacDebit, 0),
          t1: all[0]?.t1 ?? null,
          msrp: all[0]?.msrp ?? null,
          partnerPrice: all[0]?.partnerPrice ?? null,
        },
      };
    }
    return {
      mode, globals, entities, totals: {
        globalMsrp: num(globals.globalMsrp), t1Fob: num(globals.t1Fob), landedUsd: landed,
      },
    };
  };

  /** Is there anything on screen worth asking about before it is thrown away? */
  const hasWork = () => {
    if (projectName.trim()) return true;
    // Photos taken before the first save are work too — Cancel has to offer to throw them away
    // rather than clearing them without asking.
    if (pendingPhotos.length || pendingMessages.length) return true;
    if (mode === 'general') {
      return skus.some((sk) => sk.name.trim()
        || Object.entries(sk.values).some(([k, val]) => !['qty', 'supply', 'frameProfitRate', 't1Ratio', 'partnerRatio', 'moqParts',
            'moqRatePct', 'matSupply', 'marginBasis', 't0Ratio', 't0Oh', 't0Profit', 't1Oh', 't1Profit', 'dealCase',
            'pricingMethod', 'absorbMoq', 'absorbSl', 'priceTerm', 'slFlag'].includes(k)
          && val !== '' && val !== null && val !== undefined));
    }
    return !!(String(globals.globalMsrp).trim() || String(globals.t1Fob).trim() || String(globals.sku || '').trim()
      || entities.some((e) => String(e.srp).trim()));
  };

  /** Everything back to a blank costing. Used after a save and by Cancel. */
  /* Photos taken before the quote has a CP number — Max, 2026-09-14: "ảnh được thêm từ lúc tạo
     luôn". They are compressed in the page and held here until the save hands back an id, then
     uploaded against it. Nothing is written if the save fails, so there are no orphan files and no
     temporary upload area to clean up — the same shape the Project Tracker uses for a project that
     does not exist yet. */
  const [pendingPhotos, setPendingPhotos] = useState([]);
  /* And the same for what was written before the quote had a number — Max, 2026-09-14:
     "messages có thể điền khi tạo luôn". Plain strings; the author and the time are stamped by the
     server when they are posted, which is the only place that can be trusted to know them. */
  const [pendingMessages, setPendingMessages] = useState([]);
  // Photos already on the saved quote, reported up by the attachments band.
  const [photoCount, setPhotoCount] = useState(0);

  const resetForm = () => {
    setProjectId('');
    setProjectName('');
    setFolder('');
    setPendingPhotos([]);
    setPendingMessages([]);
    setLoadedGate(null);
    setSkus([BLANK_SKU(rules)]);
    setCur(0);
    setGlobals(BLANK_GLOBALS);
    setEntities(DEFAULT_ENTITIES());
  };

  /**
   * Save, then clear the screen (Max, 2026-08-28). A save FINISHES a costing — the tool is used one
   * quote at a time — so staying in edit mode afterwards invites the next quote to be typed over the
   * one just filed. The project is on the Saved list and one click away if it does need changing.
   */
  /* WHAT A QUOTE MUST CARRY — Max, 2026-09-14. Returns the list of things still missing, so the
     person sees all of them at once rather than one per press of Save.

     The photo rule counts what is HELD as well as what is stored: a photo picked before the first
     save has not reached the server yet, and refusing to save because of that would make the
     "add photos from the start" rule unusable. The licensed model has no tier ratios on screen, so
     only the photo applies to it. */
  const missing = () => {
    const out = [];
    if (!pendingPhotos.length && !photoCount) out.push(t('pricing.needPhoto'));
    if (mode !== 'general') return out;
    skus.forEach((sk, i) => {
      const val = sk.values || {};
      const who = sk.name.trim() || `SKU ${i + 1}`;
      const blank = (x) => x === '' || x === null || x === undefined;
      /* THE CBD METHOD ASKS FOR NONE OF THESE: it has no Case and no tier ratio (the 30% and the
         1.3 are rules), and its T2 is the partner's price, so the MSRP is optional context. */
      /* The inline retail price was required here from 2026-10-26 (Tina's key point) until Max lifted it on 28/10: "bỏ bắt
         buộc nhập thông tin inline trong phiếu". The approval sheet shows "—" for a missing inline figure. */
      if (isCbd(val)) return;
      if (!CASE_NEEDS[val.dealCase]) { out.push(t('pricing.needCase', { sku: who })); }
      else {
        if (CASE_NEEDS[val.dealCase].includes('t0Ratio') && blank(val.t0Ratio)) out.push(t('pricing.needT1', { sku: who }));
        if (CASE_NEEDS[val.dealCase].includes('t1Ratio') && blank(val.t1Ratio)) out.push(t('pricing.needT2', { sku: who }));
      }
      if (blank(val.inlineRrp)) out.push(t('pricing.needInlineRrp', { sku: who }));
      if (blank(val.premium)) out.push(t('pricing.needPremium', { sku: who }));
    });
    return out;
  };

  const save = async () => {
    if (!projectName.trim()) { setError(t('pricing.needName')); return; }
    const gaps = missing();
    if (gaps.length) { setError(gaps.join(' · ')); setStatus(''); return; }
    setBusy(true); setError(''); setStatus('');
    try {
      const r = await fetch(`${API}/projects`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectId || undefined, mode, name: projectName.trim(),
          folder: folder.trim(), payload: payload(),
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || t('xPricing.saveFailed', { status: r.status }));
      const saved = d.projectId;
      /* The held photos go up now that there is something to hang them on. One at a time, and a
         failure is reported rather than swallowed: the quote is already saved, so silently losing
         the pictures would leave the person believing they are there. */
      let photoNote = '';
      if (pendingPhotos.length) {
        let sent = 0;
        let stopped = '';
        for (const ph of pendingPhotos) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const pr = await fetch(`${BACKEND_URL}/api/development/photos/cpq/${encodeURIComponent(saved)}`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ mimeType: ph.mimeType, dataBase64: ph.dataBase64, fileName: ph.fileName }),
            });
            // eslint-disable-next-line no-await-in-loop
            const pd = await pr.json().catch(() => ({}));
            if (!pr.ok) throw new Error(pd.error || t('xPricing.photoUploadFailed', { status: pr.status }));
            sent += 1;
          } catch (photoErr) { stopped = photoErr.message; break; }
        }
        photoNote = stopped
          ? ` · ${t('xPricing.photosPartial', { sent, total: pendingPhotos.length, error: stopped })}`
          : ` · ${t('xPricing.photosSent', { n: sent })}`;
      }
      /* The held messages, in the order they were written. Same reporting rule as the photos: the
         quote is already saved by now, so a failure has to be said out loud. */
      if (pendingMessages.length) {
        let sent = 0;
        let stopped = '';
        for (const body of pendingMessages) {
          try {
            // eslint-disable-next-line no-await-in-loop
            const mr = await fetch(`${BACKEND_URL}/api/pricing/projects/${encodeURIComponent(saved)}/messages`, {
              method: 'POST', credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ body }),
            });
            // eslint-disable-next-line no-await-in-loop
            const md = await mr.json().catch(() => ({}));
            if (!mr.ok) throw new Error(md.error || t('xPricing.messageFailed', { status: mr.status }));
            sent += 1;
          } catch (msgErr) { stopped = msgErr.message; break; }
        }
        photoNote += stopped
          ? ` · ${t('xPricing.messagesPartial', { sent, total: pendingMessages.length, error: stopped })}`
          : ` · ${t('xPricing.messagesSent', { n: sent })}`;
      }
      resetForm();
      setStatus(t('pricing.savedAs', { id: saved }) + photoNote);
      loadProjects();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  /* Submit / approve / reject / duplicate. One caller because the server decides WHICH level a
     button means from the quote's own status — a screen that worked that out for itself could offer
     the CEO's approval on a quote the manager has not seen. */
  const approvalAction = async (id, action, reason) => {
    setBusyId(id); setApprovalMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/projects/${id}/${action}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reason ? { reason } : {}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.actionFailed', { action, status: res.status }));
      setApprovalMsg({ ok: true, text: data.projectId
        ? t('pricing.revisedInto', { id: data.projectId })
        : t('pricing.nowStatus', { status: t(`pricing.st${String(data.status).replace(/\s/g, '')}`), to: data.to || '' }) });
      await loadProjects();
    } catch (err) {
      setApprovalMsg({ ok: false, text: err.message });
    } finally {
      setBusyId('');
    }
  };

  const [loadedGate, setLoadedGate] = useState(null);
  /* What the server says this person may decide. Declared HERE, above canDecideNow, because that is
     an IIFE evaluated on every render: with it further down the page threw "Cannot access before
     initialization" the moment a quote was loaded — `!projectId` had been short-circuiting the read
     until then, so an empty screen looked fine and opening a quote went blank (2026-09-11). */
  const [gateMe, setGateMe] = useState(null);
  useEffect(() => {
    if (!PRICING_APPROVAL_ON) return;
    fetch(`${BACKEND_URL}/api/pricing/approval-context`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setGateMe(d))
      .catch(() => { /* the button simply does not appear */ });
  }, []);
  /* Which view of this screen a person gets (Max, 2026-09-11). Read from the ACTIVE role when one
     is chosen in the header, and otherwise from the roles they hold — so an admin who also holds
     CEO keeps the full screen until they switch into it, and somebody whose only role is CEO gets
     the CEO view without having to choose. */
  const { roles: myRoles = [], activeRole, permissions: authPerms = {}, name: authName } = useAuth();
  /* Whether this person may CHANGE a quote. Read from useAuth rather than from a `me` union,
     because only useAuth follows the header's role switcher — the same reason Project Approval
     reads its Approve gate from there. It decides what the photo strip draws; the endpoints
     enforce it regardless. */
  const canEdit = authPerms.collab_pricing === 'edit';
  const actingAs = (key) => (activeRole ? activeRole === key
    : myRoles.length === 1 && myRoles[0] === key);
  const isCeo = actingAs('CEO');
  /* MAY THIS PERSON CHANGE THE SHAPE OF THE QUOTE — Jay, 2026-09-16, on a CEO-view screenshot:
     "Information NOT needed for CEO view. Make it cleaner."

     Two separate reasons to say no, and both belong in one flag because both mean "this screen is
     not yours to build in". A person without the edit grant obviously cannot; and the CEO, who DOES
     hold `collab_pricing` at edit, is here to decide on somebody else's work — Save changes, Cancel,
     Add SKU, Duplicate and Delete were five ways to alter a quote offered to the one person whose
     whole job is to accept or refuse it. The grant stays as it is: it is what lets them open the
     quote at all, and taking it away would close the screen rather than tidy it.

     Approve pricing is NOT gated on this. It is the CEO's one action, and after this it is the only
     button on the row. */
  const mayShape = canEdit && !isCeo;
  // A final approver arrives to read, not to type, so the inputs start folded for them. Once only —
  // a later toggle is theirs to keep.
  const foldedForCeo = useRef(false);
  useEffect(() => {
    if (foldedForCeo.current || !isCeo) return;
    foldedForCeo.current = true;
    setInputsOpen(false);
  }, [isCeo]);
  // The two people the gate asks: the owning team's manager, then the CEO.
  const isApprover = isCeo || actingAs('Sale manager')
    || (!activeRole && myRoles.includes('Sale manager'));
  /* The button appears only when this quote is actually waiting on this person. A green Approve on a
     draft, or on somebody else's stage, is a button that exists to be refused — the server decides
     the same thing again on the way in, and a screen offering what the server will reject teaches
     people to ignore the screen. */
  const canDecideNow = (() => {
    if (!PRICING_APPROVAL_ON || !projectId || !gateMe) return false;
    // The named first approver is asked whatever role they hold today.
    if (!isApprover && !gateMe.firstApprover?.isMe) return false;
    const st = loadedGate?.status;
    // The manager of the team the quote belongs to — a person can manage one department and sit in another.
    if (st === 'Pending manager') {
      if (gateMe.isAdmin) return true;
      // A named first approver decides this step for every team (2026-10-22, the server's rule).
      if (gateMe.firstApprover?.set) return !!gateMe.firstApprover.isMe;
      return loadedGate?.teamKey
        ? (gateMe.managerTeams || []).includes(loadedGate.teamKey) : !!gateMe.isTeamManager;
    }
    if (st === 'Pending CEO') return gateMe.isAdmin || gateMe.canApproveFinal;
    return false;
  })();

  /* The panel's own two writes — which card carries the extra, and the Sales OP memo. The same
     endpoints Pricing Approval posts to; the quote is reloaded after, because both stamp who and
     when and a screen that guesses those prints a name the row does not carry. */
  const padPost = async (path, body, okMsg) => {
    setBusy(true); setError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/projects/${projectId}/${path}`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.failed', { status: res.status }));
      setPadFlash(okMsg);
      await loadProject(projectId);
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const decide = async (action, note, skus2) => {
    setBusy(true); setError(''); setApprovalMsg(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/projects/${projectId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(action === 'reject'
          ? { reason: note }
          // The per-SKU verdicts ride with an approval, exactly as they do from Pricing Approval.
          : { ...(note ? { note } : {}), ...(skus2 && skus2.length ? { skus: skus2 } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.actionFailed', { action, status: res.status }));
      setApprovalMsg({ ok: true, text: t('pricing.nowStatus', {
        status: t(`pricing.st${String(data.status).replace(/\s/g, '')}`), to: data.to || '',
      }) });
      await loadProject(projectId);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  /* The inputs fold away (Max, 2026-09-11). Once a costing is entered the reading happens on the
     right, and on a laptop the two panels do not both fit — collapsing the left one gives Results
     the whole width. Open by default: a screen that starts folded hides the thing it is for. */
  const [inputsOpen, setInputsOpen] = useState(true);
  const [decisionFor, setDecisionFor] = useState(null);
  const [decisionNote, setDecisionNote] = useState('');
  /* THE SAME DECISION, WHEREVER IT IS TAKEN — Max, 2026-10-05: "khi click approve pricing trên
     phiếu collab pricing calculator thì hiện lại popup [cũ] nhưng trong pricing approve thì mới
     đồng nhất nè". This button opened the plain Agree / Reject dialog while Pricing Approval's
     opened the per-SKU one, so the same act looked like two different decisions depending on which
     screen the approver happened to be on.
     IT DECIDES ON THE SNAPSHOT, not on what is in the boxes. The verdicts are keyed on the
     submission they were taken against, and a quote waiting in the queue can still be corrected
     (2026-09-17) — so the live payload and the frozen one can differ, and sending positions from
     the live one would file an answer against a SKU nobody judged. */
  const [gateSnapshot, setGateSnapshot] = useState(null);
  const [decisionMarks, setDecisionMarks] = useState(null);
  /* The quote row the panel reads — the same shape the approval list hands it. Kept whole rather
     than rebuilt from the pieces this screen already holds: the panel prints the team, the status,
     who submitted it, the decision and the memo, and a hand-made copy of that is one field away
     from disagreeing with the other screen. */
  const [gateRow, setGateRow] = useState(null);
  const [padOpen, setPadOpen] = useState(false);
  const [padFlash, setPadFlash] = useState('');
  // Which SKUs the approver opened before deciding, handed over by the panel at that moment.
  const [padSeen, setPadSeen] = useState(null);

  /* The quote's SKUs as rows the shared dialog draws, and the read-back under them re-priced on
     the ones still in — both the same shape Pricing Approval builds, because it is the same
     decision. A one-SKU quote gets no per-SKU list: the SKU IS the quote. */
  const gateSkuRows = (seen) => {
    const list = gateSnapshot?.payload?.skus || [];
    if (list.length < 2) return null;
    const by = {};
    (gateSnapshot.decisions || []).forEach((d) => { by[d.sku_index] = d; });
    return list.map((sk, i) => ({
      projectId: `#${i + 1}`,
      index: i,
      itemName: sk.name || `SKU ${i + 1}`,
      approvalStatus: by[i]?.decision === 'rejected' ? 'Rejected' : 'Pending',
      decisionNote: by[i]?.reason || '',
      // `seen` is passed in, not read off state: the panel hands it over in the same handler that
      // builds these rows, and a state setter does not change the value that render is closed over.
      flag: (seen && !seen.has(i)) ? t('pricing.padSkuUnseen') : '',
    }));
  };

  const gateSummary = () => {
    const rows = gateSkuRows(padSeen);
    if (!rows) return null;
    const list = gateSnapshot?.payload?.skus || [];
    const out = new Set();
    rows.forEach((r) => { if (r.approvalStatus === 'Rejected') out.add(r.index); });
    (decisionMarks || []).forEach((d) => {
      if (d.decision === 'Rejected') out.add(d.index); else out.delete(d.index);
    });
    const live = list.filter((_, i) => !out.has(i));
    if (!live.length) return <p className="pa-sum-none">{t('pricing.padSumNothing')}</p>;
    const { cards } = cpCards(list, gateSnapshot.rules || rules, t, {}, live);
    const picked = cards.find((x) => x.key === loadedGate?.extraOption) || null;
    return (
      <>
        <b className="pa-sum-head">{t('pricing.padSumHead', { n: live.length, all: list.length })}</b>
        {picked ? (
          <ul className="pa-sum-list">
            <li>
              <span>{t('pricing.padSumCard')}</span>
              <b>{picked.name.replace(/^[A-Z] \u00b7 /, '')}</b>
            </li>
            {!picked.hand && (
              <li>
                <span>{t('pricing.padNetProfit')}</span>
                <b className={picked.net < 0 ? 'bad' : 'good'}>{money(picked.net)}</b>
              </li>
            )}
          </ul>
        ) : <p className="pa-sum-nocard">{t('pricing.padSumNoCard')}</p>}
      </>
    );
  };

  const fileRef = useRef(null);
  /* WHAT THE VENDOR'S IN-LINE SHEET SAID, held until somebody picks a row from it — Max,
     2026-09-22. The file prices every colorway of the style, so a reader that applied the first
     row it found would quietly benchmark the collab against the wrong one. Nothing reaches the
     boxes until a line has been chosen. */
  const [benchPick, setBenchPick] = useState(null);
  /* THE ANSWER TO THE QUESTION THAT IS STILL BEING ASKED. Opening the dialog fires a search for ''
     and the first keystroke fires another; if the empty one comes back second it repaints the list
     with everything, under a box that says "10007R2". Measured, not theorised — a check asking how
     many lines the picker offered for one SKU got the unfiltered count twice. Each request takes a
     number and only the latest may write. */
  const benchSeq = useRef(0);
  // What the last BOM read produced — the notes are as much the point as the numbers.
  const [bomRead, setBomRead] = useState(null);

  /* THE BENCHMARK IS FILLED FROM THE LIBRARY, and only from the library — Max, 2026-09-22:
     "import tại màn hình này luôn chứ ko phải từ Collab Pricing Calculator". A vendor workbook is
     filed on Inline Cost Sheets; this screen picks a line that is already there. One door in and
     one door out, instead of the calculator doing the filing as a side effect of a read, which is
     not where anybody would look for it afterwards.

     It searches LINES and not sheets, because the person is quoting Chair One and has no idea
     which workbook it arrived in. */
  const searchBenchLibrary = async (q) => {
    const seq = (benchSeq.current += 1);
    /* The box has to answer the keystroke immediately; the rows follow when they arrive. `rows`
       and `refused` are seeded here because the dialog renders off this state the moment `open`
       is true, and on the FIRST open there is no previous object to spread — it read `.length` of
       undefined and the dialog never appeared. */
    setBenchPick((p) => ({
      ...(p || {}), open: true, from: 'library', q,
      rows: p?.rows || [], refused: p?.refused || [], row: p?.row || null, variant: p?.variant || '',
    }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/pricing/bench-rows?q=${encodeURIComponent(q || '')}`,
        { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.searchLibraryFailed'));
      if (seq !== benchSeq.current) return;   // a newer search has been asked; this answer is stale
      const rows = data.rows || [];
      setBenchPick((p) => ({
        ...(p || {}),
        open: true,
        from: 'library',
        q,
        rows,
        refused: [],
        /* Keep the chosen line if the new answer still holds it — a person narrowing a search has
           not changed their mind about the row they already picked. */
        row: rows.find((r) => p?.row && r.sheetId === p.row.sheetId && r.row === p.row.row
          && r.sku === p.row.sku) || rows[0] || null,
        variant: rows[0] ? Object.keys(rows[0].variants)[0] : '',
      }));
    } catch (e) {
      if (seq === benchSeq.current) setError(e.message);
    }
  };

  /* The four boxes, and a line saying where they came from. The source is stored ON THE QUOTE
     (`bench_src`) rather than shown once and forgotten: an approver reading a benchmark figure
     months later has no other way of knowing which vendor file, which colorway and which of the
     tag variants it was taken from. */
  /* MONEY TO THE CENT — Jay, 2026-09-23: "decimal places are inconsistent (12.4837 next to
     two-decimal figures)". Both importers run values through this on the way IN rather than
     rounding in the box, so what is stored is what is shown; a figure already saved is left alone,
     because silently moving somebody's number is worse than an odd-looking one. */
  const applyBenchmark = () => {
    if (!benchPick) return;
    const { row, variant } = benchPick;
    if (!row) return;
    const val = row.variants[variant];
    if (!val) return;
    // Every row comes out of the library now, so it carries the name of its own workbook.
    const fileName = row.fileName || '';
    /* TO THE CENT — Jay, 2026-09-23: "decimal places are inconsistent (12.4837 next to
       two-decimal figures)". A vendor sheet carries four, and four next to two on one row reads as
       two different kinds of number rather than one kind measured twice. Money on this screen is
       dollars and cents everywhere else, so it arrives that way; the tenth of a cent it drops is
       $0.04 across a 100 EA order and is not a figure anybody quotes. Rounded ON THE WAY IN rather
       than in the box, so what is stored is what is shown. */
    setVal('bench_skin', toCents(val.skin));
    setVal('bench_cmt', toCents(val.cmt));
    setVal('bench_ohp', toCents(val.ohp));
    setVal('bench_frame', toCents(val.frame));
    setVal('bench_src', `${fileName} · ${row.label} · ${val.label}`);
    setBenchPick(null);
  };


  /* ?open=CP-000011 — how the approval screen hands a quote over. Without this the Edit button
     landed on an empty calculator: it navigated correctly and loaded nothing (Max, 2026-09-11).
     The parameter is cleared once used, so a refresh cannot reload it over unsaved work. */
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current) return;
    const want = new URLSearchParams(window.location.search).get('open');
    if (!want) return;
    opened.current = true;
    window.history.replaceState({}, '', window.location.pathname);
    loadProject(want);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  /* Read a filled-in template into the FORM, not into the database. The server hands back inputs
     only; computeSku turns them into numbers here, so there is one implementation of the
     arithmetic and the file cannot disagree with the screen. Nothing is stored until Save. */
  const importFile = async (file) => {
    setBusy(true); setError(''); setStatus('');
    try {
      const dataBase64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onerror = () => reject(new Error(t('xPricing.readFileFailed')));
        fr.onload = () => resolve(String(fr.result).split(',')[1] || '');
        fr.readAsDataURL(file);
      });
      const res = await fetch(`${BACKEND_URL}/api/pricing/import-xlsx`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileBase64: dataBase64, fileName: file.name }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('xPricing.importFailed', { status: res.status }));
      /* HEVN Costing's Price Approval workbook with the factory's CBD sheets (Max, 2026-10-29) — the
         whole quote on the CBD method. Money to the cent on the way in, as every importer here does;
         the server already made the vendor's figure come out as the sheet rounds it. */
      if (data.kind === 'price-approval') {
        const loadedPa = (data.skus || []).map((row) => {
          const blank = BLANK_SKU(rules, 'cbd');
          const values = { ...blank.values, ...(row.values || {}) };
          MONEY_KEYS.forEach((k) => { if (values[k] !== undefined) values[k] = toCents(values[k]); });
          return { ...blank, name: row.name || blank.name, auto: true, overrides: { ...blank.overrides, ...(row.overrides || {}) }, values };
        });
        if (!loadedPa.length) throw new Error(t('xPricing.noRowsInFile'));
        setMode('general');
        setShowSaved(false);
        if (!projectName.trim() && data.project) setProjectName(data.project);
        setSkus(loadedPa);
        setCur(0);
        const sheets = (data.skus || []).map((row) => (row.cbdSheet ? `${row.name} ← '${row.cbdSheet}'` : row.name)).join(' · ');
        setStatus(t('xPricing.paImported', { n: loadedPa.length, sheets })
          + ((data.warnings || []).length ? ` — ${data.warnings.join(' ')}` : ''));
        return;
      }
      const loaded = (data.skus || []).map((row) => {
        const blank = BLANK_SKU(rules, methodNow);
        return {
          ...blank,
          name: row.name || blank.name,
          // Auto stays on: a value in one of the five auto columns arrives as a per-field override,
          // which is what typing into that box on the screen does.
          auto: true,
          overrides: row.overrides || {},
          values: seedTierParts({ ...blank.values, ...(row.values || {}) }, rules),
        };
      });
      if (!loaded.length) throw new Error(t('xPricing.noRowsInFile'));
      setMode('general');
      setShowSaved(false);
      setSkus(loaded);
      setCur(0);
      const warn = (data.warnings || []).length;
      setStatus(t('pricing.imported', { n: loaded.length })
        + (warn ? ` — ${(data.warnings || []).join(' ')}` : ''));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const loadProject = async (id) => {
    setBusy(true); setError('');
    try {
      const r = await fetch(`${API}/projects/${id}`, { credentials: 'include' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || t('xPricing.loadFailed', { status: r.status }));
      const p = d.project;
      // The rules as at this quote's creation, sent with it by the endpoint.
      setRules(d.rules || currentRules || null);
      /* Where this quote is in the gate, and the words the submitter sent with it. The approval mail
         carries a "Note from the submitter" row, and clicking through to the quote showed no such
         thing anywhere (Max, 2026-09-11: "ko thấy trường này trong phiếu á") — the note lived only on
         the approval row and in the mail. */
      setFolder(p.folder || '');
      setGateSnapshot(d.snapshot ? { ...d.snapshot, decisions: d.skuDecisions || [] } : null);
      setGateRow({ ...p, skuDecisions: d.skuDecisions || [] });
      setLoadedGate(p.approval_status && p.approval_status !== 'Draft' ? {
        status: p.approval_status,
        submittedBy: p.submitted_by || '',
        note: p.submit_note || '',
        rejectReason: p.reject_reason || '',
        /* WHICH CARD THE APPROVER PICKED, so the row at the foot lights the one that was chosen.
           The requester is the person who finalises the quote from that decision, and until now
           they had to open Pricing Approval to find out which card it landed on. */
        extraOption: p.extra_option || '',
        teamKey: p.team_key || '',
      } : null);
      setProjectId(p.project_id);
      setProjectName(p.name);
      setMode(p.mode);
      if (p.mode === 'general') {
        /* a draft (or a quote sent back) goes through the new flow when it is next submitted, so it is priced that way now */
        const open = ['Draft', 'Rejected'].includes(p.approval_status || 'Draft');
        setSkus(p.payload?.skus?.length
          ? p.payload.skus.map((sk) => ({
            ...sk,
            values: centsInValues(seedTierParts({
              ...(sk.values || {}), ...(open ? calcFlag(sk.values?.pricingMethod) : {}),
            }, d.rules || currentRules)),
          }))
          : [BLANK_SKU(d.rules || currentRules)]);
        setCur(0);
      } else {
        setGlobals(p.payload?.globals || globals);
        setEntities(p.payload?.entities?.length ? p.payload.entities : DEFAULT_ENTITIES());
      }
      setShowSaved(false);
      /* "Save will update it, then clear the screen" is an instruction about a button this person
         does not have, so for them it is a sentence to read and discard — the line Jay highlighted.
         Not set rather than hidden: the status line still carries real feedback, and blanking the
         whole thing would take the approval message with it. */
      setStatus(mayShape ? t('pricing.loaded', { id: p.project_id }) : '');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const removeProject = async (id) => {
    setConfirmDel('');
    try {
      const r = await fetch(`${API}/projects/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!r.ok) throw new Error(t('xPricing.deleteFailed', { status: r.status }));
      loadProjects();
    } catch (err) { setError(err.message); }
  };


  /* ---- render ---------------------------------------------------------------------------- */
  /* Same shape as `money`, plus the AUTO tag and the reset the excluded items have: the fee is
     $0.04 on a furnished-free frame and blank otherwise, and either can be typed over. */
  const fmFeeField = () => (
    <label className="cp-field" key="fmfee">
      <span>
        {t('pricing.fmfee')}
        {fmAuto && <b className="cp-tag">{t('pricing.autoTag')}</b>}
        {sku.overrides.fmfee && (
          <button type="button" className="cp-undo" onClick={() => clearOverride('fmfee')}>
            {t('pricing.resetOverride')}
          </button>
        )}
      </span>
      <em>{t('pricing.fmfeeHint')}</em>
      <div className="cp-inputrow">
        <div className="cp-money">
          <i>$</i>
          <input
            type="number"
            step="0.01"
            className={fmAuto ? 'cp-autoval' : ''}
            value={fmAuto ? calc.fmfee.toFixed(2) : (v.fmfee ?? '')}
            onChange={(e) => setExcluded('fmfee', e.target.value)}
          />
        </div>
      </div>
    </label>
  );

  const money = (k, on, label, hint, before, badge) => (
    <label className={`cp-field ${before ? 'cp-wide' : ''}`} key={k}>
      <span>{label}{badge}</span>
      {/* An EMPTY hint is not rendered at all — Max, 2026-09-18: "khoảng cách bị rộng quá". The blank
          <em> used to be deliberate, holding two lines so a one-line hint beside a two-line one did
          not step the row; the fields are bottom-aligned now, so the placeholder only left a gap
          under labels that had nothing to say. */}
      {!!hint && <em>{hint}</em>}
      <div className="cp-inputrow">
        {before}
        <div className="cp-money">
          <i>$</i>
          <input
            type="number"
            step="0.01"
            value={v[k] ?? ''}
            onChange={(e) => on(k, e.target.value)}
          />
        </div>
      </div>
    </label>
  );

  /* A difference, with the sign OUTSIDE the currency: fmtUSD(-1) is "$-1.00", which reads as a
     dollar sign followed by a negative number rather than as one dollar less. */
  const signed = (d) => (d === 0 ? fmtUSD(0) : `${d < 0 ? '−' : '+'}${fmtUSD(Math.abs(d))}`);

  /* WHAT THE INLINE COLUMN IS MEASURED AGAINST, line by line. The figure on this side is the
     inline product's; the arrow says which way THIS QUOTE sits against it — up in red when the
     collab costs more, down in green when it costs less. The amount rides with the arrow because
     an arrow alone is unreadable in greyscale and says nothing about how much. */
  const arrowOf = (diff) => {
    if (diff === null || diff === undefined) return <span className="cp-arrow same">—</span>;
    if (Math.abs(diff) < 0.005) return <span className="cp-arrow same">— {t('pricing.benchSame')}</span>;
    return (
      <span className={`cp-arrow ${diff > 0 ? 'up' : 'down'}`}>
        {diff > 0 ? '▲' : '▼'} {signed(diff)}
      </span>
    );
  };
  /* The five lines the comparison is made of, each with what this quote pays and what the inline
     one does. ONE list, read by the boxes, by the arrows and by the reasons underneath — three
     copies of it is how a screen comes to disagree with itself. */
  const benchExtraPerUnit = calc.qty ? (calc.byBasis.base.debit / calc.qty) : 0;
  const BENCH_LINES = [
    ...FACTORY_FIELDS.map((f) => ({ key: f.key, label: t(`pricing.${f.tk}`) })),
    { key: 'frame', label: t('pricing.frame') },
    { key: 'extra', label: t('pricing.extraLine'), mine: benchExtraPerUnit, derived: true },
  ];
  const benchMine = (l) => (l.mine !== undefined ? l.mine : (Number(v[l.key]) || 0));
  const benchTheirs = (l) => {
    const typed = String(v[`bench_${l.key}`] ?? '').trim();
    return typed === '' ? null : Number(typed);
  };
  const benchDiff = (l) => {
    const theirs = benchTheirs(l);
    return theirs === null ? null : benchMine(l) - theirs;
  };
  /* The inline product's factory cost — its three lines added. Read by the block's own total, by
     the gap row under the pair and by the simulator's base, so it is computed once: three copies
     of a sum is how a screen comes to print two different answers to one question. */
  const benchFactory = FACTORY_FIELDS.reduce((a, f) => a + (Number(v[`bench_${f.key}`]) || 0), 0);
  const benchTotalDiff = BENCH_LINES.reduce((a, l) => a + (benchDiff(l) || 0), 0);
  const benchAnswered = BENCH_LINES.some((l) => benchTheirs(l) !== null);

  /* THE GAP, EXPLAINED, SPLIT ACROSS THE BLOCKS IT BELONGS TO — Jay, 2026-09-23: "Extra Cost 2
     is not an extra cost. It is the breakdown of the factory cost gap, so it belongs beside the
     two factory cost cards in block 2. The frame cost difference goes under block 3."

     He is right and it was never an extra cost: nothing on those lines is money anybody pays. It
     was sitting in block 4 because that is where the second column happened to be free, and it
     broke the comparison rhythm the frames above it had just established.

     ONE renderer, called twice, rather than two copies of the table: the factory lines under block
     2, the frame line under block 3. `only` is the subset; everything else — which lines show,
     what an empty one says, the free lines somebody adds — is the behaviour it already had. */
  const gapTable = (only, { addable = false } = {}) => {
    const lines = BENCH_LINES.filter((l) => only.includes(l.key));
    const shown = lines.filter((l) => {
      const d = benchDiff(l);
      return (d !== null && Math.abs(d) >= 0.005) || String((v.benchWhy || {})[l.key] ?? '').trim() !== '';
    });
    const extra = addable ? (v.benchWhyMore || []) : [];
    return (
      <div className="cp-whys">
        {(shown.length > 0 || extra.length > 0) && (
          <div className="cp-why-head">
            <span>{t('pricing.benchLineCol')}</span>
            <span className="d">{t('pricing.benchDiff')}</span>
            <span>{t('pricing.benchWhyCol')}</span>
          </div>
        )}
        {/* ONLY THE LINES THAT DIFFER — Max, 2026-09-22: "chỉ hiện những cái nào lệch thui".
            A line stays while somebody has written a reason against it, even once the gap has
            closed: that sentence is theirs and the screen must not eat it because a number moved. */}
        {shown.map((l) => {
          const d = benchDiff(l);
          const none = d === null || Math.abs(d) < 0.005;
          return (
            <div className={`cp-why ${none ? 'muted' : ''}`} key={l.key}>
              <div className="cp-money"><input value={l.label} readOnly /></div>
              {arrowOf(d)}
              <div className="cp-money">
                <input
                  value={(v.benchWhy || {})[l.key] ?? ''}
                  placeholder={none ? t('pricing.benchNoGap') : t('pricing.benchWhyPh')}
                  onChange={(e) => setVal('benchWhy', { ...(v.benchWhy || {}), [l.key]: e.target.value })}
                />
              </div>
            </div>
          );
        })}
        {extra.map((row, i) => (
          <div className="cp-why" key={`more-${i}`}>
            <div className="cp-money">
              <input
                value={row.title ?? ''}
                placeholder={t('pricing.benchElsePh')}
                onChange={(e) => setVal('benchWhyMore', (v.benchWhyMore || []).map((r, j) => (j === i ? { ...r, title: e.target.value } : r)))}
              />
            </div>
            <div className="cp-money cp-whyamt">
              <i>$</i>
              <input
                type="number"
                step="0.01"
                value={row.amt ?? ''}
                onChange={(e) => setVal('benchWhyMore', (v.benchWhyMore || []).map((r, j) => (j === i ? { ...r, amt: e.target.value } : r)))}
              />
            </div>
            <div className="cp-money">
              <input
                value={row.note ?? ''}
                placeholder={t('pricing.benchWhyPh')}
                onChange={(e) => setVal('benchWhyMore', (v.benchWhyMore || []).map((r, j) => (j === i ? { ...r, note: e.target.value } : r)))}
              />
            </div>
            <button
              type="button"
              className="cp-linex"
              title={t('pricing.removeLine')}
              onClick={() => setVal('benchWhyMore', (v.benchWhyMore || []).filter((_, j) => j !== i))}
            >✕</button>
          </div>
        ))}
        {/* An empty table under a heading reads as a screen that failed to load; this says which
            it is instead. */}
        {!shown.length && !extra.length && (
          <p className="cp-whynone">
            {benchAnswered ? t('pricing.benchAllLevel') : t('pricing.benchNotFilled')}
          </p>
        )}
        {addable && (
          <button
            type="button"
            className="sp-btn sp-btn-ghost cp-addline"
            onClick={() => setVal('benchWhyMore', [...(v.benchWhyMore || []), { title: '', amt: '', note: '' }])}
          >
            <Plus size={12} /> {t('pricing.addLine')}
          </button>
        )}
      </div>
    );
  };

  /* WHAT AN OPTION DEPARTS FROM THE QUOTE IN, in words. ONE implementation, read by the option
     card and by the column head above the comparison table: two of them is how a screen comes to
     describe one option two ways, which is the fault this file keeps having to fix elsewhere.

     A lever that has been picked but left empty is NOT a departure — it is a question nobody has
     answered yet — and neither is one whose value matches the quote's. */
  /* Both now live in cpSummary as pure functions, so the approver's screen describes an option
     the same way this one does. These two wrappers keep the call sites unchanged: the quote's own
     values and this SKU's items are what the closures used to carry. */
  const optDepartures = (o) => cpOptDepartures(v, o);
  const optDepartureText = (o) => cpOptDepartureText(v, o, t, calc.items);


  /* HOW "AS QUOTED" WAS BUILT — Jay, 2026-09-23: "I can't see how As Quoted was built. It should
     show what's included and what's excluded, the way the options do."

     An option's line says what it CHANGED, which only works because there is something to change
     it from — and that something said "the figures entered above", which names nothing. This says
     the same KIND of thing about the quote itself: the conditions it stands on, read from the very
     fields an option is allowed to depart from, so the two lines are comparable by construction.

     Then the split Jay asked for in as many words: what the margin is taken ON, and what is
     recovered outside it. Those are the two halves of the buying cost and the reason the tier
     ratios look the way they do. */
  /* WHAT THIS CARD CHARGES — Max, 2026-09-24: "cái as quoted tôi hiểu là cho xem những cái được
     chọn á chứ ko phải rõ cái tính". The first build answered Jay's "show what's included and
     what's excluded" as an arithmetic breakdown — two columns of figures and which totals they sat
     in. That is the working, and the working already has its own `i` on every figure.

     What he means, and what the options already do, is the SELECTION: which extra-cost lines this
     card charges and which it does not. One list, the same on the quote and on every option, so
     the three cards can be read across — the quote charges all of them, an option is where one
     goes quiet, and the difference is visible without opening anything. */
  /* EVERY FIGURE ON A CARD SAYS HOW IT GOT THERE — Max, 2026-09-24: "các số trên mỗi option nên có
     chữ i nha". The Results table below has carried this per cell since 2026-09-22; the cards are
     what people read first and had none.

     Each one is computed from the CARD'S OWN calc, which is the whole point: an option on another
     ratio or with a line dropped has different arithmetic, and a hint that quoted the quote's
     numbers under an option's figure would be worse than no hint. */
  const cardFx = {
    t1: (c) => {
      if (c.t0 == null) return null;
      const sp = c.tierSplit.t1;
      return fx(t('pricing.t0'), `${t('pricing.t1BaseLine')} + ${t('pricing.fxOh')} + ${t('pricing.fxProfit')}`,
        [`${n2(c.t1On)} + ${n2(sp.oh * c.t1On)} + ${n2(sp.profit * c.t1On)}`], fmtUSD(c.t0), 'right');
    },
    t2: (c) => {
      if (c.t1 == null) return null;
      const r = c.tierSplit.t2.r * 100;
      const on = c.marginBasis === 'buying' ? c.buyingCost : c.markupBase;
      return fx(t('pricing.t1'), t('pricing.fxT2Rule'),
        [`${n2(on)} ÷ (1 − ${Math.round(r * 100) / 100}%)`], fmtUSD(c.t1), 'right');
    },
    msrp: (c) => fx(t('pricing.msrp'), t('pricing.targetHint'),
      [`${n2(Number(c.inlineRrp) || 0)} × (1 + ${Number(c.premium) || 0}%)`], fmtUSD(c.msrp), 'right'),
    /* WHICH TIER THIS PROFIT IS AT, said in the label — Max, 2026-10-01: "Profit on the order"
       sat over a figure that does not move when an option changes who carries the extra, because
       it is the profit at T1 and the split does not reach T1. It is not always T1 either: a quote
       with no T1 ratio falls back to T2, so one name over two tiers was the same fault this file
       keeps fixing. The label names the tier the figure is actually on. */
    profit: (c, per, tier) => fx(t(`pricing.optOrderProfit${tier}`),
      `${t('pricing.unit')} × ${t('pricing.qtyWord')}`,
      [`${n2(per)} × ${(c.qty || 0).toLocaleString('en-GB')}`], fmtUSD(per * c.qty), 'right'),
  };

  /* THE QUOTE, SHOWN IN THE OPTIONS EDITOR — Max, 2026-09-24: "as quoted cũng có nút Conditions để
     check với bên Conditions, nhưng có điều ko dc edit thui". An option's card says what it changed
     FROM the quote, and the only place the quote's own answers were written down was that same
     sentence — so checking one against the other meant holding two lines of prose side by side.

     It is the quote read into the same panel, every control disabled. Not a second panel: two
     renderings of one set of controls is how the read-only one comes to show a field the editable
     one has dropped. `optEdit === 'quote'` is the sentinel; everything else keys off it. */
  const QUOTE_EDIT = 'quote';
  const quoteAsOption = () => ({
    name: t('pricing.optAsQuoted'),
    on: Object.fromEntries(OPT_CONDITIONS.map((c) => [c.key, true])),
    values: Object.fromEntries(OPT_CONDITIONS.map((c) => [
      c.key,
      c.kind === 'items' ? [] : (c.kind === 'basis' ? (v.marginBasis || 'base') : String(v[c.key] ?? '')),
    ])),
  });

  const cardLines = (c) => {
    const label = {
      fprofit: t('pricing.fprofit'), handling: t('pricing.handling'), transport: t('pricing.transport'),
      moq: t('pricing.moqCharge'), addMat: t('pricing.addMat'), ffcmt: t('pricing.ffcmt'),
      shortLead: t('pricing.shortLead'),
    };
    const off = Array.isArray(c.extraOff) ? c.extraOff : [];
    /* An item dropped by an option is ZERO in that calc, so its amount has to come from the
       quote's own figures — otherwise the line people are being shown reads $0.00 and says
       nothing about what was given up. */
    return EXTRA_ITEM_KEYS
      .filter((k) => calc.items[k] || off.includes(k))
      .map((k) => ({ k, label: label[k], amt: calc.items[k], on: !off.includes(k) }));
  };

  const quotedBuiltText = () => {
    const stood = OPT_CONDITIONS.map((x) => {
      if (x.kind === 'items') return null;     // the quote drops nothing; only an option can
    const val = x.kind === 'basis' ? (v[x.key] || 'base') : String(v[x.key] ?? '');
      if (val === '') return null;
      if (x.kind === 'basis') {
        return `${t('pricing.optBasis')} ${val === 'buying' ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase')}`;
      }
      const shown = `${x.unit === '$' ? '$' : ''}${x.unit === 'EA' ? (Number(val) || 0).toLocaleString('en-GB') : val}${x.unit === '%' ? '%' : (x.unit === 'EA' ? ' EA' : '')}`;
      return `${t(`pricing.${x.tk}`)} ${shown}`;
    }).filter(Boolean);
    return [
      `${t('pricing.quotedIn')} ${fmtUSD(calc.markupBase)}`,
      `${t('pricing.quotedOut')} ${fmtUSD(calc.excluded)}`,
      ...stood,
    ].join(' · ');
  };

  /* THE WORKING OF ONE CELL — Max, 2026-09-22: "cho chữ i bên các cột option tính toán nhá".
     Per COLUMN, not per row: an option can be on another convention or another ratio, so the row's
     name is the only thing its columns still share and the arithmetic has to be asked of each one.

     Everything here reads the priced quote that produced the figure beside it. Nothing is worked
     out a second time. */
  const fxCell = (r, col) => {
    const c = col.calc;
    const vals = col.vals || v;
    const side = c.byBasis[c.marginBasis === 'buying' ? 'buying' : 'base'];
    const qty = c.qty || 0;
    const q = qty.toLocaleString('en-GB');
    const tier = (k) => (k === 't1' ? { oh: side.oh1, pf: side.pf1, price: side.t1, p: side.p1, sp: c.tierSplit.t1 }
      : { oh: side.oh2, pf: side.pf2, price: side.t2, p: side.p2, sp: c.tierSplit.t2 });
    const ohPct = (k) => pctOf(tier(k).sp.oh * 100);
    const pfPct = (k) => pctOf(tier(k).sp.profit * 100);
    /* THE TWO TIERS ARE NOT BUILT THE SAME WAY, and one hint for both said something false about
       each — Max, 2026-10-06, on the T2 row: the panel read "Helinox O.H + Helinox profit + Extra
       cost 1 / 12.17 + 24.35 + 3.47" over a figure of $81.16, and those three add to 39.99.

       Two faults in one line. It left out the BASIS, which is most of the price; and it named the
       extra cost, which is in neither tier's figure — T1 already carries it inside the buying cost
       and T2's own split sets `extra: 0`.

       So each tier now states what pricingModel actually does to it. T1 is the build-up Max drew,
       `t1On + oh + pf`, where t1On is the total buying cost. T2 is the DIVISION he settled on
       2026-09-24 ("nó vẫn theo công thức cũ là Total buying cost ÷ (1 − T2 ratio)") — the same
       wording the card hint and the tier table already use, so the three places that explain T2
       now explain it the same way instead of two agreeing and one not. */
    const tierWork = (k) => {
      const x = tier(k);
      if (x.price == null) return null;
      if (k === 't1') {
        return fx(r.label,
          `${t('pricing.t1BaseLine')} + ${t('pricing.fxOh')} + ${t('pricing.fxProfit')}`,
          [`${n2(c.buyingCost)} + ${n2(x.oh)} + ${n2(x.pf)}`], fmtUSD(x.price), 'right');
      }
      const rr = Math.round(x.sp.r * 10000) / 100;
      return fx(r.label, t('pricing.fxT2Rule'),
        [`${n2(side.on)} ÷ (1 − ${rr}%)`,
         `= ${n2(side.on)} ÷ ${(1 - x.sp.r).toFixed(2)}`], fmtUSD(x.price), 'right');
    };
    switch (r.key) {
      case 'on':
        return fx(r.label,
          c.marginBasis === 'buying' ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase'),
          c.marginBasis === 'buying'
            ? [`${t('pricing.markupBase')} ${n2(c.markupBase)} + ${t('pricing.fxExtra')} ${n2(c.excluded)}`]
            : [`${t('pricing.fxFactory')} ${n2(c.factoryCost)} + ${t('pricing.fxFrame')} ${n2(c.frameCost)}`],
          fmtUSD(side.on), 'right');
      case 't1': return tierWork('t1');
      case 't2': return tierWork('t2');
      case 'r1': case 'r2': {
        const x = tier(r.key === 'r1' ? 't1' : 't2');
        if (x.price == null) return null;
        return fx(r.label, t('pricing.fxTimesQty'), [`${n2(x.price)} × ${q}`], fmtUSD(x.price * qty), 'right');
      }
      case 'debit':
        return fx(r.label, t('pricing.fxDebitRule'),
          /* WHERE THE $25.91 COMES FROM — Max, 2026-09-24: "số 25.91 này ở đâu có vậy". The hint
             named the figure and not the lines behind it, which is exactly the question a total
             invites. They are listed, and only the ones THIS card charges: an option that dropped
             the MOQ surcharge must not be shown a debit built from it. */
          c.marginBasis === 'buying' ? [t('pricing.fxDebitInPrice')] : [
            ...EXTRA_ITEM_KEYS.filter((k) => c.items[k]).map((k) => `${t(`pricing.${k === 'moq' ? 'moqCharge' : k}`)} ${n2(c.items[k])}`),
            `= ${n2(c.excluded)} × ${q}`,
          ],
          fmtUSD(side.debit), 'right');
      case 'tp1': case 'tp2': {
        const x = tier(r.key === 'tp1' ? 't1' : 't2');
        if (x.p == null) return null;
        return fx(r.label, t('pricing.fxTimesQty'),
          [`(${n2(x.oh)} + ${n2(x.pf)}) × ${q}`], fmtUSD(x.p * qty), 'right');
      }
      case 'p1': case 'p2': {
        const x = tier(r.key === 'p1' ? 't1' : 't2');
        if (x.p == null) return null;
        return fx(r.label, `${t('pricing.fxOh')} + ${t('pricing.fxProfit')}`,
          [`${n2(x.oh)} + ${n2(x.pf)}`], fmtUSD(x.p), 'right');
      }
      case 'p1oh': case 'p2oh': {
        const k = r.key === 'p1oh' ? 't1' : 't2';
        const x = tier(k);
        if (x.oh == null) return null;
        return fx(r.label, `${ohPct(k)} × (${t('pricing.marginTakenOn')}) + ${t('pricing.fxExtra')}`,
          [`${ohPct(k)} × ${n2(side.on)} + ${n2(c.excluded)}`], fmtUSD(x.oh), 'right');
      }
      case 'p1pf': case 'p2pf': {
        const k = r.key === 'p1pf' ? 't1' : 't2';
        const x = tier(k);
        if (x.pf == null) return null;
        return fx(r.label, `${pfPct(k)} × ${t('pricing.fxOh')}`, [`${pfPct(k)} × ${n2(x.oh)}`], fmtUSD(x.pf), 'right');
      }
      case 'msrp':
        return fx(r.label, t('pricing.msrpSub'),
          [`${n2(vals.inlineRrp)} × (1 + ${pctOf(Number(vals.premium) || 0)})`], fmtUSD(c.msrp), 'right');
      case 'partner':
        return fx(r.label, t('pricing.partnerPriceSub'),
          [`${n2(c.msrp)} × ${pctOf(ruleSet(rules).partner_ratio_pct)}`], fmtUSD(c.partnerPrice), 'right');
      default: return null;
    }
  };

  /* THE COLUMNS OF THE COMPARISON TABLE: the quote as it stands, then one per option. Each is a
     fully priced quote, so every row reads the same way down every column and adding an option
     adds a column with nothing else to change. The option's own departures are printed under its
     name — the same list the option card carries, so the two cannot describe it differently. */
  const caseCols = useMemo(() => [
      {
        key: 'quoted',
        title: t('pricing.optAsQuoted'),
        // The convention this quote is priced on. It has no control of its own any more, so the
        // column that carries it has to say which one it is.
        sub: onBuying ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase'),
        calc,
        vals: v,
      },
      ...options.map((o, i) => {
        return {
          key: `opt${i}`,
          title: (o.name || '').trim() || t('pricing.optionN', { n: i + 1 }),
          sub: optDepartureText(o) || t('pricing.optSameShort'),
          calc: computeSku(withOption(sku, o), rules),
          // The values this column was priced with, so its cells can show the rates behind them.
          vals: withOption(sku, o).values,
        };
      }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [calc, options, sku, v, t]);

  /* THE WORKING BEHIND EACH FIGURE — Max, 2026-09-22. Every one of these is built from the SAME
     values the model priced with, never from a second calculation: a tooltip that works the number
     out again is a second implementation of the rule, and the day the two disagree the tooltip is
     the one people will believe.

     `align="right"` wherever the figure itself is right-aligned, or the panel runs off the card. */
  const n2 = (x) => Number(x || 0).toFixed(2);
  const fx = (title, rule, work, result, align) => (
    <FormulaHint title={title} rule={rule} work={work.filter(Boolean)} result={result} align={align} />
  );
  const pctOf = (x) => `${Number(x || 0)}%`;

  const fxFactory = () => fx(
    t('pricing.fxFactory'),
    `${t('pricing.skin')} + ${t('pricing.cmt')} + ${t('pricing.ohp')}`,
    [`${n2(v.skin)} + ${n2(v.cmt)} + ${n2(v.ohp)}`],
    fmtUSD(calc.factoryCost), 'right',
  );

  /* THE TWO BLOCK-5 HEADS — Max, 2026-09-24: "thiếu chữ i ở 2 chỗ này". Every other figure on this
     screen can be asked where it came from, and these two are the ones the whole simulator is
     priced off, so they were the worst pair to leave unexplained.

     The wording is NOT Jay's "base + extra = the amount the ratios are applied to", which reads
     well and is not what happens: the ratios are taken on the BASE, and the extra cost is added
     afterwards without being marked up. The sum of the two is the buying cost, which is a real
     figure this screen already knows — so the hint says that instead. */
  const fxBuying = () => fx(
    t('pricing.fxBuying'),
    t('pricing.fxBuyingRule'),
    [
      `${t('pricing.benchBase')} ${n2(calc.markupBase)}  (${t('pricing.fxFactory')} + ${t('pricing.fxFrame')})`,
      `${t('pricing.blkExtra')} ${n2(calc.excluded)}  (${t('pricing.fxExtraAfter')})`,
    ],
    fmtUSD(calc.markupBase + calc.excluded),
  );

  const fxBenchBase = () => fx(
    t('pricing.fxBenchBase'),
    t('pricing.fxBenchBaseRule'),
    [`${n2(benchFactory)} + ${n2(Number(v.bench_frame) || 0)}`],
    fmtUSD(benchFactory + (Number(v.bench_frame) || 0)),
    'right',
  );

  const fxFrame = () => fx(
    t('pricing.fxFrame'),
    `${t('pricing.frame')} + ${t('pricing.fmfee')}`,
    [`${n2(v.frame)} + ${n2(calc.fmfee)}`],
    fmtUSD(calc.frameCost), 'right',
  );

  const fxExtra = () => fx(
    t('pricing.fxExtra'),
    t('pricing.fxExtraRule'),
    Object.entries(calc.items)
      .filter(([, amt]) => Number(amt) !== 0)
      // The model's item keys and the label keys agree except for this one.
      .map(([k, amt]) => `${t(`pricing.${k === 'moq' ? 'moqCharge' : k}`)} ${n2(amt)}`),
    fmtUSD(calc.excluded), 'right',
  );

  /* The two auto surcharges, which are the ones people ask about: both are a RATE off a ladder
     applied to a base that is itself made of several boxes, so neither the rate nor the base is
     visible anywhere on the screen. */
  const fxMoq = () => fx(
    t('pricing.fxMoq'),
    t('pricing.fxMoqRule'),
    [
      `${t('pricing.fxMoqBase')} ${n2(calc.moqBaseAmount)}`,
      `x ${pctOf(calc.moqRate * 100)}  (${(calc.qty || 0).toLocaleString('en-GB')} EA)`,
    ],
    fmtUSD(calc.items.moq), 'right',
  );

  const fxSl = () => fx(
    t('pricing.fxSl'),
    t('pricing.fxSlRule'),
    [
      `${t('pricing.cmt')} ${n2(v.cmt)} x ${pctOf(calc.slRate * 100)}`,
      `${t('pricing.fxLead')} ${calc.lead === null ? '—' : t('xPricing.nDays', { n: calc.lead })}`,
    ],
    fmtUSD(calc.items.shortLead), 'right',
  );

  /* THE TIER'S FOUR LINES, each showing the arithmetic Max stated on 2026-09-22. `r` is what the
     row was priced from, so the panel and the figure beside it cannot drift. */
  const fxBasisLabel = () => (onBuying ? t('pricing.marginBasisBuying') : `${t('pricing.fxFactory')} + ${t('pricing.fxFrame')}`);
  /* THE TWO TIERS ARE PRICED DIFFERENTLY, so their workings say different things. T1 is built UP
     — overhead on the basis, profit on the overhead, extra cost on top. T2 is taken OUT — the
     basis divided by one minus the ratio, with the two halves splitting what that earns. A single
     wording for both is how the screen came to describe T2 with T1's arithmetic. */
  const basisWord = () => (onBuying ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase'));

  const fxTierOh = (which, r) => {
    if (r.oh == null) return null;
    const pctv = calc.tierSplit[which === 't1' ? 't1' : 't2'].oh * 100;
    if (which !== 't1') {
      return fx(
        t('pricing.fxOh'),
        `${pctOf(pctv)} x ${t(which === 't1' ? 'pricing.t0' : 'pricing.t1')}`,
        [`${pctOf(pctv)} x ${n2(r.paid)}`],
        fmtUSD(r.oh),
      );
    }
    /* Max's own wording, 2026-09-24 — the percentage is taken on the stated figure and nothing
       else is folded into it. */
    return fx(
      t('pricing.fxOh'),
      `${pctOf(pctv)} x (${t('pricing.fxT1Base')})`,
      [`${pctOf(pctv)} x ${n2(calc.t1On)}`],
      fmtUSD(r.oh),
    );
  };
  const fxTierPf = (which, r) => {
    if (r.pf == null) return null;
    const pctv = calc.tierSplit[which === 't1' ? 't1' : 't2'].profit * 100;
    if (which !== 't1') {
      return fx(
        t('pricing.fxProfit'),
        `${pctOf(pctv)} x ${t('pricing.t1')}`,
        [`${pctOf(pctv)} x ${n2(r.paid)}`],
        fmtUSD(r.pf),
      );
    }
    /* ON THE BASE, not on the overhead — Jay, 2026-09-23, and Max's restatement answers it. */
    return fx(
      t('pricing.fxProfit'),
      `${pctOf(pctv)} x (${t('pricing.fxT1Base')})`,
      [`${pctOf(pctv)} x ${n2(calc.t1On)}`],
      fmtUSD(r.pf),
    );
  };
  const fxTierPaid = (which, r) => {
    if (r.paid == null) return null;
    if (which !== 't1') {
      const rr = Number(v.t1Ratio) || 0;
      return fx(
        t('pricing.fxT2'),
        t('pricing.fxT2Rule'),
        [`${basisWord()} ${n2(r.basis)} ÷ (1 − ${rr}%)`,
         `= ${n2(r.basis)} ÷ ${(1 - rr / 100).toFixed(2)}`,
         `${n2(r.basis)} + ${n2(r.oh)} + ${n2(r.pf)}`],
        fmtUSD(r.paid),
      );
    }
    /* THE BASIS PRINTED 0.00 — found by check_cp_tier_hints.py on 2026-10-06, while fixing the
       Results table's own tier hint. Two things were wrong and the first hid the second.
       `tierRows` returns the field as `basis`, not `on`, so `r.on` was undefined and n2 made it
       "0.00": the line read 0.00 + 9.33 + 6.22 = 15.55 over a figure of 77.73.
       And `basis` would have been wrong too. It is `side.on`, the margin basis, which follows the
       Markup Base / Total buying cost convention — but the SIMULATOR's T1 does not: `simLine`
       builds it on `t1On` every time, whatever the convention, and t1On is the buying cost. So the
       hint reads the same figure the model used. */
    return fx(
      `⇒ ${t('pricing.t0')}`,
      `${t('pricing.t1BaseLine')} + ${t('pricing.fxOh')} + ${t('pricing.fxProfit')}`,
      [`${n2(calc.buyingCost)} + ${n2(r.oh)} + ${n2(r.pf)}`],
      fmtUSD(r.paid),
    );
  };

  /* A tier, in the four lines Max drew: the overhead, the profit, the extra cost, and the price
     they add up to — per unit and for the order. Both grains because a quote is argued in both:
     the vendor is told a unit price and the approver is deciding on an order total.

     The EXTRA COST line follows the convention this quote is on. On the guideline it is debited to
     the partner on top of the tier, so it is added here; on the quote sheet it is already inside
     the basis the tier was worked out from, and adding it again would count it twice. */
  const tierRows = (which) => {
    const side = calc.byBasis[onBuying ? 'buying' : 'base'];
    const oh = which === 't1' ? side.simOh1 : side.simOh2;
    const pf = which === 't1' ? side.simPf1 : side.simPf2;
    /* The four lines come from the model's `simLine`, which is Max's own statement of them
       (2026-09-22). `paid` is their SUM and not the tier price plus extra: under his rule the
       extra cost is already inside the overhead line, and the row is the addition he wrote. */
    const paid = which === 't1' ? side.sim1 : side.sim2;
    const price = which === 't1' ? side.t1 : side.t2;
    /* T2's first line is its BASIS, not the extra cost: under `basis ÷ (1 − r)` the extra is
       either outside the figure (Markup Base) or already inside it (Total buying cost), and in
       neither case is it a line added on top. */
    return { side, price, oh, pf, extra: calc.excluded, paid, basis: side.on };
  };


  /* THE CBD METHOD'S STEP 5 — Linda's "Calculation" sheet, row for row: what the factory charges,
     what HPAC adds, the two prices it lands on and what HPAC keeps. No Case and no ratio box: the
     30% and the 1.3 are pricing rules (Settings), the same for every quote raised under them. The
     two absorb ticks are the sheet's B10 and B11 — a surcharge HPAC absorbs leaves both prices
     while the factory still charges it, so GP falls by exactly that much. */
  const cbdBlock = () => {
    const k = calc.cbd || {};
    const q = calc.qty;
    const row = (label, unit, opts = {}) => (
      <tr className={opts.cls || ''}>
        <th>{label}{opts.sub ? <em>{opts.sub}</em> : null}</th>
        <td className="unit">{unit === null ? '—' : <>{opts.pct ? unit : fmtUSD(unit)}{opts.pct ? null : <span className="per"> /{t('pricing.unit')}</span>}</>}</td>
        <td className="tot">{opts.noTotal || unit === null ? '' : `${t('pricing.totalWord')} ${fmtUSD(unit * q)}`}</td>
        <td className="ctl">{opts.ctl || null}</td>
      </tr>
    );
    const absorb = (key, label) => (
      <label className="cp-cbd-abs">
        <input type="checkbox" checked={!!v[key]} disabled={!canEdit}
          onChange={(e) => setVal(key, e.target.checked)} />
        {label}
      </label>
    );
    return (
      <div className="cp-cbd" data-tour="cp-cbd">
        <p className="cp-sub">{t('pricing.cbdSub', { oh: k.ohPct, x: (1 + (k.markupPct || 0) / 100).toFixed(2) })}</p>
        <table className="cp-tt cp-cbd-tt">
          <tbody>
            {row(t('pricing.cbdBase'), k.base, { sub: t('pricing.cbdBaseSub') })}
            {row(t('pricing.cbdFrame'), k.frame)}
            {row(t('pricing.cbdBaseFrame'), (k.base || 0) + (k.frame || 0), { cls: 'sum' })}
            {row(t('pricing.cbdOh', { pct: k.ohPct }), k.oh, { sub: t('pricing.cbdOhSub') })}
            {row(t('pricing.cbdMoq'), k.moq, { ctl: absorb('absorbMoq', t('pricing.cbdAbsorb')) })}
            {row(t('pricing.cbdSl'), k.sl, { ctl: absorb('absorbSl', t('pricing.cbdAbsorb')) })}
            {row(t('pricing.cbdT1'), k.t1, { cls: 'tier', sub: t('pricing.cbdT1Sub') })}
            {row(t('pricing.cbdT2'), k.t2, { cls: 'tier', sub: t('pricing.cbdT2Sub', { x: (1 + (k.markupPct || 0) / 100).toFixed(2) }) })}
            {row(t('pricing.cbdFactory'), k.factory, { sub: t('pricing.cbdFactorySub') })}
            {row(t('pricing.cbdGp'), k.gp, { cls: k.gp < 0 ? 'bad' : 'good' })}
            {row(t('pricing.cbdMargin'), k.margin === null ? null : `${(k.margin * 100).toFixed(1)}%`, { pct: true, noTotal: true })}
          </tbody>
        </table>
      </div>
    );
  };

  /* One tier, as the four lines Max drew. The ratio's two halves are editable here — the overhead
     is the fixed 15% and read-only, the profit is the half a person sets — so the table is both
     the statement and the control, which is what "simulator" means on his sketch. */
  const tierBlock = (which) => {
    const isT1 = which === 't1';
    const r = tierRows(which);
    const totalKey = isT1 ? 't0Ratio' : 't1Ratio';
    const ohKey = isT1 ? 't0Oh' : 't1Oh';
    const pfKey = isT1 ? 't0Profit' : 't1Profit';
    const fromKey = isT1 ? 't0EstFrom' : 't1EstFrom';
    const toKey = isT1 ? 't0EstTo' : 't1EstTo';
    const total = String(v[totalKey] ?? '').trim();
    const money2 = (x) => (x === null || x === undefined ? '—' : fmtUSD(x));
    const per = (x) => (x === null || x === undefined ? <span className="dash">—</span>
      : <>{fmtUSD(x)} <span className="per">/{t('pricing.unit')}</span></>);
    const tot = (x) => (x === null || x === undefined ? <span className="dash">—</span>
      : `${t('pricing.totalWord')} ${fmtUSD(x * calc.qty)}`);
    const from = Number(v[fromKey]);
    const to = Number(v[toKey]);
    const band = (r.paid !== null && from > 0 && to > 0)
      ? `${fmtUSD(r.paid * Math.min(from, to))} – ${fmtUSD(r.paid * Math.max(from, to))}` : null;
    /* T2 IS THE SAME RATIO ON TWO CONVENTIONS — Max, 2026-09-24: "thay vì tách như vậy thì bạn có
       thể thay bằng 2 dòng là Markup Base và Total buying cost tương ứng cho cùng mức phần trăm".

       It is the better answer to the question he asked before it — where the basis belongs now
       that the choice is made per option — because it stops being a choice the reader has to look
       up. Both figures are on the page; the quote carries the one marked, and an option is where
       that mark moves. The model has computed both all along (`byBasis`), so this prints what was
       already there rather than working anything out twice.

       And the two halves are gone from T2 with the build-up they belonged to: overhead and profit
       were the steps of an addition, and there is no addition here — it is one division. */
    if (!isT1) {
      const rr = Number(total) || 0;
      const bases = [
        { key: 'base', label: t('pricing.marginBasisBase'), on: calc.byBasis.base.on, price: calc.byBasis.base.t2 },
        { key: 'buying', label: t('pricing.marginBasisBuying'), on: calc.byBasis.buying.on, price: calc.byBasis.buying.t2 },
      ];
      const live = onBuying ? 'buying' : 'base';
      return (
        <div className="cp-tier" key={which}>
          <div className="cp-tier-head">
            {t('pricing.t1Ratio')}
            <div className="cp-money cp-tier-ratio">
              <input type="number" step="0.1" value={v.t1Ratio ?? ''} onChange={(e) => setT2Ratio(e.target.value)} /><i>%</i>
            </div>
            {needs('t1Ratio') && <b className="cp-req">*</b>}
          </div>
          <table className="cp-tt">
            <tbody>
              {bases.map((b) => (
                <tr key={b.key} className={b.key === live ? 'cp-tsum' : ''}>
                  <th>
                    {b.key === live && '⇒ '}{b.label}
                    {b.price !== null && fx(
                      `${t('pricing.t1')} — ${b.label}`,
                      t('pricing.fxT2Rule'),
                      [`${n2(b.on)} ÷ (1 − ${rr}%)`, `= ${n2(b.on)} ÷ ${(1 - rr / 100).toFixed(2)}`],
                      fmtUSD(b.price),
                    )}
                    {b.key === live && <em className="cp-live">{t('pricing.t2ThisQuote')}</em>}
                  </th>
                  <td className="pct"><span className="none">{n2(b.on)} ÷ (1 − {rr}%)</span></td>
                  <td className="unit">{per(b.price)}</td>
                  <td className="tot">{tot(b.price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="cp-tier-note">{t('pricing.t2Both')}</p>
          <div className="cp-est">
            {t('pricing.estMsrp')}
            <div className="cp-money"><input type="number" step="0.1" value={v[fromKey] ?? ''} onChange={(e) => setVal(fromKey, e.target.value)} /></div>
            {t('pricing.estTo')}
            <div className="cp-money"><input type="number" step="0.1" value={v[toKey] ?? ''} onChange={(e) => setVal(toKey, e.target.value)} /></div>
            {t('pricing.estTimes')}
            {band ? <b>{band} <span className="per">/{t('pricing.unit')}</span></b> : <span className="dash">—</span>}
          </div>
        </div>
      );
    }

    return (
      <div className="cp-tier" key={which}>
        <div className="cp-tier-head">
          {t(isT1 ? 'pricing.t0Ratio' : 'pricing.t1Ratio')}
          {total !== '' && <b>= {Number(total)}%</b>}
          {needs(totalKey) && <b className="cp-req">*</b>}
        </div>
        <table className="cp-tt">
          <tbody>
            {!isT1 && (
              /* THE BASIS, FIRST — Max, 2026-09-24, asking where it should live now that the
                 choice between Markup Base and Total buying cost is made per OPTION. It is the
                 figure T2 is divided out of, so the three lines read down the page in the order
                 they add up in: basis, then the two halves of what the tier earns, then the price.
                 Put third, as it was for one build, the column added up to the row above it with
                 the reader's eye travelling backwards.
                 When an option flips the basis, this is the line that visibly moves. The main
                 quote has no picker for it and never had one — it reads Markup Base — so the cell
                 says where the choice is made rather than looking like a box to fill in. */
              <tr>
                <th>{onBuying ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase')}</th>
                <td className="pct"><span className="none">{t('pricing.t2BasisFromOpt')}</span></td>
                <td className="unit">{per(r.basis)}</td>
                <td className="tot">{tot(r.basis)}</td>
              </tr>
            )}
            {/* WHAT THE TWO PERCENTAGES ARE TAKEN ON — Max, 2026-09-24: "trên dòng Helinox O.H cần
                thêm dòng thể hiện là Factory cost + Frame cost + Extra cost 1". Same shape as T2's
                block: the figure first, then what is taken on it, then the price they add to. */}
            <tr>
              <th>{t('pricing.t1BaseLine')}</th>
              <td className="pct" />
              <td className="unit">{per(calc.t1On)}</td>
              <td className="tot">{tot(calc.t1On)}</td>
            </tr>
            <tr>
              {/* Max's own lines, each carrying the arithmetic that produced it. Built from `r`,
                  which is what priced the row — never worked out again here. */}
              <th>{t('pricing.ohLine')}{fxTierOh(which, r)}</th>
              <td className="pct">
                {/* EDITABLE — Max, 2026-09-24, from Amy: "có một vài trường hợp phần trăm T1 chỉ
                    có 5%". It was read-only at a fixed 15% since 2026-09-18 ("O.H 15% => fix
                    cứng"), and 15% is still what a fresh quote opens at; it is no longer a wall.
                    `setPart` already recomputes the ratio from whichever half moved, so the head
                    above keeps agreeing with the two lines under it. */}
                <div className="cp-money">
                  <input
                    type="number"
                    step="0.1"
                    value={v[ohKey] ?? ''}
                    onChange={(e) => setPart(totalKey, ohKey, pfKey, e.target.value)}
                  /><i>%</i>
                </div>
              </td>
              <td className="unit">{per(r.oh)}</td>
              <td className="tot">{tot(r.oh)}</td>
            </tr>
            <tr>
              <th>{t('pricing.profitLine')}{fxTierPf(which, r)}</th>
              <td className="pct">
                <div className="cp-money">
                  <input
                    type="number"
                    step="0.1"
                    value={v[pfKey] ?? ''}
                    onChange={(e) => setPart(totalKey, pfKey, ohKey, e.target.value)}
                  /><i>%</i>
                </div>
              </td>
              <td className="unit">{per(r.pf)}</td>
              <td className="tot">{tot(r.pf)}</td>
            </tr>
            {/* The Extra cost row came out on 2026-09-24 — it is recovered as a marketing debit
                rather than marked up, so it is not one of the steps that build this price. */}
            <tr className="cp-tsum">
              {/* THE SAME WORD, TWO FIGURES, unless this row says which one it is. The sketch adds
                  the extra cost into the build-up, so this is what the partner pays at the tier;
                  the two-convention table in Results prints the tier price ALONE, with the extra
                  debited beside it. Both are right and they differ by the debit, so the row that
                  includes it says so. */}
              <th>
                ⇒ {t(isT1 ? 'pricing.t0' : 'pricing.t1')}{fxTierPaid(which, r)}
                {isT1 && <em>{t('pricing.t1Sum')}</em>}
                {!isT1 && total !== '' && (
                  <em>{t('pricing.t2Divide', {
                    basis: onBuying ? t('pricing.marginBasisBuying') : t('pricing.marginBasisBase'),
                    r: Number(total),
                  })}</em>
                )}
              </th>
              <td className="pct" />
              <td className="unit">{per(r.paid)}</td>
              <td className="tot">{tot(r.paid)}</td>
            </tr>
          </tbody>
        </table>
        <div className="cp-est">
          {t('pricing.estMsrp')}
          <div className="cp-money"><input type="number" step="0.1" value={v[fromKey] ?? ''} onChange={(e) => setVal(fromKey, e.target.value)} /></div>
          {t('pricing.estTo')}
          <div className="cp-money"><input type="number" step="0.1" value={v[toKey] ?? ''} onChange={(e) => setVal(toKey, e.target.value)} /></div>
          {t('pricing.estTimes')}
          {band ? <b>{band} <span className="per">/{t('pricing.unit')}</span></b> : <span className="dash">—</span>}
          {/* THE INLINE SHELF PRICE, ON THIS LINE — Jay, 2026-09-23: "put the inline retail price
              $109.95 on the same line and at the same eye level as the estimated MSRP range, so the
              two read together." It used to sit at the top of the column opposite, level with
              nothing. Here it is the figure the band is actually being judged against, so it is on
              the band's own line.
              T1 ONLY: the range is per tier and the shelf price is one number; printing it twice
              would make it look like two different prices. */}
          {isT1 && Number(v.inlineRrp) > 0 && (
            <span className="cp-est-vs">
              {t('pricing.vsInline')} <b>{fmtUSD(Number(v.inlineRrp))}</b>
            </span>
          )}
        </div>
        {/* WHICH MSRP IS THE PROPOSAL — Jay, 2026-09-23: "block 5 gives an MSRP of $209–244 and
            block 6 gives $120.95. Two MSRPs on one screen, almost double apart. Which one is the
            proposal? The screen has to answer that on its own."
            It can, and not as a judgement: `calc.msrp` is block 6's figure, and it is what the row
            stores, what the partner price is taken off and what the Results table prints. This
            band is read by NOTHING — the two multiples are typed and used here alone. So the two
            are not two answers to one question; they are a proposal and a sanity check, and each
            now says which it is. */}
        {band && <p className="cp-estnote">{t('pricing.estIsCheck')}</p>}
      </div>
    );
  };


  /* Model FIRST, price beside it, on one line (Max, 2026-08-28). The model is what a person
     recognises — "Chair One V2", not $16.32 — so it leads; and keeping them on one line stops the
     frame field standing a row taller than the three next to it. The picker holds its selection,
     and the model name is stored with the costing so a reloaded project still says which frame it
     was priced on. */
  // The row behind the chosen model — for the retail price it carries and its product page.
  const framePicked = frames.find((x) => x.model === v.frameModel) || null;

  const framePicker = (
    <select
      className="cp-framesel"
      value={v.frameModel || ''}
      onChange={(e) => {
        const fr = frames.find((x) => x.model === e.target.value);
        setVal('frameModel', e.target.value);
        if (!fr) return;
        setVal('frame', fr.price_usd);
        /* The shelf price of the in-line product comes with the model (Max, 2026-09-11). Only when
           the list has one AND the field is still empty: a collab is often priced above its in-line
           equivalent, and overwriting a figure somebody typed would undo their work every time they
           corrected the frame. Clear the field and re-pick to take the list's price. */
        if (fr.inline_retail_usd != null && String(v.inlineRrp ?? '').trim() === '') {
          setVal('inlineRrp', fr.inline_retail_usd);
        }
      }}
    >
      <option value="">{t('pricing.framePick')}</option>
      {frames.map((fr) => (
        <option key={fr.frame_pk_id} value={fr.model}>
          {fr.category} · {fr.model} — {fmtUSD(fr.price_usd)}
        </option>
      ))}
    </select>
  );

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main">
        <ModuleTabs module="pricing" />
        <div className="mh-titlerow">
          <div className="sp-title">
            <h2>{t('pricing.title')}</h2>
            <p>{t('pricing.subtitle')}</p>
          </div>
          <ModuleHelp steps={TOUR} tourKey="pricingCalculator" ready={!!rules}
            onBeforeTour={() => { setShowSaved(false); setShowRules(false); setMode('general'); }} />
        </div>

        <div className="cp-modes">
          {/* THREE tabs, one selected. The mode tabs used to key their highlight off `mode` alone and
              Saved projects off its own flag, so opening the list left two tabs lit at once — the
              screen claiming to be in two places (Max, 2026-08-28). They share one selection now. */}
          <button
            type="button"
            className={`cp-mode ${!showSaved && !showRules && mode === 'general' ? 'on' : ''}`}
            onClick={() => { setMode('general'); setShowSaved(false); setShowRules(false); }}
          >
            <strong>{t('pricing.modeGeneral')}</strong>
            <span>{t('pricing.modeGeneralSub')}</span>
          </button>
          {/* Not for the CEO (Max, 2026-09-11): the royalty model is a different deal shape that
              nobody brings to a final approval, and browsing everyone's saved work is not what a
              final approver opens this screen to do — they arrive on one quote from a mail link. */}
          {!isCeo && (
          <button
            type="button"
            className={`cp-mode ${!showSaved && !showRules && mode === 'licensed' ? 'on' : ''}`}
            onClick={() => { setMode('licensed'); setShowSaved(false); setShowRules(false); }}
          >
            <strong>{t('pricing.modeLicensed')}</strong>
            <span>{t('pricing.modeLicensedSub')}</span>
          </button>
          )}
          {!isCeo && (
          <button
            type="button"
            className={`cp-mode ${showSaved ? 'on' : ''}`}
            onClick={() => { setShowSaved(true); setShowRules(false); }}
          >
            <strong><FolderOpen size={13} /> {t('pricing.modeSaved')}</strong>
            <span>{t('pricing.modeSavedSub')}</span>
          </button>
          )}
          {/* THE ADMIN ROLE'S TAB. Drawn only for the people who may use it — `mayEdit` comes back
              with the rules, so the screen never offers a door the server will shut. */}
          {mayEditRules && (
          <button
            type="button"
            className={`cp-mode ${showRules ? 'on' : ''}`}
            onClick={() => setShowRules(true)}
          >
            <strong><Settings size={13} /> {t('pricing.modeRules')}</strong>
            <span>{t('pricing.modeRulesSub')}</span>
          </button>
          )}
        </div>

        <div className="cp-toolbar">
          {/* Read-only for the same people, and for the same reason: with Save gone, a box that
              still accepts typing is a promise the screen cannot keep. */}
          <input
            className="sp-input cp-name"
            placeholder={t('pricing.projectPlaceholder')}
            value={projectName}
            readOnly={!mayShape}
            onChange={(e) => setProjectName(e.target.value)}
          />
          {/* Beside the name because it is part of naming the thing: which shelf it goes on. The
              datalist offers what is already in use, so "27SS" does not become "27ss" and "27 SS"
              across three quotes. */}
          <input
            className="sp-input cp-folder"
            list="cp-folder-list"
            placeholder={t('pricing.folderPlaceholder')}
            value={folder}
            readOnly={!mayShape}
            onChange={(e) => setFolder(e.target.value)}
          />
          <datalist id="cp-folder-list">
            {folderOptions.map((f) => <option key={f} value={f} />)}
          </datalist>
          {projectId && <span className="cp-pid">{projectId}</span>}
          {mayShape && (
            <button type="button" className="sp-btn primary" disabled={busy} onClick={save}>
              <Save size={14} /> {projectId ? t('pricing.saveChanges') : t('pricing.save')}
            </button>
          )}
          {/* Not for the CEO (Max, 2026-09-11). Exporting the costing and round-tripping it through
              a spreadsheet is the work of whoever builds the quote; a final approver is here to
              decide on the one in front of them. Import Excel reads HEVN Costing's Price Approval
              workbook — Price Approval plus the factory's CBD sheets — and its INPUTS only (the
              Import template button and its flat file went on 2026-10-29, Max; a flat file
              downloaded before still reads). It lands in the form rather than in the database, so a
              workbook edited by hand can never carry a total the screen would not have produced. */}
          {!isCeo && (
            <>
              {/* Export Excel, Partner Offer (Excel) and Import vendor BOM came off the bar (Max, 05/11/2026); their code stays for now */}
              {/* Linda's step 5: the offer that goes to the partner — T2 only, after approval. */}
              {mode === 'general' && (
                <CbdPriceApprovalExport projectId={projectId} skus={skus} rules={rules}
                  decided={loadedGate?.extraOption || ''} apiBase={API}
                  meta={{ project: projectName, preparedBy: authName || '', reviewedBy: '', approver: '' }} />
              )}
              <button type="button" className="sp-btn" disabled={busy} onClick={() => fileRef.current?.click()}>
                <Upload size={14} /> {t('pricing.importExcel')}
              </button>
            </>
          )}
          {/* Stable ids: there are two hidden .xlsx inputs on this screen and they do NOT read in
              source order — the BOM one sits inside the role-gated fragment above. Picking one by
              position sent a vendor BOM to the template reader (2026-09-11). */}
          <input
            ref={fileRef}
            id="cp-import-template"
            type="file"
            accept=".xlsx"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importFile(f); }}
          />

          {/* Cancel, not "New project" — it abandons what is on screen, and naming it after the thing
              it creates hid that. It asks first whenever there is anything to lose. */}
          {mayShape && (projectId || projectName || hasWork()) && (
            <button
              type="button"
              className="sp-btn"
              onClick={() => (hasWork() ? setConfirmCancel(true) : (resetForm(), setStatus('')))}
            >
              {t('pricing.cancel')}
            </button>
          )}
          {status && <span className="cp-status">{status}</span>}
        </div>

        {/* What the approver was mailed about, on the screen they were sent to. */}
        {loadedGate && (
          <div className="cp-gate">
            <div className="cp-gate-head">
              <b>{t(`pricing.st${String(loadedGate.status).replace(/\s/g, '')}`)}</b>
              {loadedGate.submittedBy && (
                <span>{t('pricing.gateBy', { who: givenName(loadedGate.submittedBy) })}</span>
              )}
            </div>
            {!!loadedGate.note && (
              <div className="cp-gate-note">
                <b>{t('pricing.noteFrom')}:</b> {loadedGate.note}
              </div>
            )}
            {!!loadedGate.rejectReason && (
              <div className="cp-gate-warn">
                <b>{t('pricing.sentBackMgr')}:</b> {loadedGate.rejectReason}
              </div>
            )}
          </div>
        )}

        {!!renaming && (
          <div className="pa-overlay" onClick={() => setRenaming(null)}>
            <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('pricing.renameFolderTitle', { name: renaming.from })}</h3>
              <input
                className="sp-input"
                value={renaming.to}
                /* eslint-disable-next-line jsx-a11y/no-autofocus */
                autoFocus
                onChange={(e) => setRenaming((r) => ({ ...r, to: e.target.value }))}
                onKeyDown={(e) => { if (e.key === 'Enter') renameFolder(); }}
              />
              <div className="pa-modal-actions">
                <button type="button" className="pa-btn" onClick={() => setRenaming(null)}>{t('pricing.cancel')}</button>
                <button type="button" className="pa-btn approve" disabled={!renaming.to.trim()} onClick={renameFolder}>
                  {t('pricing.renameFolder')}
                </button>
              </div>
            </div>
          </div>
        )}

        {!!confirmFolderDel && (
          <div className="pa-overlay" onClick={() => setConfirmFolderDel('')}>
            <div className="pa-modal" onClick={(e) => e.stopPropagation()}>
              <h3>{t('pricing.deleteFolderTitle', { name: confirmFolderDel })}</h3>
              {/* Says what happens to the quotes, because "delete" over a folder full of costings
                  reads like it takes them with it. It does not. */}
              <p className="pa-modal-sub">{t('pricing.deleteFolderBody')}</p>
              <div className="pa-modal-actions">
                <button type="button" className="pa-btn" onClick={() => setConfirmFolderDel('')}>{t('pricing.cancel')}</button>
                <button
                  type="button"
                  className="pa-btn reject"
                  onClick={async () => {
                    const name = confirmFolderDel;
                    setConfirmFolderDel('');
                    setError('');
                    try {
                      const r = await fetch(`${API}/folders/${encodeURIComponent(name)}`, {
                        method: 'DELETE', credentials: 'include',
                      });
                      const d = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(d.error || t('xPricing.deleteFailed', { status: r.status }));
                      await loadProjects();
                      await loadFolders();
                    } catch (err) { setError(err.message); }
                  }}
                >
                  <Trash2 size={14} /> {t('pricing.confirmDelete')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* THE WHOLE PANEL, not a summary box — Max, 2026-10-05: "nó hiện nguyên cái bự luôn chứ".
            The approver reads the quote here exactly as they would on Pricing Approval: the same
            component, the same figures, the same gate at its foot, which then opens the same
            per-SKU dialog. Two screens, one decision. */}
        {padOpen && gateRow && (
          <PricingApprovalDetail
            row={gateRow}
            snapshot={gateSnapshot}
            skuDecisions={gateSnapshot?.decisions}
            canDecide={canDecideNow}
            canGate={canDecideNow}
            busy={busy}
            flash={padFlash}
            onClose={() => { setPadOpen(false); setPadFlash(''); }}
            onDecide={(option, instruction) => padPost('extra-decision', { option, instruction },
              t('pricing.padDecisionSaved'))}
            onMemo={(memo) => padPost('sop-memo', { memo }, t('pricing.padMemoSaved'))}
            onGate={(action, seen) => {
              setDecisionNote(''); setDecisionMarks(null); setPadSeen(seen instanceof Set ? seen : null);
              setDecisionFor({
                action, from: 'pad',
                group: { groupId: projectId, groupName: projectName, skuRows: gateSkuRows(seen) },
              });
            }}
          />
        )}

        {decisionFor && (
          <DecisionDialog
            spec={decisionFor}
            note={decisionNote}
            onNote={setDecisionNote}
            busy={busy}
            selectedIds={[]}
            onSwitch={(action) => setDecisionFor((d) => ({ ...d, action }))}
            onCancel={() => {
              setDecisionFor(null); setDecisionNote(''); setDecisionMarks(null); setPadSeen(null);
            }}
            /* z-index 60 against the panel's 1200: without the lift the dialog opens BEHIND the
               quote and the screen simply stops responding — the same trap Pricing Approval's own
               caller records. */
            overlayClass={decisionFor.from === 'pad' ? 'over-pad' : ''}
            summary={decisionFor.action === 'approve' ? gateSummary() : null}
            onMarks={setDecisionMarks}
            onConfirm={(decided) => {
              const { action } = decisionFor;
              const note = decisionNote.trim();
              setDecisionFor(null); setDecisionNote(''); setDecisionMarks(null); setPadSeen(null);
              /* The endpoint follows the ROWS, not the button that opened the dialog — the same
                 rule Pricing Approval and Project Approval both follow. Crossing every SKU is a
                 rejection, and the per-SKU reasons travel with it rather than being retyped. */
              const marks = Array.isArray(decided) ? decided : null;
              const allOut = marks && marks.length && marks.every((d) => d.decision === 'Rejected');
              return decide(
                allOut ? 'reject' : action,
                allOut
                  ? [note, ...marks.map((d) => `${d.name || d.projectId}: ${d.note}`)]
                    .filter(Boolean).join('\n')
                  : note,
                !allOut && action === 'approve' ? marks : null,
              );
            }}
          />
        )}

        {/* What the BOM said, and what it could not say. A read that silently fills three boxes is
            a read nobody can check — the notes are where the frame and the FF fee are explained,
            and they are the two things that go wrong. */}
        {!!bomRead && (
          <div className={`cp-bom ${bomRead.ok ? '' : 'is-err'}`}>
            <div className="cp-bom-head">
              <strong>
                {bomRead.ok
                  ? t('pricing.bomRead', {
                    vendor: bomRead.vendorName,
                    n: bomRead.skus.length,
                    names: bomRead.skus.map((x) => x.name).join(', '),
                  })
                  : t('pricing.bomFailed')}
              </strong>
              <button type="button" className="cp-undo" onClick={() => setBomRead(null)}>
                {t('pricing.dismiss')}
              </button>
            </div>
            {!bomRead.ok && <p>{bomRead.error}</p>}
            {!bomRead.ok && !!bomRead.looked?.length && (
              <>
                <p className="cp-bom-sub">{t('pricing.bomLookedFor')}</p>
                <ul>{bomRead.looked.map((l) => <li key={l}>{l}</li>)}</ul>
                {!!bomRead.sheets?.length && (
                  <p className="cp-bom-sub">{t('pricing.bomSheets', { list: bomRead.sheets.join(', ') })}</p>
                )}
              </>
            )}
            {bomRead.ok && <ul>{bomRead.notes.map((n) => <li key={n}>{n}</li>)}</ul>}
          </div>
        )}

        {error && <div className="sp-alert err">{error}</div>}

        {showSaved && !!approvalMsg && (
          <div className={`sp-alert ${approvalMsg.ok ? '' : 'err'}`}>{approvalMsg.text}</div>
        )}

        {/* ================= PRICING RULES (Admin) ==================================================
            Max, 2026-09-29. Three constants, each changed on its own with its own reason, and every
            change kept: a quote is priced by the rule in force when it was CREATED, so the history
            is not a log beside the truth — it IS how an old quote still prices correctly. */}
        {showRules && (
          <section className="sp-section cp-rules">
            <h3 className="cp-rules-h">{t('pricing.rulesTitle')}</h3>
            <p className="cp-rules-sub">{t('pricing.rulesSub')}</p>
            {!!ruleMsg && <div className="sp-alert">{ruleMsg}</div>}
            <table className="sp-table cp-rules-tbl">
              <thead>
                <tr>
                  <th>{t('pricing.ruleName')}</th>
                  <th className="num">{t('pricing.ruleNow')}</th>
                  <th className="num">{t('pricing.ruleNew')}</th>
                  <th>{t('pricing.ruleWhy')}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {['partner_ratio_pct', 'tier_oh_pct', 'frame_profit_pct'].map((k) => {
                  const d = ruleDraft[k] || {};
                  const set = (patch) => setRuleDraft((prev) => ({ ...prev, [k]: { ...(prev[k] || {}), ...patch } }));
                  const n = Number(d.value);
                  /* Save stays dead until BOTH are answered. The server refuses the same two things,
                     because a rule that lives only in the form is not a rule — this only saves the
                     person a round trip. */
                  const ready = Number.isFinite(n) && n >= 0 && n < 100
                    && String(d.reason || '').trim().length >= 4;
                  return (
                    <tr key={k}>
                      <td>
                        <b>{t(`pricing.rule_${k}`)}</b>
                        <div className="cp-rules-note">{t(`pricing.ruleHint_${k}`)}</div>
                      </td>
                      <td className="num"><b>{currentRules ? `${currentRules[k]}%` : '—'}</b></td>
                      <td className="num">
                        <input
                          type="number" step="0.1" min="0" max="99.9"
                          className="cp-rules-val"
                          value={d.value ?? ''}
                          onChange={(e) => set({ value: e.target.value })}
                        />
                      </td>
                      <td>
                        <input
                          className="cp-rules-why"
                          placeholder={t('pricing.ruleWhyPh')}
                          value={d.reason ?? ''}
                          onChange={(e) => set({ reason: e.target.value })}
                        />
                      </td>
                      <td>
                        <button type="button" className="sp-btn sp-btn-primary"
                          disabled={!ready || busy} onClick={() => saveRule(k)}>
                          {t('pricing.ruleSave')}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <h4 className="cp-rules-h4">{t('pricing.ruleHistory')}</h4>
            <table className="sp-table cp-rules-tbl">
              <thead>
                <tr>
                  <th>{t('pricing.ruleName')}</th>
                  <th className="num">{t('pricing.ruleValue')}</th>
                  <th>{t('pricing.ruleWhy')}</th>
                  <th>{t('pricing.ruleBy')}</th>
                  <th>{t('pricing.ruleFrom')}</th>
                </tr>
              </thead>
              <tbody>
                {ruleHistory.map((h, i) => (
                  <tr key={`${h.rule_key}-${h.effective_from}-${i}`}>
                    <td>{t(`pricing.rule_${h.rule_key}`)}</td>
                    <td className="num">{Number(h.value_num)}%</td>
                    <td>{h.reason}</td>
                    <td>{h.changed_by}</td>
                    <td className="nw">{String(h.effective_from).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {showSaved && (
          <section className="sp-section">
            <div className="sp-section-head">
              <div><FolderOpen size={14} /> {t('pricing.modeSaved')}</div>
              <div className="sp-head-btns">
                {/* A folder is made here and then dragged into — which is why the registry holds
                    rows: an empty folder has to exist before anything is in it. */}
                <input
                  className="sp-input cp-newfolder"
                  placeholder={t('pricing.newFolderPlaceholder')}
                  value={newFolder}
                  onChange={(e) => setNewFolder(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); makeFolder(); } }}
                />
                <button type="button" className="sp-btn" disabled={!newFolder.trim()} onClick={makeFolder}>
                  <Plus size={13} /> {t('pricing.newFolder')}
                </button>
                <span className="cp-count">
                  {savedRows.length}
                  {savedRows.length !== projects.length ? ` / ${projects.length}` : ''}
                </span>
                {(!!Object.keys(savedFilters).length || !!savedSort.key) && (
                  <button
                    type="button"
                    className="sp-btn"
                    onClick={() => { setSavedFilters({}); setSavedSort({ key: '', dir: 'asc' }); }}
                  >
                    {t('pricing.clearFilters')}
                  </button>
                )}
              </div>
            </div>
            <div className="sp-table-wrap">
              <table className="sp-table cp-table">
                <thead>
                  <tr>
                    {SAVED_COLUMNS.map((c) => <SortTh key={c.key} {...savedTh(c)} />)}
                    <th className="cp-actioncol">{t('pricing.colAction')}</th>
                  </tr>
                </thead>
                <tbody>
                  {savedGroups.map((g) => (
                    <React.Fragment key={g.key || '__none__'}>
                      {/* One heading row per folder, spanning the table. Clicking it folds the group
                          — the state is a list of CLOSED folders, so one that appears later (a new
                          season) starts open rather than hidden. */}
                      {/* The heading is the drop target — a FOLDER, not a position, so it means the
                          same thing however the table is sorted or filtered. That is what makes
                          dragging safe on a table that already reorders itself. */}
                      <tr
                        className={`cp-grouprow ${dropOn === g.key ? 'is-over' : ''}`}
                        onDragOver={(e) => { e.preventDefault(); setDropOn(g.key); }}
                        onDragLeave={() => setDropOn((d) => (d === g.key ? null : d))}
                        onDrop={(e) => { e.preventDefault(); dropFolder(g.key); }}
                      >
                        <td colSpan={SAVED_COLUMNS.length + 1}>
                          <div className="cp-grouphead">
                            <button
                              type="button"
                              className="cp-group"
                              aria-expanded={!shutFolders.includes(g.key)}
                              onClick={() => setShutFolders((cur) => (cur.includes(g.key)
                                ? cur.filter((x) => x !== g.key) : [...cur, g.key]))}
                            >
                              {shutFolders.includes(g.key) ? <ChevronRight size={13} /> : <ChevronDown size={13} />}
                              <FolderOpen size={13} />
                              <strong>{g.label}</strong>
                              <span className="cp-groupn">{g.rows.length}</span>
                            </button>
                            {/* Unfiled is not a folder — it is the absence of one — so it cannot be
                                renamed or deleted. */}
                            {!!g.key && (
                              <span className="cp-groupbtns">
                                <button type="button" className="sp-mini-btn"
                                  onClick={() => setRenaming({ from: g.key, to: g.key })}>
                                  <Pencil size={12} /> {t('pricing.renameFolder')}
                                </button>
                                <button type="button" className="sp-mini-btn sp-mini-danger"
                                  onClick={() => setConfirmFolderDel(g.key)}>
                                  <Trash2 size={12} /> {t('pricing.del')}
                                </button>
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {!shutFolders.includes(g.key) && g.rows.map((p) => (
                    <tr
                      key={p.project_id}
                      className="cp-dragrow"
                      draggable
                      onDragStart={(e) => {
                        dragging.current = p.project_id;
                        // Firefox will not start a drag without data on the transfer.
                        e.dataTransfer.setData('text/plain', p.project_id);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => { dragging.current = null; setDropOn(null); }}
                    >
                      <td><button type="button" className="cp-link" onClick={() => loadProject(p.project_id)}>{p.project_id}</button></td>
                      <td>{p.name}</td>
                      <td>{p.modeLabel}</td>
                      <td className="num">{p.count || '—'}</td>
                      <td>
                        <span className={`cp-st cp-st-${(p.approval_status || 'Draft').replace(/\s/g, '-').toLowerCase()}`}>
                          {p.statusLabel}
                        </span>
                        {/* Who sent it back and why, on the row rather than a click away: a rejected
                            quote with no reason in sight is a quote nobody knows how to fix. */}
                        {!!p.reject_reason && (
                          <em className="cp-st-why" title={`${p.reject_by || ''}: ${p.reject_reason}`}>
                            {p.reject_level === 'ceo' ? t('pricing.sentBackCeo') : p.reject_level === 'calc' ? t('pricing.sentBackCalc') : t('pricing.sentBackMgr')}
                          </em>
                        )}
                      </td>
                      <td className="num">{p.buying_cost != null ? fmtUSD(p.buying_cost) : '—'}</td>
                      <td className="num">{p.t1 != null ? fmtUSD(p.t1) : '—'}</td>
                      <td className="num">{p.msrpAny != null ? fmtUSD(p.msrpAny) : '—'}</td>
                      <td>{p.savedBy || '—'}</td>
                      <td>{p.savedOn}</td>
                      {/* This list is the ARCHIVE, not the queue. Everything that MOVES a quote lives
                          on Pricing Approval — mixing the two put a decision somebody owes in the
                          middle of every quote ever saved, with nothing to tell them apart (Max,
                          2026-09-10). The status is still here, read-only, because it qualifies the
                          numbers beside it. */}
                      <td className="cp-actioncol">
                        <button type="button" className="sp-mini-btn" onClick={() => loadProject(p.project_id)}>
                          <Pencil size={12} /> {p.approval_status === 'Approved' ? t('pricing.view') : t('pricing.edit')}
                        </button>
                        {p.approval_status === 'Approved' && (
                          <button type="button" className="sp-mini-btn" disabled={busyId === p.project_id}
                            onClick={() => approvalAction(p.project_id, 'duplicate')}>
                            <Copy size={12} /> {t('pricing.revise')}
                          </button>
                        )}
                        {['Draft', 'Rejected'].includes(p.approval_status || 'Draft') && (
                          <button type="button" className="sp-mini-btn sp-mini-danger" onClick={() => setConfirmDel(p.project_id)}>
                            <Trash2 size={12} /> {t('pricing.del')}
                          </button>
                        )}
                      </td>
                    </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  {!savedRows.length && (
                    <tr><td className="sp-empty" colSpan={SAVED_COLUMNS.length + 1}>{t('pricing.nothingSaved')}</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {!showSaved && !showRules && mode === 'general' && (
          <>
            <div className="cp-skubar">
              <span className="cp-skulabel">{t('pricing.sku')}</span>
              {skus.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  className={`cp-skutab ${i === cur ? 'on' : ''}`}
                  onClick={() => setCur(i)}
                >
                  {s.name || `SKU ${i + 1}`} <i>{num(s.values.qty).toLocaleString('en-GB')}</i>
                </button>
              ))}
              <div className="cp-skubtns">
                {mayShape && (
                  <button type="button" className="sp-btn" onClick={addSku}><Plus size={13} /> {t('pricing.addSku')}</button>
                )}
                {/* Right after Add SKU (Max, 2026-09-11). An approver reaches this screen from the
                    mail link via View, and having to go back to the queue to say yes is the step
                    this removes. */}
                {canDecideNow && (
                  <button
                    type="button"
                    className="sp-btn cp-approve"
                    disabled={busy}
                    onClick={() => { setPadFlash(''); setPadOpen(true); }}
                  >
                    <CheckCircle2 size={13} /> {t('pricing.approvePricing')}
                  </button>
                )}
                {mayShape && (
                  <>
                    <button type="button" className="sp-btn" onClick={dupSku}><Copy size={13} /> {t('pricing.duplicate')}</button>
                    <button type="button" className="sp-btn" disabled={skus.length <= 1} onClick={delSku}>
                      <Trash2 size={13} /> {t('pricing.del')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Max, 2026-09-14, marked this spot: photos and the conversation about the quote,
                between the SKU tabs and the numbers. Both belong to the QUOTE, not to one SKU —
                a collab is priced as one thing however many items it covers. */}
            {/* Which Project Tracker project this quote prices (Amy 30/10, Max 31/10) — its Costing column follows the
                quote. Saved on its own, so a submitted (frozen) quote can still be linked. */}
            <CpTrackerLink projectId={projectId} lines={skus.map((x) => x.name || '')} canEdit={mayShape} />
            <CpAttachments
              projectId={projectId}
              readOnly={!canEdit}
              pendingPhotos={pendingPhotos}
              onPendingPhotos={setPendingPhotos}
              pendingMessages={pendingMessages}
              onPendingMessages={setPendingMessages}
              onPhotoCount={setPhotoCount}
            />

            <div className={`cp-grid ${inputsOpen ? '' : 'is-folded'}`}>
              <section className={`sp-section cp-inputs ${inputsOpen ? '' : 'is-folded'}`}>
                <div className="sp-section-head">
                  <div><Calculator size={14} /> {t('pricing.inputs')}</div>
                  <button
                    type="button"
                    className="cp-fold"
                    aria-expanded={inputsOpen}
                    aria-controls="cp-inputs-body"
                    onClick={() => setInputsOpen((o) => !o)}
                  >
                    {inputsOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    {inputsOpen ? t('pricing.collapse') : t('pricing.expand')}
                  </button>
                </div>
                <div id="cp-inputs-body" className="cp-inputs-body" hidden={!inputsOpen}>

                {/* FIVE NUMBERED STEPS, in the order a quote is actually built — the layout
                    tabled on 2026-09-17. What changed is grouping, not arithmetic: the frame is its
                    own cost rather than a line inside the factory's, because it is bought somewhere
                    else, priced off its own list, and the way it reaches the vendor decides three
                    more figures. The margin is still taken on factory + frame together. */}
                <div className="cp-step">
                  <div className="cp-step-head"><span className="cp-step-no">1</span>
                    <h4 className="cp-h">{t('pricing.stepOrder')}</h4></div>
                  {/* HOW THIS QUOTE IS PRICED — the guideline (O.H 15% + a tier ratio) or HEVN
                      Costing's CBD workbook (O.H 30%, T2 × 1.3). First, because every figure below
                      it means something different under the other one. */}
                  <div className="cp-methodbar" data-method={isCbd(v) ? 'cbd' : 'guideline'}>
                    <span className="lbl">{t('pricing.method')}</span>
                    <select className="cp-methodsel" value={isCbd(v) ? 'cbd' : 'guideline'}
                      disabled={!canEdit} onChange={(e) => setMethod(e.target.value)}>
                      <option value="cbd">{t('pricing.methodCbd')}</option>
                      <option value="guideline">{t('pricing.methodGuideline')}</option>
                    </select>
                    <em>{isCbd(v) ? t('pricing.methodCbdHint') : t('pricing.methodGuidelineHint')}</em>
                  </div>
                  {/* THE OFFER'S OWN FACTS, on the CBD method — what the workbook's CEO Approval and
                      Partner Offer sheets print in their heads: the partner (typed, "no source field
                      in factory CBD" — Linda's point 5), the season, the price term and the validity.
                      Quote-wide, so they are written to every SKU like the method. The colour and the
                      Short Lead Time answer are per style, as the Offer Sheet has them. */}
                  {isCbd(v) && (
                    <div className="cp-cbd-offer">
                      <div className="cp-cbd-offer-h">{t('pricing.cbdOfferBox')}</div>
                      <label className="cp-field"><span>{t('pricing.cbdPartner')}</span>
                        <input className="sp-input" value={v.partnerName || ''} onChange={(e) => setQuoteVal('partnerName', e.target.value)} /></label>
                      {/* The factory, for the Price Approval sheet's head (Linda's template, 2026-10-26). */}
                      <label className="cp-field"><span>{t('pricing.cbdFactoryName')}</span>
                        <input className="sp-input" value={v.factoryName || ''} placeholder="MS" onChange={(e) => setQuoteVal('factoryName', e.target.value)} /></label>
                      <label className="cp-field"><span>{t('pricing.cbdSeason')}</span>
                        <input className="sp-input" value={v.offerSeason || ''} placeholder="S27" onChange={(e) => setQuoteVal('offerSeason', e.target.value)} /></label>
                      <label className="cp-field"><span>{t('pricing.cbdTerm')}</span>
                        <input className="sp-input" value={v.priceTerm ?? 'FOB HCM, Vietnam'} onChange={(e) => setQuoteVal('priceTerm', e.target.value)} /></label>
                      <label className="cp-field"><span>{t('pricing.cbdValidFrom')}</span>
                        <input className="sp-input" type="date" value={v.validFrom || ''} onChange={(e) => setQuoteVal('validFrom', e.target.value)} /></label>
                      <label className="cp-field"><span>{t('pricing.cbdValidTo')}</span>
                        <input className="sp-input" type="date" value={v.validTo || ''} onChange={(e) => setQuoteVal('validTo', e.target.value)} /></label>
                    </div>
                  )}
                  <div className={isCbd(v) ? 'cp-row5 cp-row7' : 'cp-row5'}>
                    {/* No hint on these four — Max, 2026-09-17. "SKU / item name" and "Order
                        quantity (EA)" say what they are, and a line of explanation under a box that
                        needs none is a line the eye has to rule out. The boxes are bottom-aligned by
                        .cp-trio and .cp-row2, so dropping the hints does not step the row. */}
                    <label className="cp-field">
                      <span>{t('pricing.skuName')}</span>
                      <input className="sp-input" value={sku.name} onChange={(e) => setSkuField({ name: e.target.value })} />
                    </label>
                    {isCbd(v) && (
                      <label className="cp-field">
                        <span>{t('pricing.cbdColor')}</span>
                        <input className="sp-input" value={v.color || ''} onChange={(e) => setVal('color', e.target.value)} />
                      </label>
                    )}
                    {isCbd(v) && (
                      <label className="cp-field">
                        <span>{t('pricing.styleCode')}</span>
                        <input className="sp-input" value={v.styleCode || ''} onChange={(e) => setVal('styleCode', e.target.value)} />
                      </label>
                    )}
                    <label className="cp-field">
                      <span>{t('pricing.qty')}</span>
                      <input className="sp-input" type="number" value={v.qty} onChange={(e) => setVal('qty', e.target.value)} />
                    </label>
                    {/* FIVE FIELDS, ONE ROW — Max, 2026-09-22: "SKU / item name, Order quantity
                        (EA), PO issue date, Requested XF date và Lead time nằm trên 1 hàng", and
                        the mockup he signed off has them as five siblings of one grid.
                        They were wrapped in `.cp-trio`, a NESTED grid spanning the whole row, so
                        the three dates could only ever land on a second line however wide the card
                        got. The wrapper existed to keep the lead time beside the two dates it is
                        the subtraction of — which source order already does, now that they are
                        siblings. */}
                      <label className="cp-field">
                        <span>{t('pricing.poDate')}</span>
                        <input className="sp-input" type="date" value={v.poDate} onChange={(e) => setVal('poDate', e.target.value)} />
                      </label>
                      <label className="cp-field">
                        <span>{t('pricing.xfDate')}</span>
                        <input className="sp-input" type="date" value={v.xfDate} onChange={(e) => setVal('xfDate', e.target.value)} />
                      </label>
                      <label className="cp-field">
                        <span>{t('pricing.leadLabel')}<b className="cp-tag">{t('pricing.autoTag')}</b></span>
                        {/* The one hint kept in this step, because it is the only box here whose
                            value is not typed: it says the subtraction it came from and what that
                            buys — XF − PO, and the surcharge band that follows. */}
                        <em>{calc.lead === null
                          ? t('pricing.leadNone')
                          : t('pricing.leadWorked', { days: calc.lead, rate: fmtPct(calc.slRate) })}</em>
                        <div className="cp-money">
                          <input type="number" readOnly className="cp-autoval"
                            value={calc.lead === null ? '' : calc.lead} />
                          <i>{t('pricing.days')}</i>
                        </div>
                      </label>
                      {/* The Offer Sheet's own column H: this SKU's answer, beside the lead time it
                          is about. Auto reads the lead time as the guideline does. */}
                      {isCbd(v) && (
                        <label className="cp-field">
                          <span>{t('pricing.cbdSlFlag')}</span>
                          <select className="sp-input" value={v.slFlag || ''} onChange={(e) => setVal('slFlag', e.target.value)}>
                            <option value="">{t('pricing.cbdSlAuto', { rate: fmtPct(slRateOf(calc.lead)) })}</option>
                            <option value="yes">{t('pricing.cbdSlYes')}</option>
                            <option value="no">{t('pricing.cbdSlNo')}</option>
                          </select>
                        </label>
                      )}
                  </div>
                </div>

                {/* THE SKETCH, 2026-09-18 — the input is two boxes side by side: the COLLAB being
                    priced, and the INLINE product it is measured against. They are ONE grid, so a
                    block and the block beside it share a row and stay level however tall either
                    one grows; two columns of their own drift apart a few pixels at a time and stop
                    being readable across, which is the only reason they are side by side.

                    The order above them is neither column's: it is the same order whichever one
                    you are reading, so it spans both. */}
                <div className="cp-io">
                  <div className="cp-io-head">{t('pricing.colCollab')}</div>
                  <div className="cp-io-head bench">
                    {t('pricing.colInline')}
                    <b className="cp-tag">{t('pricing.benchTag')}</b>
                    {/* IT BELONGS TO THIS COLUMN, so it is on this column's head: it fills the
                        factory boxes AND the frame, two blocks, and a button inside either one of
                        them would have named half of what it does.

                        NEXT TO THE NAME, not at the far end of the row — Max, 2026-09-22: "vị trí
                        đang ko okie và design cũng ko ok luôn". `margin-left: auto` had put it
                        roughly 800px from the words it belongs to, where it read as furniture
                        someone had left on the line. It is the same pill as the tag beside it
                        rather than a boxed button, because a button chrome that outweighs the
                        heading it sits on is the heading's competitor. */}
                    {canEdit && (
                      <button
                        type="button"
                        className="cp-benchimp"
                        disabled={busy}
                        onClick={() => searchBenchLibrary('')}
                        title={t('pricing.benchImportHint')}
                      >
                        {t('pricing.benchImport')}
                      </button>
                    )}
                  </div>

                {/* ONE FRAME PER COMPARISON — Jay, 2026-09-23: "the cards are split into
                    separate boxes on each side, so it isn't clear where to start or where to go
                    next. Make the left and right read as one pair."
                    They already shared a row and a height; what they did not share was an EDGE, so
                    the eye read two columns of cards rather than a row of comparisons. The frame
                    holds both halves with one rule down the middle, which is the shortest way to
                    say "these two are about the same thing". */}
                <div className="cp-cmp">
                <div className="cp-step">
                  <div className="cp-step-head"><span className="cp-step-no">2</span>
                    <h4 className="cp-h">{t('pricing.stepFactory')}</h4>
                    {/* THE RUNNING TOTAL OF THE BLOCK, as the agreed mockup carries it and as
                        the benchmark beside it already did — Max, 2026-09-22: "sao cái bạn làm ko
                        giống cái đã design trước đó". The collab side was the only one of the two
                        without it, so a reader comparing the pair had a figure on one side and had
                        to add up three boxes on the other. */}
                    <span className="cp-blksum">{fmtUSD(calc.factoryCost)}{fxFactory()}</span></div>
                  <div className="cp-row2">
                    {/* No hints here — Max, 2026-09-18. "Skin + Case + Packaging", "Vendor CMT"
                        and "Vendor OH & Profit" are the vendor's own line items and the people
                        filling them in read them off a vendor sheet; a sentence under each said
                        nothing they did not already know. */}
                    {FACTORY_FIELDS.map((f) => money(f.key, setVal, t(`pricing.${f.tk}`)))}
                  </div>
                </div>

                <div className="cp-step cp-bench">
                  <div className="cp-step-head"><h4 className="cp-h">{t('pricing.stepFactory')}</h4>
                    <span className="cp-blksum">
                      {fmtUSD(FACTORY_FIELDS.reduce((a, f) => a + (Number(v[`bench_${f.key}`]) || 0), 0))}
                    </span>
                  </div>
                  {/* Max, 2026-09-18: "2 trường này là lấy theo Frame model bên collab á" — so this
                      is not a picker. There is one place to choose a model, in block 3, and this
                      side follows it; a second dropdown holding the same answer is a second place
                      for it to be different. */}
                  {/* The "Linked to <model> — follows block 3" line was here and is GONE (Max,
                      2026-09-22: "bỏ dòng trên"). The FRAME block below says the same thing where
                      the frame actually is, and a factory-cost block led by a sentence about the
                      frame model described the block underneath rather than this one. */}
                  <div className="cp-row2">
                    {FACTORY_FIELDS.map((f) => {
                      const line = BENCH_LINES.find((l) => l.key === f.key);
                      return (
                        <label className="cp-field" key={f.key}>
                          <span>{t(`pricing.${f.tk}`)}</span>
                          <div className="cp-benchrow">
                            <div className="cp-money">
                              <i>$</i>
                              <input
                                type="number"
                                step="0.01"
                                value={v[`bench_${f.key}`] ?? ''}
                                onChange={(e) => setVal(`bench_${f.key}`, e.target.value)}
                              />
                            </div>
                            {arrowOf(benchDiff(line))}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {/* WHERE THESE NUMBERS CAME FROM, under them — Max, 2026-09-22: "dòng dưới thì
                      chuyển xuống dưới cùng trong Factory cost". Above the boxes it read as an
                      instruction about what to type; under them it is what it is, a note on the
                      figures now standing there. A benchmark with no source is a number the
                      approver has to take on trust. */}
                  {v.bench_src && (
                    <p className="cp-benchsrc" title={v.bench_src}>
                      <span>{t('pricing.benchSrc')}</span> {v.bench_src}
                    </p>
                  )}
                </div>

                {/* THE GAP, UNDER THE TWO CARDS IT IS A GAP BETWEEN — Jay, 2026-09-23. It used to
                    sit in block 4 as "Extra Cost 2", which it never was: nothing on these lines is
                    money anybody pays.
                    AND THE TWO TOTALS ARE RESTATED ABOVE IT, because he asked for exactly that:
                    "On its own, +$6.20 and +$1.30 doesn't tell the reader what the two figures are
                    a difference between, or what they add up to." So the row reads collab, inline,
                    gap — and the lines below say why. */}
                <div className="cp-gap">
                  <div className="cp-gap-top">
                    <span><em>{t('pricing.collabCol')}</em> <b>{fmtUSD(calc.factoryCost)}</b></span>
                    <span className="op">−</span>
                    <span><em>{t('pricing.benchCol')}</em> <b>{fmtUSD(benchFactory)}</b></span>
                    <span className="op">=</span>
                    <span className="cp-gap-res"><em>{t('pricing.gapWord')}</em> {arrowOf(calc.factoryCost - benchFactory)}</span>
                  </div>
                  {gapTable(FACTORY_FIELDS.map((f) => f.key), { addable: true })}
                </div>
                </div>

                {/* ONE FRAME PER COMPARISON — Jay, 2026-09-23: "the cards are split into
                    separate boxes on each side, so it isn't clear where to start or where to go
                    next. Make the left and right read as one pair."
                    They already shared a row and a height; what they did not share was an EDGE, so
                    the eye read two columns of cards rather than a row of comparisons. The frame
                    holds both halves with one rule down the middle, which is the shortest way to
                    say "these two are about the same thing". */}
                <div className="cp-cmp">
                <div className="cp-step">
                  <div className="cp-step-head"><span className="cp-step-no">3</span>
                    <h4 className="cp-h">{t('pricing.stepFrame')}</h4>
                    {/* The frame cost AND the FM fee, which is what the block asks for — the two
                        boxes under it are the parts of this one figure. */}
                    <span className="cp-blksum">{fmtUSD(calc.frameCost)}{fxFrame()}</span></div>
                  <div className="cp-row2">
                    {money('frame', setVal, t('pricing.frame'), t('pricing.frameHint'), framePicker)}
                    {fmFeeField()}
                  </div>
                  <h5 className="cp-subh">{t('pricing.frameSupply')}</h5>
                  <div className="cp-supply">
                    <label>
                      <input
                        type="radio"
                        checked={(v.supply || 'sell') === 'sell'}
                        onChange={() => setVal('supply', 'sell')}
                      />
                      <b>{t('pricing.supplySell')}</b>
                      <em>{t('pricing.supplySellHint')}</em>
                    </label>
                    <label>
                      <input
                        type="radio"
                        checked={v.supply === 'foc'}
                        onChange={() => setVal('supply', 'foc')}
                      />
                      <b>{t('pricing.supplyFoc')}</b>
                      <em>{t('pricing.supplyFocHint')}</em>
                    </label>
                  </div>
                </div>

                <div className="cp-step cp-bench">
                  <div className="cp-step-head"><h4 className="cp-h">{t('pricing.stepFrame')}</h4>
                    <span className="cp-blksum">{fmtUSD(Number(v.bench_frame) || 0)}</span>
                  </div>
                  <div className="cp-row2">
                    <label className="cp-field">
                      <span>{t('pricing.frame')}</span>
                      <em>{t('pricing.benchFrameHint')}</em>
                      <div className="cp-benchrow">
                        <div className="cp-money">
                          <i>$</i>
                          <input
                            type="number"
                            step="0.01"
                            value={v.bench_frame ?? ''}
                            onChange={(e) => setVal('bench_frame', e.target.value)}
                          />
                        </div>
                        {arrowOf(benchDiff(BENCH_LINES.find((l) => l.key === 'frame')))}
                      </div>
                    </label>
                    <div className="cp-field">
                      <span>{t('pricing.frameModelLabel')}</span>
                      <em>{t('pricing.benchModelHint')}</em>
                      <div className="cp-derived">
                        <b>{v.frameModel || '—'}</b>
                        <span className="cp-src">{t('pricing.benchFromCollab')}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* THE FRAME'S OWN GAP, under the frame — Jay, 2026-09-23: "The frame cost
                    difference (−$0.04) goes under block 3, for the same reason." Same row, same
                    shape as the factory gap above it, so the two read as one habit rather than two
                    inventions. No free lines here: anything the blocks do not cover is a cost
                    thing and belongs to the factory table, which has the button. */}
                <div className="cp-gap">
                  <div className="cp-gap-top">
                    <span><em>{t('pricing.collabCol')}</em> <b>{fmtUSD(calc.frameCost)}</b></span>
                    <span className="op">−</span>
                    <span><em>{t('pricing.benchCol')}</em> <b>{fmtUSD(Number(v.bench_frame) || 0)}</b></span>
                    <span className="op">=</span>
                    <span className="cp-gap-res"><em>{t('pricing.gapWord')}</em> {arrowOf(calc.frameCost - (Number(v.bench_frame) || 0))}</span>
                  </div>
                  {gapTable(['frame'])}
                </div>
                </div>

                {/* NOT A COMPARISON, so it does not wear the comparison frame — Jay, same
                    mail: "Block 4 is not a comparison, so it should look different and clearly
                    single-column." */}
                <div className="cp-solo">
                <div className="cp-step">
                  <div className="cp-step-head"><span className="cp-step-no">4</span>
                    <h4 className="cp-h">{t('pricing.stepOthers')}</h4>
                    {/* Every excluded item added up, the additional-material list included: it is
                        the figure the margin is NOT taken on, and the one the comparison block
                        across the card is measured against. */}
                    <span className="cp-blksum">{fmtUSD(calc.excluded)}{fxExtra()}</span></div>
                  <p className="cp-sub">{isCbd(v) ? t('pricing.cbdExtraSub') : t('pricing.excludedSub')}</p>
                  <div className="cp-row2">
                    {EXCLUDED_FIELDS.filter((f) => !f.asList).map((f) => {
                      const overridden = !!sku.overrides[f.key];
                      const isAuto = f.auto && sku.auto !== false && !overridden;
                      /* The ladder IS the hint of the box it produces — the band this order falls in
                         is marked, so the number above has its reason beside it rather than in a
                         note further down the page. */
                      const cbdSlFixed = isCbd(v) && f.key === 'shortLead' && !!v.slFlag;
                      const moqLadder = isCbd(v) ? CBD_MOQ_LADDER : MOQ_LADDER;
                      const ladder = f.key === 'moq' ? moqLadder : (f.key === 'shortLead' && !cbdSlFixed ? SL_LADDER : null);
                      const nowBand = ladder && (f.key === 'moq'
                        ? ((moqLadder.find((b) => (Number(calc.qty) || 0) > 0 && calc.qty <= b.upto) || {}).label || null)
                        : slBandLabel(calc.lead));
                      return (
                        <label className="cp-field" key={f.key}>
                          <span>
                            {t(`pricing.${f.tk}`)}
                            {/* Only the two that are a RATE off a ladder applied to a base made of
                                several boxes — neither the rate nor the base is written anywhere
                                else on the screen. The rest are a single figure and explain
                                themselves in the hint under the label. */}
                            {f.key === 'moq' && fxMoq()}
                            {f.key === 'shortLead' && fxSl()}
                            {isAuto && <b className="cp-tag">{t('pricing.autoTag')}</b>}
                            {f.auto && overridden && (
                              <button type="button" className="cp-undo" onClick={() => clearOverride(f.key)}>
                                {t('pricing.resetOverride')}
                              </button>
                            )}
                          </span>
                          <em>
                            {ladder
                              ? (
                                <span className="cp-ladder">
                                  {ladder.map((b) => (
                                    <i key={b.label} className={b.label === nowBand ? 'on' : ''}>
                                      {b.n ? t('xPricing.nDays', { n: b.n }) : b.label} {b.rate === 'none' ? t('xPricing.rateNone') : b.rate}
                                    </i>
                                  ))}
                                </span>
                              )
                              : (cbdSlFixed ? t(v.slFlag === 'yes' ? 'pricing.cbdSlYesHint' : 'pricing.cbdSlNoHint', { pct: 50 })
                                : t(`pricing.${f.tk}Hint`))}
                          </em>
                          <div className="cp-money">
                            <i>$</i>
                            <input
                              type="number"
                              step="0.01"
                              className={isAuto ? 'cp-autoval' : ''}
                              value={isAuto ? calc.items[f.key].toFixed(2) : (v[f.key] ?? '')}
                              onChange={(e) => (f.auto ? setExcluded(f.key, e.target.value) : setVal(f.key, e.target.value))}
                            />
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {/* ADDITIONAL MATERIAL IS A LIST — Max, 2026-09-18: "user có thể click add để
                      thêm nhiều loại phí khác vào á". One box could only ever hold a total, and a
                      total is what the approver asks about: $4.20 of what? Each line carries what
                      it is beside what it costs, so the figure on the heading is an answer.
                      `addMat` is still the number the arithmetic reads, derived from these. */}
                  <div className="cp-sublist">
                    <div className="cp-sublist-head">
                      {t('pricing.addMat')}
                      <span className="cp-blksum">{fmtUSD(calc.items.addMat)}</span>
                    </div>
                    {(v.addMatLines || (String(v.addMat ?? '').trim() === '' ? [] : [{ name: '', amt: v.addMat }])).map((row, i) => (
                      <div className="cp-line" key={i}>
                        <div className="cp-money">
                          <input
                            value={row.name ?? ''}
                            placeholder={t('pricing.addMatWhat')}
                            onChange={(e) => setAddMat(i, { name: e.target.value })}
                          />
                        </div>
                        <div className="cp-money cp-lineamt">
                          <i>$</i>
                          <input
                            type="number"
                            step="0.01"
                            value={row.amt ?? ''}
                            onChange={(e) => setAddMat(i, { amt: e.target.value })}
                          />
                        </div>
                        <button
                          type="button"
                          className="cp-linex"
                          title={t('pricing.removeLine')}
                          onClick={() => dropAddMat(i)}
                        >✕</button>
                      </div>
                    ))}
                    <button
                      type="button"
                      className="sp-btn sp-btn-ghost cp-addline"
                      onClick={() => setAddMat((v.addMatLines || []).length, {})}
                    >
                      <Plus size={12} /> {t('pricing.addLine')}
                    </button>
                  </div>
                  {/* The vendor's own MOQ rate had a line here until 2026-09-17, when Max took it
                      off the screen. The FIELD is still read — a quote saved with a typed rate keeps
                      using it — but nothing on this form offers to change it any more. */}
                </div>

                {/* EXTRA COST 2 IS GONE FROM HERE — Jay, 2026-09-23: "Extra Cost 2 is not an
                    extra cost. It is the breakdown of the factory cost gap, so it belongs beside
                    the two factory cost cards in block 2."
                    It is not deleted, it is MOVED: the factory lines now sit under block 2 and the
                    frame line under block 3, each beside the pair it is a gap between. What is left
                    in block 4 is Extra Cost 1 alone, which is the one thing here that really is a
                    cost. */}
                </div>

                {/* PROFIT AND TARGET ARE TWO GROUPS, not one "Pricing" — the sketch names them
                    separately and they answer different questions: what Helinox takes on the cost,
                    and what the shelf price is aimed at. */}
                {/* ONE FRAME PER COMPARISON — Jay, 2026-09-23: "the cards are split into
                    separate boxes on each side, so it isn't clear where to start or where to go
                    next. Make the left and right read as one pair."
                    They already shared a row and a height; what they did not share was an EDGE, so
                    the eye read two columns of cards rather than a row of comparisons. The frame
                    holds both halves with one rule down the middle, which is the shortest way to
                    say "these two are about the same thing". */}
                <div className="cp-cmp">
                <div className="cp-step">
                  <div className="cp-step-head"><span className="cp-step-no">5</span>
                    <h4 className="cp-h">{t('pricing.stepProfit')}</h4>
                    {/* BOTH HALVES OF THE TIER PRICE, as the mockup carries it: the margin is
                        taken on the base and the extra cost is added after it, so a head printing
                        the base alone names the smaller half of what the simulator below is
                        pricing. The bench beside it keeps "base" alone — it has no extra set. */}
                    {/* AND THE SUM — Jay, 2026-09-23: "the header '$39.91 base · $30.05 extra' has
                        no context. Show the sum too — base + extra = the amount the ratios are
                        applied to." It is the buying cost, which the screen already knows; printing
                        it here is what makes the two figures beside it add up to something. */}
                    <span className="cp-blksum">{fmtUSD(calc.markupBase)} {t('pricing.benchBase')}
                      {' · '}{fmtUSD(calc.excluded)} {t('pricing.blkExtra')}
                      {' = '}<b>{fmtUSD(calc.markupBase + calc.excluded)}</b>{fxBuying()}</span></div>
                  {isCbd(v) ? cbdBlock() : (<>
                  {/* The Case governs both tiers, so it sits across the top of both rather than in
                      one of their columns. */}
                  <div className="cp-casebar">
                    <span className="lbl">{t('pricing.dealCase')} <b className="cp-req">*</b></span>
                    <select
                      className="cp-casesel"
                      value={v.dealCase || ''}
                      onChange={(e) => setVal('dealCase', e.target.value)}
                    >
                      <option value="">{t('pricing.dealCasePick')}</option>
                      <option value="both">{t('pricing.dealCaseBoth')}</option>
                      <option value="a">{t('pricing.dealCaseA')}</option>
                      <option value="b">{t('pricing.dealCaseB')}</option>
                    </select>
                    <em>{t('pricing.dealCaseHint')}</em>
                    {/* INSIDE THE CASE BOX — Max, 2026-09-24: "cho phần này lên trên luôn". It sat
                        under the box, as a separate slab, so the picker and the thing that
                        explains the picker read as two subjects. It is one: choose, and the box
                        tells you what you chose. `flex-basis: 100%` puts it on its own line of the
                        same flex row rather than needing a wrapper around both. */}
                    {!!v.dealCase && (
                      <div className="cp-casewhat">
                        <div className="cp-casepath">
                          <span className="cp-casepath-l">{t('pricing.dealCasePathLbl')}</span>
                          <b>{t(`pricing.dealCasePath_${v.dealCase}`)}</b>
                        </div>
                        <p>{t(`pricing.dealCaseWhat_${v.dealCase}`)}</p>
                      </div>
                    )}
                  </div>
                  {/* WHAT THE CHOSEN CASE MEANS, in words — Jay, 2026-09-23: "Case A and Case B
                      need a plain description of what each one means, written for someone who
                      doesn't know our internal terms."
                      Under the picker and only for what is SELECTED: three descriptions stacked
                      under a dropdown is a glossary, and a glossary is what somebody reads once
                      they have already given up on the screen. */}

                  {tierBlock('t1')}
                  {tierBlock('t2')}
                  </>)}
                </div>

                <div className="cp-step cp-bench">
                  <div className="cp-step-head"><h4 className="cp-h">{t('pricing.stepProfit')}</h4>
                    {/* WHERE THE BASE COMES FROM — Jay, 2026-09-23: "write out where the inline
                        $28.65 base comes from. I read it as factory cost $12.29 + frame cost
                        $16.36. Show that." He read it right, and having to work it out is the
                        point: the collab side beside it states its own two halves, so this one
                        states them too. */}
                    <span className="cp-blksum">
                      <em className="cp-basefrom">
                        {fmtUSD(benchFactory)} {t('pricing.factoryWord')}
                        {' + '}{fmtUSD(Number(v.bench_frame) || 0)} {t('pricing.frameWord')}
                        {' = '}
                      </em>
                      <b>{fmtUSD(benchFactory + (Number(v.bench_frame) || 0))}</b> {t('pricing.benchBase')}
                      {fxBenchBase()}
                    </span>
                  </div>
                  {/* ONE LINE — Max, 2026-09-18: "bỏ hết và nó chỉ có 1 dòng Helinox profit thui".
                      The inline product is not quoted at two tiers, so mirroring T1 and T2 down
                      this column was the collab's structure drawn over something that does not
                      have it. */}
                  <table className="cp-tt">
                    <tbody>
                      <tr>
                        <th>{t('pricing.profitPart')}</th>
                        <td className="pct"><span className="none">{v.t0Profit === '' || v.t0Profit == null ? '—' : `${v.t0Profit}%`}</span></td>
                        <td className="unit">
                          {(() => {
                            const base = FACTORY_FIELDS.reduce((a, f) => a + (Number(v[`bench_${f.key}`]) || 0), 0)
                              + (Number(v.bench_frame) || 0);
                            const r = (Number(v.t0Profit) || 0) / 100;
                            const per = r > 0 && r < 1 ? (base / (1 - r)) * r : null;
                            return per === null ? '—' : (
                              <>
                                {fmtUSD(per)} <span className="per">/{t('pricing.unit')}</span>
                                {/* Max, 2026-09-22: "thiếu mất chữ i bên Profit section Profit /
                                    MSRP simulator". It was the one computed figure on this side
                                    without its working — and the one most worth having, because
                                    the RATE is not the inline product's: it is the collab's own
                                    T1 profit, and there is no box here that says so. */}
                                {fx(
                                  t('pricing.fxBenchProfit'),
                                  t('pricing.fxBenchRule'),
                                  [`${t('pricing.fxBenchBase')} ${n2(base)}`,
                                   `${n2(base)} ÷ (1 − ${Number(v.t0Profit) || 0}%) × ${Number(v.t0Profit) || 0}%`],
                                  fmtUSD(per),
                                  'right',
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="tot">
                          {(() => {
                            const base = FACTORY_FIELDS.reduce((a, f) => a + (Number(v[`bench_${f.key}`]) || 0), 0)
                              + (Number(v.bench_frame) || 0);
                            const r = (Number(v.t0Profit) || 0) / 100;
                            const per = r > 0 && r < 1 ? (base / (1 - r)) * r : null;
                            /* Jay, 2026-09-23: "Total $318.33 sits there with no label. If it's
                               the inline total margin at 100 EA, say so." It is — profit per unit
                               times the order — so it says so, with the quantity in it. */
                            return per === null ? '—' : `${t('pricing.marginAtQty', { n: calc.qty })} ${fmtUSD(per * calc.qty)}`;
                          })()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  {/* The shelf price moved onto the Estimated MSRP row opposite, which is the
                      figure it is there to be compared with — Jay, 2026-09-23. It is not printed
                      twice. */}
                </div>
                </div>

                {/* ONE FRAME PER COMPARISON — Jay, 2026-09-23: "the cards are split into
                    separate boxes on each side, so it isn't clear where to start or where to go
                    next. Make the left and right read as one pair."
                    They already shared a row and a height; what they did not share was an EDGE, so
                    the eye read two columns of cards rather than a row of comparisons. The frame
                    holds both halves with one rule down the middle, which is the shortest way to
                    say "these two are about the same thing". */}
                <div className="cp-cmp">
                <div className="cp-step">
                  <div className="cp-step-head"><span className="cp-step-no">6</span>
                    <h4 className="cp-h">{t('pricing.stepTarget')}</h4>
                    {/* THE ONE THE QUOTE CARRIES, said on the block itself. */}
                    <span className="cp-blksum"><em className="cp-basefrom">{t('pricing.msrpIsQuote')}</em>
                      {' '}<b>{fmtUSD(calc.msrp)}</b></span></div>
                  <div className="cp-row2">
                    <label className="cp-field">
                      <span>{t('pricing.premium')} {!isCbd(v) && <b className="cp-req">*</b>}</span>
                      <em>{t('pricing.premiumHint')}</em>
                      <div className="cp-money">
                        <input
                          type="number"
                          step="0.1"
                          value={v.premium ?? ''}
                          onChange={(e) => setVal('premium', e.target.value)}
                        />
                        <i>%</i>
                      </div>
                    </label>
                    {/* The MSRP is not typed: it IS the inline retail price plus the premium, and a
                        box holding it would be a second answer to a question already answered. */}
                    <div className="cp-field">
                      <span>{t('pricing.targetMsrp')} <b className="cp-tag">{t('pricing.autoTag')}</b></span>
                      <em>{t('pricing.targetMsrpHint')}</em>
                      <div className="cp-money cp-fixed">
                        <i>$</i>
                        <input value={calc.msrp ? calc.msrp.toFixed(2) : ''} readOnly tabIndex={-1} />
                      </div>
                    </div>
                  </div>
                  <div className="cp-out">
                    <div className="r big"><span>{t('pricing.msrp')}</span><b>{fmtUSD(calc.msrp)}</b></div>
                    {/* What multiple of the tier price the shelf price lands on — the figure the
                        guideline talks in, and the one the Estimated MSRP bands above are aimed at. */}
                    <div className="r">
                      <span>{t('pricing.msrpOverT1')}<em>{t('pricing.msrpOverT1Hint')}</em></span>
                      <b>{(() => {
                        const p = tierRows('t1').paid;
                        return p ? `${(calc.msrp / p).toFixed(2)} ×` : '—';
                      })()}</b>
                    </div>
                  </div>
                </div>

                <div className="cp-step cp-bench">
                  <div className="cp-step-head"><h4 className="cp-h">{t('pricing.stepTarget')}</h4>
                    <span className="cp-blksum">{fmtUSD(Number(v.inlineRrp) || 0)}</span>
                  </div>
                  <div className="cp-row2">
                    {money('inlineRrp', setVal, <>{t('pricing.inlineRrp')} <b className="cp-req">*</b></>,
                      t('pricing.inlineRrpHint'), null,
                      framePicked?.link_url ? (
                        <a
                          className="cp-srclink"
                          href={framePicked.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={11} /> {t('pricing.productPage')}
                        </a>
                      ) : null)}
                    {/* The INLINE style's own T1 — Linda's Price Approval template reads the collab T1 against it
                        ("T1 vs Inline T1"). CBD only; the guideline has no tier to compare. */}
                    {isCbd(v) && money('inlineT1', setVal, t('pricing.inlineT1'), t('pricing.inlineT1Hint'))}
                    {/* Y'S TARGET IS GONE — Jay, 2026-09-23: "What is Y's target used for? If
                        there's no particular reason, remove it." There was none: it came off the
                        whiteboard on 2026-09-18 and was never confirmed, `pricingModel` has never
                        read it, and no figure on this screen or in the Excel export depends on it.
                        A box that is stored and never used is a question the screen asks for no
                        reason. The FIELD is left in saved payloads rather than migrated away —
                        nothing reads it, so nothing has to. */}
                  </div>
                  <div className="cp-out">
                    <div className="r">
                      <span>{t('pricing.collabVsInline')}</span>
                      <b>{(() => {
                        const rrp = Number(v.inlineRrp) || 0;
                        if (!rrp) return '—';
                        return arrowOf(calc.msrp - rrp);
                      })()}</b>
                    </div>
                  </div>
                </div>
                </div>

                </div>

                </div>
              </section>

              {/* TWO BLOCKS, NOT ONE "RESULTS" — Max, 2026-09-18, off the sketch: what the unit
                  COSTS, and what it is SOLD at. They were one card with five tables in it, and a
                  card called Results says only that the screen has finished thinking.

                  The split is the sketch's own: everything above the line is the cost build-up
                  (A + B + C, and the inline product it is measured against), everything below is
                  the price that is taken on it — the tiers, what the order earns, the MSRP and the
                  options. The one is the input to the other, so they stack in that order. */}
              <section className="sp-section cp-results">
                <div className="sp-section-head"><div>{t('pricing.blockResult')}</div></div>

                {/* RESULT AND OPTIONS — the sketch's bottom box, 2026-09-18: the conditions ticked
                    on the left, the options they produce on the right, and the arrow between them.

                    Each card is COMPUTED, never stored: an option holds the conditions it was
                    ticked for and nothing else, so a correction to the inputs above moves all three
                    with it. "As quoted" leads the list because an option is only meaningful against
                    the thing it departs from. */}
                <div className="cp-opts">
                  <div className="cp-opts-grid">
                    {/* THE TWO HEADS SHARE A ROW — Max, 2026-09-22: "bị lệch nhau". Conditions had
                        its title INSIDE its bordered box and Options had its title bare above the
                        cards, so the two column names sat at different heights and the columns
                        read as two unrelated blocks. Same rule as `.cp-twocol` further down this
                        file: both columns carry the same head height or they step down the page
                        against each other. */}
                    <div className="cp-opts-col">
                      <div className="cp-opts-head">
                        <h4 className="cp-h">{t('pricing.optConditions')}</h4>
                      </div>
                      <div className="cp-opts-conds">
                      {optEdit !== null && (optEdit === QUOTE_EDIT || options[optEdit]) ? (() => {
                      const readOnly = optEdit === QUOTE_EDIT;
                      const editingOpt = readOnly ? quoteAsOption() : options[optEdit];
                      return (
                        <>
                          <div className="cp-opt-who">
                            {/* NAMED, not numbered — Max, 2026-09-22: "tôi muốn có thể đổi tên
                                option nha". "Option 2" says nothing an approver can act on; "500 EA
                                on the quote sheet" is the thing being weighed. The number stays as
                                the PLACEHOLDER, so an option nobody has named still reads the way
                                it always did rather than as an empty box. */}
                            {/* IT HAS TO LOOK EDITABLE — Max asked for renaming three times, the
                                last two with the box already on screen: "chỗ sửa tên option đang bị
                                nhỏ và khó biết là có thể chỉnh á". Dressed as the heading it
                                replaced, it read as a heading. It is a FIELD now — its own line,
                                its own label above it, a real border and the width of the panel —
                                which is what every other thing on this screen you can type into
                                looks like. */}
                            <label className="cp-opt-namefield">
                              <span>{t('pricing.optName')}</span>
                              <input
                                className="cp-opt-name"
                                value={editingOpt.name ?? ''}
                                readOnly={readOnly}
                                placeholder={t('pricing.optNamePh', { n: optEdit + 1 })}
                                maxLength={60}
                                onChange={(e) => setOptions((prev) => prev.map((o, i) => (i === optEdit
                                  ? { ...o, name: e.target.value } : o)))}
                              />
                            </label>
                            <span className="cp-opts-note">{readOnly ? t('pricing.optQuoteReadOnly') : t('pricing.optCondNote')}</span>
                          </div>
                          {/* Ticking a line is what makes it editable — an untouched condition
                              follows the quote, and a box you can type in beside an unticked line
                              would be a number that does nothing. */}
                          <div className="cp-opt-conds">
                            {OPT_CONDITIONS.filter((c) => (isCbd(v) ? !CBD_NO_LEVER.includes(c.key) : !c.cbdOnly)).map((c) => {
                              const on = !!editingOpt.on[c.key];
                              const val = editingOpt.values[c.key];
                              return (
                                <label
                                  className={`cp-opt-cond-row ${on || c.always ? 'on' : ''}${c.kind === 'basis' || c.kind === 'items' ? ' wide' : ''}${c.always ? ' always' : ''}`}
                                  key={c.key}
                                >
                                  {!c.always && (
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      disabled={readOnly}
                                      onChange={(e) => (e.target.checked
                                        ? pickOptCond(optEdit, c) : dropOptCond(optEdit, c))}
                                    />
                                  )}
                                  <span>{t(`pricing.${c.tk}`)}</span>
                                  {c.kind === 'items' ? (
                                    <div className="cp-opt-items">
                                      {EXTRA_ITEM_KEYS.filter((k) => calc.items[k]).map((k) => {
                                        const off = Array.isArray(val) ? val : [];
                                        const applied = !off.includes(k);
                                        return (
                                          <label key={k} className={`cp-opt-item${applied ? ' on' : ''}`}>
                                            <input
                                              type="checkbox"
                                              disabled={readOnly || (!on && !c.always)}
                                              checked={applied}
                                              onChange={(e) => setOptCond(optEdit, c.key, {
                                                values: {
                                                  [c.key]: e.target.checked
                                                    ? off.filter((x) => x !== k) : [...off, k],
                                                },
                                              })}
                                            />
                                            {t(`pricing.${k === 'moq' ? 'moqCharge' : k}`)}
                                            <b>{fmtUSD(calc.items[k])}</b>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  ) : c.kind === 'basis' ? (
                                    <select
                                      disabled={readOnly || !on}
                                      value={val ?? 'base'}
                                      onChange={(e) => setOptCond(optEdit, c.key, { values: { [c.key]: e.target.value } })}
                                    >
                                      <option value="base">{t('pricing.marginBasisBase')}</option>
                                      <option value="buying">{t('pricing.marginBasisBuying')}</option>
                                    </select>
                                  ) : (
                                    <div className="cp-money">
                                      {c.unit === '$' && <i>$</i>}
                                      <input
                                        type="number"
                                        step={c.step}
                                        disabled={readOnly || !on}
                                        /* The quote's own figure, greyed: the box says what it
                                           would be, and asks what this option makes it. */
                                        placeholder={on ? String(v[c.key] ?? '') : ''}
                                        value={val ?? ''}
                                        onChange={(e) => setOptCond(optEdit, c.key, { values: { [c.key]: e.target.value } })}
                                      />
                                      {c.unit !== '$' && <i>{c.unit}</i>}
                                    </div>
                                  )}
                                </label>
                              );
                            })}
                          </div>

                          {/* REMOVE STAYS APART, on the left. Max asked for the two to be right
                              aligned; putting all three there would set the one that destroys the
                              option immediately beside the one that keeps it. */}
                          <div className="cp-opt-actions">
                            {!readOnly && (
                              <button type="button" className="sp-btn sp-btn-ghost cp-opt-drop" onClick={() => dropOption(optEdit)}>
                                <Trash2 size={13} /> {t('pricing.optRemove')}
                              </button>
                            )}
                            <div className="cp-opt-actions-end">
                              {/* Nothing to cancel on a panel that cannot be typed in. */}
                              {!readOnly && (
                                <button type="button" className="sp-btn sp-btn-ghost" onClick={() => closeOptEdit(true)}>
                                  {t('pricing.optCancel')}
                                </button>
                              )}
                              <button type="button" className="sp-btn sp-btn-primary" onClick={() => closeOptEdit(false)}>
                                <CheckCircle2 size={13} /> {readOnly ? t('pricing.optClose') : t('pricing.optDone')}
                              </button>
                            </div>
                          </div>
                        </>
                      ); })() : (
                        /* ONE Add button, and it lives with the OPTIONS — Max, 2026-09-22: "chỉ
                           cần 1 nút add an option thôi". There were two, doing the same thing, and
                           the one here sat under a heading called Conditions, which is not what it
                           adds. This panel says what an option is and waits for one. */
                        <div className="cp-opts-empty">
                          <p>{t('pricing.optionsNote')}</p>
                        </div>
                      )}
                      </div>
                    </div>

                    <div className="cp-opts-col cp-opts-out">
                      <div className="cp-opts-head">
                        <h4 className="cp-h">{t('pricing.options')}</h4>
                        <button
                          type="button"
                          className="sp-btn sp-btn-ghost cp-opts-add"
                          onClick={addOption}
                          disabled={options.length >= OPT_MAX}
                        >
                          <Plus size={13} /> {t('pricing.optionAdd')}
                        </button>
                      </div>
                      <div className="cp-opt-cards">
                        {(() => {
                          /* `isQuote` decides the highlight, not "has no button" — the As quoted
                             card gained a Conditions button on 2026-09-24 and the old test quietly
                             took its own styling away with it. */
                          const card = (title, sub, c, extra, isQuote) => (
                            <div className={`cp-opt ${isQuote ? 'base' : ''}`} key={title}>
                              <div className="cp-opt-top">
                                <b>{title}</b>
                                {extra}
                              </div>
                              <div className="cp-opt-cond">{sub}</div>
                              {/* The lines this card charges — see cardLines. */}
                              <div className="cp-cardlines">
                                <h6>{t('pricing.linesApplied')}</h6>
                                <div className="cp-cardlines-row">
                                  {cardLines(c).map((L) => (
                                    <span key={L.k} className={L.on ? 'on' : 'off'} title={L.on ? undefined : t('pricing.lineDropped')}>
                                      {L.label} <b>{fmtUSD(L.amt)}</b>
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <dl>
                                <div><dt>{t('pricing.t0')}</dt><dd>{c.t0 != null ? fmtUSD(c.t0) : '—'}{cardFx.t1(c)}</dd></div>
                                <div><dt>{t('pricing.t1')}</dt><dd>{c.t1 != null ? fmtUSD(c.t1) : '—'}{cardFx.t2(c)}</dd></div>
                                <div><dt>{t('pricing.msrp')}</dt><dd>{fmtUSD(c.msrp)}{cardFx.msrp(c)}</dd></div>
                                {/* The figure the decision is actually made on: what the whole
                                    order earns, on whichever tier this quote has a ratio for. */}
                                <div className="tot">
                                  {(() => {
                                    const side = c.byBasis[c.marginBasis === 'buying' ? 'buying' : 'base'];
                                    const t1 = side.p1 != null;
                                    const per = t1 ? side.p1 : side.p2;
                                    return (
                                      <>
                                        <dt>{t(`pricing.optOrderProfit${t1 ? 'T1' : 'T2'}`)}</dt>
                                        <dd>{per == null ? '—'
                                          : <>{fmtUSD(per * c.qty)}{cardFx.profit(c, per, t1 ? 'T1' : 'T2')}</>}</dd>
                                      </>
                                    );
                                  })()}
                                </div>
                              </dl>
                            </div>
                          );
                          return [
                            card(t('pricing.optAsQuoted'), quotedBuiltText(), calc, (
                              <button
                                type="button"
                                className={`cp-opt-edit ${optEdit === QUOTE_EDIT ? 'on' : ''}`}
                                onClick={() => setOptEdit(QUOTE_EDIT)}
                              >
                                <Pencil size={11} /> {t('pricing.optEdit')}
                              </button>
                            ), true),
                            ...options.map((o, i) => {
                              const c = computeSku(withOption(sku, o), rules);
                              /* ONE describer for the card and the column head — see
                                 optDepartureText. Two of them is how a screen comes to say one
                                 option two ways. */
                              const sub = optDepartureText(o) || t('pricing.optSameAsQuote');
                              return card(
                                (o.name || '').trim() || t('pricing.optionN', { n: i + 1 }), sub, c,
                                <button
                                  type="button"
                                  className={`cp-opt-edit ${optEdit === i ? 'on' : ''}`}
                                  onClick={() => openOptEdit(i)}
                                >
                                  <Pencil size={11} /> {t('pricing.optEdit')}
                                </button>,
                              );
                            }),
                          ];
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                <h4 className="cp-h">{t('pricing.blockDetail')}</h4>

                {/* THE COST OF ONE UNIT, in the three groups it is built from — the statement
                    tabled on 2026-09-17. Each group's own total sits ON its heading: a separate
                    subtotal row under the lines said the same number twice, in the language of a
                    ledger nobody here is keeping. Every share is of the TOTAL, so the column adds
                    to 100% across the three groups rather than making three separate 100%s. */}
                <table className="cp-ledger">
                  <thead>
                    <tr>
                      <th>{t('pricing.unitCost')}</th>
                      <th className="pct">{t('pricing.share')}</th>
                      <th className="amt">USD</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="grp">
                      <td>{t('pricing.stepFactory')}</td><td className="pct" />
                      <td className="amt">{fmtUSD(calc.factoryCost)}</td>
                    </tr>
                    {FACTORY_FIELDS.map((f) => (
                      <tr className={`line ${num(v[f.key]) ? '' : 'off'}`} key={f.key}>
                        <td className="name">{t(`pricing.${f.tk}`)}</td>
                        <td className="pct">{fmtPct(calc.buyingCost ? num(v[f.key]) / calc.buyingCost : 0)}</td>
                        <td className="amt">{fmtUSD(num(v[f.key]))}</td>
                      </tr>
                    ))}
                    <tr className="grp">
                      <td>{t('pricing.stepFrame')}</td><td className="pct" />
                      <td className="amt">{fmtUSD(calc.frameCost)}</td>
                    </tr>
                    {/* With no FM fee the frame group is one line carrying the group's own name and
                        its own total — the heading has said it once already. */}
                    {!!calc.fmfee && (
                      <>
                        <tr className="line">
                          <td className="name">{t('pricing.frame')}</td>
                          <td className="pct">{fmtPct(calc.buyingCost ? num(v.frame) / calc.buyingCost : 0)}</td>
                          <td className="amt">{fmtUSD(num(v.frame))}</td>
                        </tr>
                        <tr className="line">
                          <td className="name">{t('pricing.fmfee')}</td>
                          <td className="pct">{fmtPct(calc.buyingCost ? calc.fmfee / calc.buyingCost : 0)}</td>
                          <td className="amt">{fmtUSD(calc.fmfee)}</td>
                        </tr>
                      </>
                    )}
                    <tr className="grp">
                      <td>{t('pricing.stepOthers')}</td><td className="pct" />
                      <td className="amt">{fmtUSD(calc.excluded)}</td>
                    </tr>
                    {EXCLUDED_FIELDS.map((f) => (
                      <tr className={`line ${calc.items[f.key] ? '' : 'off'}`} key={f.key}>
                        <td className="name">{t(`pricing.${f.tk}`)}</td>
                        <td className="pct">{fmtPct(calc.buyingCost ? calc.items[f.key] / calc.buyingCost : 0)}</td>
                        <td className="amt">{fmtUSD(calc.items[f.key])}</td>
                      </tr>
                    ))}
                    <tr className="total">
                      <td>{t('pricing.buyingCost')}</td>
                      <td className="pct">100%</td>
                      <td className="amt">{fmtUSD(calc.buyingCost)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* The comparison used to be repeated here as a table. It is not any more: since
                    2026-09-18 the Extra cost 2 block IS the comparison, line by line and with the
                    reason beside each, and two places printing one thing is how they come to
                    disagree. */}

                {/* ONE COLUMN PER OPTION — Max, 2026-09-22: "chỗ này thay bằng các option tính thì
                    mới đúng". This table used to print the quote on the two COSTING CONVENTIONS,
                    which is a question the radio already settles; the comparison somebody makes
                    here is between the options they built.

                    THE CONVENTION RADIO MOVED OUT rather than being dropped — it is still the
                    choice the quote is submitted on, and every column is priced on it, so it
                    belongs above the table it governs instead of inside two of its headings. */}
                <h4 className="cp-h">{t('pricing.byOption')}</h4>
                {/* THE CONVENTION IS AN OPTION, not a radio over the table — Max, 2026-09-22:
                    "ko cần để user tự chọn nó như là một option". It was two buttons above a table
                    whose columns are options, asking the same kind of question in a different
                    shape; "Margin taken on" is already one of the levers an option can pull, so
                    seeing the quote on the other convention is one more column rather than a
                    switch that repaints the one you have.

                    The quote's OWN convention is named under "As quoted" instead, because a quote
                    saved on the other one has to go on saying so with the switch gone. */}
                <div className="cp-case-wrap">
                  <table className="cp-cases">
                    <thead>
                      <tr>
                        <th>{t('pricing.whatTheyPay')}</th>
                        {caseCols.map((col) => (
                          <th key={col.key} className={col.key === 'quoted' ? 'picked' : ''}>
                            <span className="ch-k">{col.title}</span>
                            <div className="ch-s">{col.sub}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {CASE_ROWS(t, calc, v, caseCols.length > 1).map((r) => (
                        <tr key={r.key} className={r.cls || ''}>
                          <th>{r.label}{r.sub && <em>{r.sub}</em>}</th>
                          {caseCols.map((col) => {
                            const x = r.get(col.calc);
                            return (
                              <td key={col.key} className={col.key === 'quoted' ? 'picked' : ''}>
                                {x === null || x === undefined
                                  ? <span className="dash">—</span>
                                  : <>{fmtUSD(x)}{fxCell(r, col)}</>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* The two notes that stood here are GONE — Max, 2026-09-22: "bỏ qua cái này".
                    One priced the gap between the two costing conventions and the other named the
                    one the quote is submitted on; both described a table of two conventions, and
                    the columns are the options now. The convention this quote is on is printed
                    under "As quoted", which is where a reader looking for it will be. */}

                {skus.length > 1 && (
                  <>
                    <h4 className="cp-h">{t('pricing.allSkus')}</h4>
                    <div className="sp-table-wrap">
                      <table className="sp-table cp-table">
                        <thead>
                          <tr><th>{t('pricing.sku')}</th><th className="num">{t('pricing.qty')}</th><th className="num">{t('pricing.markupBase')}</th><th className="num">{t('pricing.buyingCost')}</th><th className="num">{t('pricing.t0')}</th><th className="num">{t('pricing.t1')}</th><th className="num">{t('pricing.msrp')}</th></tr>
                        </thead>
                        <tbody>
                          {skus.map((s, i) => {
                            const c = computeSku(s, rules);
                            return (
                              <tr key={i} className={i === cur ? 'cp-on' : ''}>
                                <td>{s.name || `SKU ${i + 1}`}</td>
                                <td className="num">{c.qty.toLocaleString('en-GB')}</td>
                                <td className="num">{fmtUSD(c.markupBase)}</td>
                                <td className="num">{fmtUSD(c.buyingCost)}</td>
                                <td className="num">{c.t0 != null ? fmtUSD(c.t0) : '—'}</td>
                                <td className="num">{c.t1 != null ? fmtUSD(c.t1) : '—'}</td>
                                <td className="num">{fmtUSD(c.msrp)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </section>

              {/* WHAT THE APPROVER WILL SEE — Max, 2026-09-29. Blocks 5 and 6 of Jay's mockup, the
                  two that say what the unit costs and what the two sales routes earn on it, drawn
                  by the SAME component the approval screen draws them with.
                  At the foot and read-only on purpose: this screen is where a quote is built, and
                  the tier ladder above is where the ratios are typed. This is the quote read back
                  — the last thing to look at before sending it, not another place to change it. */}
              {summaryFig && (
                <section className="sp-section cp-approverview">
                  <div className="sp-section-head">
                    <div><Eye size={14} /> {t('pricing.approverView')}</div>
                  </div>
                  <p className="cp-approverview-sub">{t('pricing.approverViewSub')}</p>
                  <div className="pad-wrap">
                    {/* The Price Approval sheet, the same one the approver decides on (Linda's template, Tina-approved,
                        2026-10-26). No tick here — the margin box works, so whoever builds the quote can try one. */}
                    {summaryCards.method === 'cbd' && (
                      <CbdPriceApproval skus={skus} rules={rules} decided={loadedGate?.extraOption || ''} canDecide={false}
                        onGmChange={mayShape ? (j, v) => setSkus((prev) => {
                          // the j-th CBD item's proposed margin — kept on the quote, saved with Save (Max, 29/10)
                          let k = -1;
                          return prev.map((sk) => (sk && isCbd(sk.values) && ++k === j ? { ...sk, values: { ...sk.values, paGm: v } } : sk));
                        }) : undefined} />
                    )}
                    <CpColHead />
                    <CpTotalCost fig={summaryFig} />
                    <CpPriceMargin fig={summaryFig} />
                    {/* BLOCK 7 TOO — Max, 2026-09-30: "khi user tạo phiếu sẽ có sẵn 3 card A, B và
                        C, nếu user muốn tính thêm kiểu khác thì thêm option". A, B and C exist on
                        every quote whether or not anybody drew them, and the person building one
                        could not see the cards it was going to be judged on — only the options
                        they had added themselves. Same component, same arithmetic, no Select
                        button: this is the quote read back, not a second place to decide it. */}
                    {summaryCards.method !== 'cbd' && (
                    <div className="pad-block">
                      <div className="pad-btitle">
                        <h4>{t(summaryCards.method === 'cbd' ? 'pricing.cbdB7' : 'pricing.padB7', {
                          amt: fmtUSD(summaryCards.ord.extra), n: summaryCards.ord.qty,
                        })}</h4>
                        <span className="pad-kind">{t('pricing.padB7KindCalc')}</span>
                      </div>
                      <p className="pad-note">{t(summaryCards.method === 'cbd' ? 'pricing.cbdB7Note' : 'pricing.padB7NoteCalc')}</p>
                      <CpCardRow cards={summaryCards.cards} cols={summaryCards.cols}
                        decided={loadedGate?.extraOption || ''} ohPct={summaryFig.ohPct} />
                    </div>
                    )}
                  </div>
                </section>
              )}
            </div>
          </>
        )}

        {!showSaved && !showRules && mode === 'licensed' && (
          <>
            {/* The one thing people get wrong about this model, said before any number is typed. It
                is a callout rather than a footnote because reading it late means having already
                built the cost stack the wrong way. */}
            <div className="cp-callout">
              <AlertTriangle size={15} />
              <p>{t('pricing.royaltyNote')}</p>
            </div>

            {/* Which Project Tracker project this quote prices (Amy 30/10, Max 31/10) — its Costing column follows the
                quote. Saved on its own, so a submitted (frozen) quote can still be linked. */}
            <CpTrackerLink projectId={projectId} lines={skus.map((x) => x.name || '')} canEdit={mayShape} />
            <CpAttachments
              projectId={projectId}
              readOnly={!canEdit}
              pendingPhotos={pendingPhotos}
              onPendingPhotos={setPendingPhotos}
              pendingMessages={pendingMessages}
              onPendingMessages={setPendingMessages}
              onPhotoCount={setPhotoCount}
            />

            <div className="cp-grid">
              <section className="sp-section">
                <div className="sp-section-head"><div><Calculator size={14} /> {t('pricing.globalInputs')}</div></div>
                <p className="cp-sub">{t('pricing.licensedSub')}</p>

                <div className="cp-row2">
                  <label className="cp-field">
                    <span>{t('pricing.skuField')}</span>
                    <em>{t('pricing.skuFieldHint')}</em>
                    <input
                      className="sp-input"
                      value={globals.sku || ''}
                      onChange={(e) => setGlobals({ ...globals, sku: e.target.value })}
                    />
                  </label>
                  <label className="cp-field">
                    <span>{t('pricing.globalMsrp')}</span>
                    <em>{t('pricing.globalMsrpHint')}</em>
                    <div className="cp-money"><i>$</i>
                      <input type="number" step="0.01" value={globals.globalMsrp} onChange={(e) => setGlobals({ ...globals, globalMsrp: e.target.value })} />
                    </div>
                  </label>
                  <label className="cp-field">
                    <span>{t('pricing.t1Fob')}</span>
                    <em>{t('pricing.t1FobHint')}</em>
                    <div className="cp-money"><i>$</i>
                      <input type="number" step="0.01" value={globals.t1Fob} onChange={(e) => setGlobals({ ...globals, t1Fob: e.target.value })} />
                    </div>
                  </label>
                  {['logistics', 'wholesale', 'wsRoyalty', 'dtcRoyalty'].map((k) => (
                    <label className="cp-field" key={k}>
                      <span>{t(`pricing.${k}`)}</span>
                      <em>{t(`pricing.${k}Hint`)}</em>
                      <div className="cp-money">
                        <input type="number" step="0.1" value={globals[k]} onChange={(e) => setGlobals({ ...globals, [k]: e.target.value })} />
                        <i>%</i>
                      </div>
                    </label>
                  ))}
                </div>

                <div className="cp-headline">
                  <span>{t('pricing.landed')}</span>
                  <strong>{fmtUSD(landed)}</strong>
                  <em>{t('pricing.landedSub')}</em>
                </div>

                {/* The two royalty rates are only equivalent at a 50% wholesale ratio. Changing the
                    ratio moves that, and the rates are usually left alone by mistake. */}
                <div className="cp-hint">{t('pricing.burdenNote')}</div>

                <h4 className="cp-h">
                  {t('pricing.entities')}
                  <span className="cp-hbtns">
                    <button
                      type="button"
                      className="sp-btn"
                      title={t('pricing.autoFillHint')}
                      onClick={() => setEntities(entities.map((e) => ({
                        ...e, srp: (num(globals.globalMsrp) * (num(e.fx) || 1)).toFixed(2),
                      })))}
                    >
                      {t('pricing.autoFillSrp')}
                    </button>
                    <button type="button" className="sp-btn" onClick={() => setEntities(DEFAULT_ENTITIES())}>
                      {t('pricing.resetEntities')}
                    </button>
                    <button type="button" className="sp-btn" onClick={() => setEntities([...entities, BLANK_ENTITY('', 1)])}>
                      <Plus size={13} /> {t('pricing.addEntity')}
                    </button>
                  </span>
                </h4>
                {entities.map((e, i) => (
                  <div className="cp-ent" key={i}>
                    <input
                      className="sp-input"
                      placeholder={t('pricing.entity')}
                      value={e.name}
                      onChange={(ev) => setEntities(entities.map((x, j) => (j === i ? { ...x, name: ev.target.value } : x)))}
                    />
                    <label><span>{t('pricing.fx')}</span>
                      <input
                        className="sp-input"
                        type="number"
                        step="0.0001"
                        value={e.fx}
                        onChange={(ev) => setEntities(entities.map((x, j) => (j === i ? { ...x, fx: ev.target.value } : x)))}
                      />
                    </label>
                    <label><span>{t('pricing.localSrp')}</span>
                      <input
                        className="sp-input"
                        type="number"
                        step="0.01"
                        value={e.srp}
                        onChange={(ev) => setEntities(entities.map((x, j) => (j === i ? { ...x, srp: ev.target.value } : x)))}
                      />
                    </label>
                    <button
                      type="button"
                      className="sp-mini-btn sp-mini-danger"
                      onClick={() => setEntities(entities.filter((_, j) => j !== i))}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </section>

              <section className="sp-section cp-results">
                <div className="sp-section-head"><div>{t('pricing.resultsByEntity')}</div></div>
                <p className="cp-sub">{t('pricing.resultsByEntitySub')}</p>

                {/* A card per entity, not a row: each one carries six figures and a verdict, and at
                    six columns wide the numbers stopped being comparable to each other. */}
                {entCalc.map(({ ent, r }, i) => (
                  <div className="cp-entcard" key={i}>
                    <div className="cp-entcard-head">
                      <b>{ent.name || `${t('pricing.entity')} ${i + 1}`}</b>
                      <span className="cp-fob">{t('pricing.fobBadge')} ×{r.fobMultiple.toFixed(2)}</span>
                    </div>
                    <div className="cp-entgrid">
                      <div className="cp-cell">
                        <span>{t('pricing.wholesalePrice')}</span>
                        <b>{fmtLocal(r.wsLocal, r.fx)}</b>
                        <em>{t('pricing.wsRoyaltyAmt')} {fmtLocal(r.wsRoyLocal, r.fx)}</em>
                      </div>
                      <div className="cp-cell">
                        <span>{t('pricing.dtcRoyaltyAmt')}</span>
                        <b>{fmtLocal(r.dtcRoyLocal, r.fx)}</b>
                        <em>{t('pricing.localSrp')} × {num(globals.dtcRoyalty)}%</em>
                      </div>
                      <div className={`cp-cell cp-m-${marginTone(r.wsMarginPost)}`}>
                        <span>
                          {t('pricing.wsMarginLabel')}
                          {marginTone(r.wsMarginPost) === 'bad' && r.wsUsd > 0 && (
                            <i className="cp-low">{t('pricing.lowMargin')}</i>
                          )}
                        </span>
                        <b>{fmtPct(r.wsMarginPost)}</b>
                        <em>{t('pricing.pre')} {fmtPct(r.wsMarginPre)}</em>
                      </div>
                      <div className={`cp-cell cp-m-${marginTone(r.dtcMarginPost)}`}>
                        <span>
                          {t('pricing.dtcMarginLabel')}
                          {marginTone(r.dtcMarginPost) === 'bad' && r.srpUsd > 0 && (
                            <i className="cp-low">{t('pricing.lowMargin')}</i>
                          )}
                        </span>
                        <b>{fmtPct(r.dtcMarginPost)}</b>
                        <em>{t('pricing.pre')} {fmtPct(r.dtcMarginPre)}</em>
                      </div>
                    </div>
                    <div className="cp-entfoot">{t('pricing.srpUsd')} {fmtUSD(r.srpUsd)}</div>
                  </div>
                ))}
                {!entCalc.length && <p className="cp-sub">{t('pricing.addEntityFirst')}</p>}
              </section>
            </div>
          </>
        )}

        {/* THE PICKER. The vendor's file prices every colorway of the style, so the row is a
            choice somebody has to make; the variant beside it is the second one, because the same
            product has a different cost with and without the EU GARAN tag and only the person
            quoting knows which market this is for. Each row shows the sheet's OWN buying cost, not
            our fold of it — that is the number they recognise from the file in front of them. */}
        {benchPick?.open && (
          <div className="cp-modal-back" onClick={() => setBenchPick(null)}>
            <div className="cp-modal cp-benchmodal" onClick={(e) => e.stopPropagation()}>
              <h4>{t('pricing.benchPickTitle')}</h4>
              <p>{t('pricing.benchLibBody')}</p>

              <div className="cp-benchbar">
                <input
                  type="search"
                  className="cp-benchq"
                  placeholder={t('pricing.benchSearch')}
                  value={benchPick.q ?? ''}
                  onChange={(e) => searchBenchLibrary(e.target.value)}
                />
              </div>

              <div className="cp-benchrows">
                {/* An empty answer is a sentence, not a blank box: on a first ever use the library
                    IS empty, and a dialog showing nothing looks broken rather than new. */}
                {!benchPick.rows.length && (
                  <p className="cp-benchempty">
                    {benchPick.q ? t('pricing.benchNoHit', { q: benchPick.q }) : t('pricing.benchLibEmpty')}
                  </p>
                )}
                {benchPick.rows.map((r) => {
                  const on = benchPick.row && benchPick.row.row === r.row && benchPick.row.sku === r.sku;
                  const val = r.variants[benchPick.variant] || Object.values(r.variants)[0];
                  return (
                    <button
                      type="button"
                      /* THE SHEET IS PART OF THE KEY. `r.row` is the line number inside one
                         workbook, and every workbook has a row 12 — so two sheets offering the
                         same line collided, and React redrew the list holding BOTH the previous
                         answer and the new one: a search for one SKU showed seven rows built out
                         of four stale and three fresh. Found by measuring the list, not by
                         reading it. */
                      key={`${r.sheetId}-${r.row}-${r.sku}`}
                      className={`cp-benchrowpick${on ? ' on' : ''}`}
                      aria-pressed={on}
                      onClick={() => setBenchPick((p) => ({
                        ...p,
                        row: r,
                        // A row may not price every variant. Keep the chosen one where it exists.
                        variant: r.variants[p.variant] ? p.variant : Object.keys(r.variants)[0],
                      }))}
                    >
                      <b>{r.style || r.sku || `Row ${r.row}`}</b>
                      <span>{[r.colorway, r.frameColor, r.sku].filter(Boolean).join(' · ')}</span>
                      {/* Which workbook a stored line came out of. Two seasons of the same style
                          look identical on every other field on this row. */}
                      {r.fileName && <i className="cp-benchfile">{[r.season, r.vendor, r.fileName].filter(Boolean).join(' · ')}</i>}
                      <em>{val ? fmtUSD(val.total) : '—'}</em>
                    </button>
                  );
                })}
              </div>

              {benchPick.row && (
              <label className="cp-field cp-benchvar">
                <span>{t('pricing.benchVariant')}</span>
                <select
                  value={benchPick.variant}
                  onChange={(e) => setBenchPick((p) => ({ ...p, variant: e.target.value }))}
                >
                  {Object.entries(benchPick.row.variants).map(([k, val]) => (
                    <option key={k} value={k}>{val.label} — {fmtUSD(val.total)}</option>
                  ))}
                </select>
              </label>
              )}

              {/* What is about to be written, before it is written. Four boxes are about to be
                  overwritten and the person should not have to close this to find out with what. */}
              {benchPick.row?.variants?.[benchPick.variant] && (
                <ul className="cp-benchprev">
                  {[['skin', 'skin'], ['cmt', 'cmt'], ['ohp', 'ohp'], ['frame', 'benchFrameLabel']].map(([k, tk]) => (
                    <li key={k}>
                      <span>{t(`pricing.${tk}`)}</span>
                      <b>{fmtUSD(benchPick.row.variants[benchPick.variant][k])}</b>
                    </li>
                  ))}
                </ul>
              )}

              {/* The refused lines are reported where the file is READ, on Inline Cost Sheets —
                  they are a fault in the workbook and belong to the person filing it, not to
                  somebody picking a line months later. */}

              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setBenchPick(null)}>
                  {t('pricing.cancel')}
                </button>
                <button
                  type="button"
                  className="sp-btn sp-btn-primary"
                  autoFocus
                  disabled={!benchPick.row}
                  onClick={applyBenchmark}
                >
                  {t('pricing.benchApply')}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmCancel && (
          <div className="cp-modal-back" onClick={() => setConfirmCancel(false)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <h4>{t('pricing.discardTitle')}</h4>
              <p>{t('pricing.discardBody')}</p>
              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" autoFocus onClick={() => setConfirmCancel(false)}>
                  {t('pricing.keepEditing')}
                </button>
                <button
                  type="button"
                  className="sp-btn sp-btn-danger"
                  onClick={() => { resetForm(); setStatus(''); setConfirmCancel(false); }}
                >
                  {t('pricing.discard')}
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmDel && (
          <div className="cp-modal-back" onClick={() => setConfirmDel('')}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <h4>{t('pricing.deleteTitle')}</h4>
              <p>{t('pricing.deleteBody', { id: confirmDel })}</p>
              <div className="cp-modal-actions">
                <button type="button" className="sp-btn" onClick={() => setConfirmDel('')}>
                  {t('pricing.cancel')}
                </button>
                <button
                  type="button"
                  className="sp-btn sp-btn-danger"
                  autoFocus
                  onClick={() => removeProject(confirmDel)}
                >
                  <Trash2 size={13} /> {t('pricing.confirmDelete')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
